import React, { useState, useEffect, useRef } from 'react';import { 
  FileText, 
  Plus, 
  Building2, 
  DollarSign,
  Calendar,
  User,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  Check,
  Clock,
  Download,
  Upload,
  Trash2,
  Edit,
  Eye,
  EyeOff,
  X,
  Wallet,
  Loader2,
  Banknote,
  Search,
  Sparkles,
  Files,
  CheckCircle,
  XCircle,
  FileSpreadsheet,
  Lock
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { contratosApi, depositosApi, unidadesApi, reservasApi, verazApi, coworkSuscriptoresApi } from '../../lib/api';
import { formatCurrency, cn, formatDate } from '../../lib/utils';
import { AlquileresTorreEmiliaView } from './contratos-depositos/AlquileresTorreEmiliaView';
import { useTableSort, compareValues, SortHeader } from './contratos-depositos/tableSort';

// Comparador de unidades — orden canónico (igual que 5.Factura):
// Piso 1 primero, luego oficinas por número (201..402), y al final Piso 5, 6, 7.
const tipoPrioridadCmp = { oficina: 0, cowork: 1, local: 2, cochera: 3 };
const ordenUnidadCmp = (nombre, tipo) => {
  const tp = tipoPrioridadCmp[tipo] ?? 99;
  const n = String(nombre || '').trim();
  if (/^piso\s*1\b/i.test(n)) return [tp, 0, 0];
  const soloNum = n.match(/^(\d+)/);
  if (soloNum) return [tp, 1, parseInt(soloNum[1], 10)];
  const mPiso = n.match(/piso\s*(\d+)/i);
  if (mPiso) return [tp, 2, parseInt(mPiso[1], 10)];
  return [tp, 3, 9999];
};
const compareUnidades = (a, b) => {
  const ka = ordenUnidadCmp(a.nombre_unidad, a.tipo);
  const kb = ordenUnidadCmp(b.nombre_unidad, b.tipo);
  for (let i = 0; i < ka.length; i++) { if (ka[i] !== kb[i]) return ka[i] - kb[i]; }
  return 0;
};
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { toast } from 'sonner';
import {
  estadosContrato,
  estadosDeposito,
  initialContratoForm,
  initialDepositoForm,
} from './contratos-depositos/constants';
import { formatMoneyInput, parseMoneyInput, getErrorMsg } from './contratos-depositos/helpers';
import { CargaMasivaDialog } from './contratos-depositos/CargaMasivaDialog';
import { BorradoresDialog } from './contratos-depositos/BorradoresDialog';
import { EditBorradorDialog } from './contratos-depositos/EditBorradorDialog';
import { EditContratoDialog } from './contratos-depositos/EditContratoDialog';
import { DocumentosSection } from './contratos-depositos/DocumentosSection';
import { CocherasModoSection } from './contratos-depositos/CocherasModoSection';
import { ReservaDialog } from './contratos-depositos/ReservaDialog';
import { EmailsFacturacionList, cleanEmailsFacturacion, emailsFacturacionTienenError } from './contratos-depositos/EmailsFacturacionList';
import { VerazSection } from './contratos-depositos/VerazSection';
import { ReservaCell } from './ReservaCell';
import { useAuth } from '../../contexts/AuthContext';

// Input de fecha en formato argentino DD/MM/AAAA. Muestra/edita en AR pero
// almacena el valor en ISO (yyyy-mm-dd), por lo que el guardado no cambia.
const FechaARInput = ({ value, onChange, min, required, testId }) => {
  const dateRef = useRef(null);
  const [text, setText] = useState('');
  const [focused, setFocused] = useState(false);

  const isoToAr = (iso) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || '');
    return m ? `${m[3]}/${m[2]}/${m[1]}` : '';
  };
  const arToIso = (s) => {
    const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(String(s).trim());
    if (!m) return '';
    const d = m[1].padStart(2, '0'), mo = m[2].padStart(2, '0'), y = m[3];
    if (+mo < 1 || +mo > 12 || +d < 1 || +d > 31) return '';
    return `${y}-${mo}-${d}`;
  };

  useEffect(() => { if (!focused) setText(isoToAr(value)); }, [value, focused]);

  const openPicker = () => {
    if (!dateRef.current) return;
    if (dateRef.current.showPicker) dateRef.current.showPicker();
    else dateRef.current.focus();
  };

  return (
    <div className="relative">
      <Input
        type="text"
        inputMode="numeric"
        placeholder="DD/MM/AAAA"
        value={text}
        onFocus={() => setFocused(true)}
        onChange={(e) => {
          const v = e.target.value.replace(/[^\d/]/g, '');
          setText(v);
          const iso = arToIso(v);
          if (iso) onChange(iso);
          else if (v === '') onChange('');
        }}
        onBlur={() => { setFocused(false); setText(isoToAr(value)); }}
        required={required}
        data-testid={testId}
        className="pr-9"
      />
      <button
        type="button"
        onClick={openPicker}
        tabIndex={-1}
        aria-label="Abrir calendario"
        className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
      >
        <Calendar className="w-4 h-4" />
      </button>
      <input
        ref={dateRef}
        type="date"
        value={value || ''}
        min={min}
        onChange={(e) => onChange(e.target.value)}
        className="sr-only"
        tabIndex={-1}
        aria-hidden="true"
      />
    </div>
  );
};


export const ContratosDepositos = ({ edificioId, edificio, unidades, mode = 'all', onReloadUnidades }) => {
  const { user } = useAuth();
  const isComercial = user?.rol === 'comercial';
  // mode: 'all' | 'contratos' | 'depositos'
  const [contratos, setContratos] = useState([]);
  const [depositos, setDepositos] = useState([]);  const [loading, setLoading] = useState(true);
  const [expandedUnidades, setExpandedUnidades] = useState({});
  
  // Modal states
  const [isContratoDialogOpen, setIsContratoDialogOpen] = useState(false);
  // Diálogo "Cargar reserva" Foresta — nuevo sistema con ReservaDialog
  const [isReservaDialogOpen, setIsReservaDialogOpen] = useState(false);
  const [reservaDialogMode, setReservaDialogMode] = useState('create'); // 'create' | 'edit'
  const [reservaToEdit, setReservaToEdit] = useState(null);
  const [reservaToDelete, setReservaToDelete] = useState(null);
  const [isDeleteReservaOpen, setIsDeleteReservaOpen] = useState(false);
  // Reservas activas (lista cargada vía reservasApi)
  const [reservasActivas, setReservasActivas] = useState([]);
  const [verazResumen, setVerazResumen] = useState({}); // { contrato_id: count }
  const [isDepositoDialogOpen, setIsDepositoDialogOpen] = useState(false);
  const [isDetalleDepositoOpen, setIsDetalleDepositoOpen] = useState(false);
  const [isFinalizarContratoOpen, setIsFinalizarContratoOpen] = useState(false);
  const [fechaBajaContrato, setFechaBajaContrato] = useState(() => new Date().toISOString().slice(0, 10));
  const [isDevolucionDialogOpen, setIsDevolucionDialogOpen] = useState(false);
  const [isDeleteDepositoOpen, setIsDeleteDepositoOpen] = useState(false);
  const [isDeleteContratoOpen, setIsDeleteContratoOpen] = useState(false);
  const [isEditContratoOpen, setIsEditContratoOpen] = useState(false);
  const [editContratoForm, setEditContratoForm] = useState(null);
  
  // Tab activo para modales de contrato
  const [contratoTab, setContratoTab] = useState('carga_ia');
  const documentosRef = useRef(null);
  const [editContratoTab, setEditContratoTab] = useState('documentos');
  const [isEditMode, setIsEditMode] = useState(false); // Modo lectura por defecto para contratos
  const [isEditGarantiaMode, setIsEditGarantiaMode] = useState(false); // Modo lectura por defecto para garantías

  // Ref para abrir el modal "Nuevo contrato" de la vista Torre Emilia desde la barra superior
  const teViewRef = useRef(null);
  const isTorreEmilia = edificio?.modelo_administracion === 'gc_factura_y_cobra';

  // ── Cowork: paso previo (Oficina/Cowork) + alta simplificada de suscriptor ──
  const [coworkChoiceOpen, setCoworkChoiceOpen] = useState(false);
  const [coworkOficinaAction, setCoworkOficinaAction] = useState(null);
  const [coworkDialogOpen, setCoworkDialogOpen] = useState(false);
  const [savingCowork, setSavingCowork] = useState(false);
  const [coworkSuscriptores, setCoworkSuscriptores] = useState([]);
  const [coworkDisponibles, setCoworkDisponibles] = useState([]);
  const [coworkEditId, setCoworkEditId] = useState(null);
  const [contratoFilter, setContratoFilter] = useState('oficina'); // 'oficina' | 'cowork'
  const initialCoworkForm = {
    unidad_id: '', tipo: 'virtual', nombre: '', cuit: '', razon_social: '', emails: [''], telefono: '',
    condicion_iva: 'responsable_inscripto', fecha_inicio: '', descuento_pct: '',
  };
  const [coworkForm, setCoworkForm] = useState(initialCoworkForm);

  const coworkMontoBase = coworkForm.tipo === 'full'
    ? (edificio?.monto_cowork_full ?? 0)
    : (edificio?.monto_cowork_virtual ?? 0);
  const coworkDescuento = parseFloat(coworkForm.descuento_pct) || 0;
  const coworkMontoFinal = Math.max(0, Math.round(coworkMontoBase * (1 - coworkDescuento / 100)));
  // Opciones de unidad para el select: disponibles + (al editar) la unidad actual del suscriptor.
  const coworkUnidadOptions = (() => {
    const opts = [...coworkDisponibles];
    if (coworkEditId && coworkForm.unidad_id && !opts.some(u => u.unidad_id === coworkForm.unidad_id)) {
      const s = coworkSuscriptores.find(x => x.suscriptor_id === coworkEditId);
      opts.unshift({ unidad_id: coworkForm.unidad_id, nombre_unidad: s?.numero_cowork || 'Actual' });
    }
    return opts;
  })();

  const loadCoworkSuscriptores = async () => {
    if (!edificio?.edificio_id) return;
    try {
      const r = await coworkSuscriptoresApi.list(edificio.edificio_id);
      setCoworkSuscriptores(r.data || []);
    } catch { /* noop */ }
  };
  const loadCoworkDisponibles = async () => {
    if (!edificio?.edificio_id) return;
    try {
      const r = await coworkSuscriptoresApi.disponibles(edificio.edificio_id);
      setCoworkDisponibles(r.data || []);
    } catch { /* noop */ }
  };
  useEffect(() => { loadCoworkSuscriptores(); loadCoworkDisponibles(); /* eslint-disable-next-line */ }, [edificio?.edificio_id]);

  // Abre el paso previo; `oficinaAction` es lo que se ejecuta si elige "Oficina".
  const openTipoContratoChoice = (oficinaAction) => {
    setCoworkOficinaAction(() => oficinaAction);
    setCoworkChoiceOpen(true);
  };

  const handleGuardarCowork = async () => {
    if (!coworkForm.unidad_id) { toast.error('Elegí una unidad de cowork disponible'); return; }
    if (!coworkForm.nombre.trim()) { toast.error('Ingresá el nombre del suscriptor'); return; }
    if (!coworkForm.fecha_inicio) { toast.error('Ingresá la fecha de inicio'); return; }
    setSavingCowork(true);
    try {
      const payload = {
        edificio_id: edificio.edificio_id,
        unidad_id: coworkForm.unidad_id,
        tipo: coworkForm.tipo,
        nombre: coworkForm.nombre.trim(),
        cuit: coworkForm.cuit.trim() || null,
        razon_social: coworkForm.razon_social.trim() || null,
        email: cleanEmailsFacturacion(coworkForm.emails || []).join(', ') || null,
        telefono: (coworkForm.telefono || '').trim() || null,
        condicion_iva: coworkForm.condicion_iva,
        fecha_inicio: coworkForm.fecha_inicio,
        descuento_pct: coworkDescuento,
        monto_base: coworkMontoBase,
        monto_final: coworkMontoFinal,
      };
      if (coworkEditId) {
        await coworkSuscriptoresApi.update(coworkEditId, payload);
        toast.success('Suscriptor Cowork actualizado');
      } else {
        await coworkSuscriptoresApi.create(payload);
        toast.success('Suscriptor Cowork dado de alta');
      }
      setCoworkDialogOpen(false);
      setCoworkEditId(null);
      setCoworkForm(initialCoworkForm);
      loadCoworkSuscriptores();
      loadCoworkDisponibles();
    } catch (e) {
      toast.error(getErrorMsg(e) || 'Error al guardar el suscriptor');
    } finally {
      setSavingCowork(false);
    }
  };

  const handleEditarCowork = (s) => {
    setCoworkEditId(s.suscriptor_id);
    setCoworkForm({
      unidad_id: s.unidad_id || '',
      tipo: s.tipo || 'virtual',
      nombre: s.nombre || '',
      cuit: s.cuit || '',
      razon_social: s.razon_social || '',
      emails: (s.email || '').split(/[,;]/).map(x => x.trim()).filter(Boolean),
      telefono: s.telefono || '',
      condicion_iva: s.condicion_iva || 'responsable_inscripto',
      fecha_inicio: s.fecha_inicio || '',
      descuento_pct: (s.descuento_pct ?? '') === 0 ? '' : (s.descuento_pct ?? ''),
    });
    loadCoworkDisponibles();
    setCoworkDialogOpen(true);
  };

  const handleFinalizarCowork = async (s) => {
    if (!window.confirm(`¿Finalizar el suscriptor de ${s.numero_cowork}? Queda en el historial y la unidad se libera.`)) return;
    try {
      await coworkSuscriptoresApi.finalizar(s.suscriptor_id);
      toast.success('Suscriptor finalizado');
      loadCoworkSuscriptores();
      loadCoworkDisponibles();
    } catch (e) {
      toast.error(getErrorMsg(e) || 'Error al finalizar el suscriptor');
    }
  };

  const handleEliminarCowork = async (s) => {
    if (!window.confirm(`¿Eliminar definitivamente el suscriptor de ${s.numero_cowork}? Esta acción no se puede deshacer.`)) return;
    try {
      await coworkSuscriptoresApi.eliminar(s.suscriptor_id);
      toast.success('Suscriptor eliminado');
      loadCoworkSuscriptores();
      loadCoworkDisponibles();
    } catch (e) {
      toast.error(getErrorMsg(e) || 'Error al eliminar el suscriptor');
    }
  };



  // Modo multi-unidad (Torre Emilia): habilita la solapa "Unidades del contrato" como primera
  // dentro del MISMO dialog de Nuevo Contrato que usa Foresta. NO duplica el formulario.
  const [multiUnidadMode, setMultiUnidadMode] = useState(false);
  const [seleccionMultiUnidad, setSeleccionMultiUnidad] = useState([]); // [{kind:'unidad'|'conjunto', id, nombre, tipo, miembros?}]
  const [catalogoMultiUnidad, setCatalogoMultiUnidad] = useState(null); // {unidades, conjuntos} desde TE
  const [unidadBusqueda, setUnidadBusqueda] = useState(''); // filtro de texto de "Unidades disponibles"
  // Resultado de la detección de unidades por IA después de analizar un contrato.
  // status: 'none' (default), 'matched' (detectó+matcheó), 'detected_no_match' (detectó pero no matchea catálogo), 'not_detected' (analizó y no encontró)
  const [iaDeteccionUnidades, setIaDeteccionUnidades] = useState({ status: 'none', detectados: [], matched: [] });
  
  // Filtro de estado para depósitos
  const [filtroEstadoDeposito, setFiltroEstadoDeposito] = useState('vigente');
  // Buscador de depósitos
  const [busquedaDeposito, setBusquedaDeposito] = useState('');
  // Ordenamiento de alquileres
  // Ordenamiento por columnas de la tabla de alquileres Foresta (no se persiste).
  const { sortConfig: sortAlq, requestSort: requestSortAlq } = useTableSort();
  
  // Form states
  const [selectedUnidad, setSelectedUnidad] = useState(null);
  const [selectedContrato, setSelectedContrato] = useState(null);
  const [selectedDeposito, setSelectedDeposito] = useState(null);
  const [contratoForm, setContratoForm] = useState(initialContratoForm);
  const [showProvPassNuevo, setShowProvPassNuevo] = useState(false);
  // "Monto Actualizado a Hoy" para el modal de NUEVO contrato (misma lógica que edición).
  const [montoActHoyNuevo, setMontoActHoyNuevo] = useState(null);
  useEffect(() => {
    if (!isContratoDialogOpen) { setMontoActHoyNuevo(null); return; }
    const mi = parseFloat(contratoForm?.monto_inicial);
    const fi = contratoForm?.fecha_inicio;
    if (!mi || mi <= 0 || !fi) { setMontoActHoyNuevo(null); return; }
    const indices = contratoForm?.ajuste_indices || [];
    const esPorcentaje = (contratoForm?.indice_tipo === 'porcentaje');
    const sinAjuste = (contratoForm?.indice_tipo === 'sin_ajuste');
    const payload = {
      monto_inicial: mi,
      fecha_inicio: fi,
      indice_tipo: sinAjuste ? 'sin_ajuste' : (esPorcentaje ? 'porcentaje' : 'ipc'),
      indice_frecuencia: contratoForm?.ajuste_frecuencia || contratoForm?.indice_frecuencia || 'trimestral',
      indice_valor_porcentaje: esPorcentaje ? (parseFloat(contratoForm?.indice_valor) || null) : null,
      ajuste_indices: sinAjuste ? [{ tipo: 'sin_ajuste', peso: 100 }] : ((indices && indices.length > 0) ? indices : null),
    };
    let cancel = false;
    contratosApi.calcularMontoActualizado(payload)
      .then((r) => { if (!cancel) setMontoActHoyNuevo(r.data); })
      .catch(() => { if (!cancel) setMontoActHoyNuevo(null); });
    return () => { cancel = true; };
  }, [isContratoDialogOpen, contratoForm?.monto_inicial, contratoForm?.fecha_inicio,
      contratoForm?.indice_tipo, contratoForm?.indice_frecuencia, contratoForm?.ajuste_frecuencia,
      contratoForm?.indice_valor, contratoForm?.ajuste_indices]);

  const [depositoForm, setDepositoForm] = useState(initialDepositoForm);
  // reserva_id de origen cuando el nuevo contrato se crea desde una reserva (para mostrar Veraz ya cargados)
  const [reservaParaContrato, setReservaParaContrato] = useState(null);
  const [devolucionForm, setDevolucionForm] = useState({ monto: '', fecha: '', comentario: '' });
  const [editGarantiaForm, setEditGarantiaForm] = useState(initialDepositoForm); // Para edición de garantía
  const [saving, setSaving] = useState(false);
  const [tenedorGarantia, setTenedorGarantia] = useState('administrador'); // 'propietario' o 'administrador'
  const [garantiaNoDisponible, setGarantiaNoDisponible] = useState(false); // contrato viejo sin garantía registrada
  
  // Estado para análisis de contrato con IA
  const [contratoFile, setContratoFile] = useState(null);
  const [analizandoContrato, setAnalizandoContrato] = useState(false);
  
  // Estado para carga masiva de contratos
  const [isCargaMasivaOpen, setIsCargaMasivaOpen] = useState(false);
  const [cargaMasivaFiles, setCargaMasivaFiles] = useState([]);
  const [procesandoCargaMasiva, setProcesandoCargaMasiva] = useState(false);
  const [resultadosCargaMasiva, setResultadosCargaMasiva] = useState(null);
  const cargaMasivaInputRef = useRef(null);
  
  // Estado para borradores
  const [borradores, setBorradores] = useState([]);
  const [isBorradoresOpen, setIsBorradoresOpen] = useState(false);
  const [selectedBorrador, setSelectedBorrador] = useState(null);
  const [isEditBorradorOpen, setIsEditBorradorOpen] = useState(false);
  const [borradorForm, setBorradorForm] = useState({});

  useEffect(() => {
    if (edificioId) {
      fetchData();
    }
  }, [edificioId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [contratosRes, depositosRes, borradoresRes, reservasRes, verazRes] = await Promise.all([
        contratosApi.list({ edificio_id: edificioId }),
        depositosApi.list({ edificio_id: edificioId }),
        contratosApi.listBorradores({ edificio_id: edificioId, estado: 'pendiente' }),
        reservasApi.list({ edificio_id: edificioId, estado: 'activa' }),
        verazApi.contratosResumen().catch(() => ({ data: {} })),
      ]);
      setContratos(contratosRes.data);
      setDepositos(depositosRes.data);
      setBorradores(borradoresRes.data);
      setReservasActivas(reservasRes.data || []);
      setVerazResumen(verazRes.data || {});
    } catch (err) {
      console.error('Error fetching data:', err);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const toggleExpanded = (unidadId) => {
    setExpandedUnidades(prev => ({
      ...prev,
      [unidadId]: !prev[unidadId]
    }));
  };

  const getContratosUnidad = (unidadId) => {
    return contratos.filter(c => c.unidad_id === unidadId);
  };

  const getDepositosUnidad = (unidadId) => {
    return depositos.filter(d => d.unidad_id === unidadId);
  };

  const getContratoVigente = (unidadId) => {
    return contratos.find(c => 
      c.estado === 'vigente' && (
        c.unidad_id === unidadId || 
        c.unidades_contrato?.some(uc => uc.unidad_id === unidadId)
      )
    );
  };

  const getDepositoContrato = (contratoId) => {
    return depositos.find(d => d.contrato_id === contratoId && d.estado === 'vigente');
  };

  // ============ CONTRATO HANDLERS ============
  const handleOpenContrato = (unidad) => {
    setSelectedUnidad(unidad);
    setSelectedContrato(null);
    setContratoForm(initialContratoForm);
    setReservaParaContrato(null);
    // Modo single-unit (Foresta legacy)
    setMultiUnidadMode(false);
    setSeleccionMultiUnidad([]);
    setCatalogoMultiUnidad(null);
    setContratoTab('carga_ia');
    setIsContratoDialogOpen(true);
  };

  // Handler para abrir el dialog en modo multi-unidad (Torre Emilia).
  // Recibe el catálogo de unidades/conjuntos alquilables desde la vista TE.
  // La primera solapa es "Carga IA" para que el usuario suba el PDF primero y la IA
  // pueda preseleccionar las unidades detectadas en la solapa "Unidades" siguiente.
  const handleOpenContratoMultiUnidad = (catalogo) => {
    setSelectedUnidad(null);
    setSelectedContrato(null);
    setContratoForm(initialContratoForm);
    setReservaParaContrato(null);
    setMultiUnidadMode(true);
    setSeleccionMultiUnidad([]);
    setUnidadBusqueda('');
    setCatalogoMultiUnidad(catalogo || null);
    setIaDeteccionUnidades({ status: 'none', detectados: [], matched: [] });
    setContratoTab('carga_ia'); // Carga IA es la primera solapa (puede preseleccionar unidades)
    setIsContratoDialogOpen(true);
  };

  // Handler para "Completar contrato" desde una reserva activa (Foresta).
  // Abre el modal de nuevo contrato con TODOS los datos de la reserva pre-cargados:
  // unidad/es, inquilino, monto, moneda, fechas, observaciones. Si la reserva tiene
  // archivo adjunto, también lo descarga y dispara análisis IA para extraer datos
  // adicionales del contrato. Tras crear el contrato, el backend marca la reserva
  // como completada y asocia contrato_id automáticamente.
  const handleCompletarContratoDesdeReserva = async (reserva) => {
    const unidadesRes = reserva.unidades_reserva || [];
    if (unidadesRes.length === 0) {
      toast.error('La reserva no tiene unidades asociadas');
      return;
    }

    // Resolver objetos unidad completos desde el catálogo
    const unidadesObj = unidadesRes
      .map(ur => unidades.find(u => u.unidad_id === ur.unidad_id))
      .filter(Boolean);
    if (unidadesObj.length === 0) {
      toast.error('No se encontraron las unidades de la reserva en el catálogo');
      return;
    }
    const primeraUnidad = unidadesObj[0];

    // Reset y abrir modal
    setSelectedUnidad(primeraUnidad);
    setSelectedContrato(null);
    setReservaParaContrato(reserva.reserva_id || null);

    // Pre-cargar form con datos de la reserva
    const montoStr = reserva.monto_total != null ? String(reserva.monto_total) : '';
    setContratoForm({
      ...initialContratoForm,
      razon_social: reserva.razon_social || '',
      nombre_comercial: reserva.nombre_comercial || '',
      cuit: reserva.cuit || '',
      contacto_comercial_email: reserva.email || '',
      email_facturacion: reserva.email || '',
      contacto_comercial_telefono: reserva.telefono || '',
      monto_inicial: montoStr,
      monto_actual: montoStr,
      moneda: reserva.moneda || 'ARS',
      fecha_inicio: reserva.fecha_inicio_estimada || '',
    });

    // Multi-unidad si corresponde
    if (unidadesObj.length > 1) {
      setMultiUnidadMode(true);
      const catalogo = unidades.filter(u => u.en_administracion);
      setCatalogoMultiUnidad(catalogo);
      setSeleccionMultiUnidad(
        unidadesObj.map(u => ({
          kind: 'unidad', id: u.unidad_id, nombre: u.nombre_unidad, tipo: u.tipo,
        }))
      );
    } else {
      setMultiUnidadMode(false);
      setSeleccionMultiUnidad([]);
      setCatalogoMultiUnidad(null);
    }

    setContratoTab('carga_ia');
    setIsContratoDialogOpen(true);

    // Si tiene archivo, intentar análisis IA en background
    if (reserva.archivo?.archivo_id) {
      try {
        toast.info('Descargando archivo de reserva para analizarlo con IA...');
        const url = reservasApi.downloadArchivoUrl(reserva.reserva_id);
        const res = await fetch(url);
        if (!res.ok) throw new Error('No se pudo descargar el archivo');
        const blob = await res.blob();
        const fileName = reserva.archivo.nombre || 'reserva';
        const file = new File([blob], fileName, { type: blob.type || 'application/octet-stream' });
        await handleAnalizarContrato(file);
      } catch (err) {
        console.error('Error analizando archivo de reserva:', err);
        // No bloqueante — el usuario ya tiene los datos manuales precargados
      }
    }
  };

  // Editar reserva
  const handleEditReserva = (reserva) => {
    setReservaToEdit(reserva);
    setReservaDialogMode('edit');
    setIsReservaDialogOpen(true);
  };

  // Eliminar reserva (con confirm)
  const handleAskDeleteReserva = (reserva) => {
    setReservaToDelete(reserva);
    setIsDeleteReservaOpen(true);
  };

  const handleConfirmDeleteReserva = async () => {
    if (!reservaToDelete) return;
    setSaving(true);
    try {
      await reservasApi.delete(reservaToDelete.reserva_id);
      toast.success('Reserva eliminada');
      setIsDeleteReservaOpen(false);
      setReservaToDelete(null);
      await fetchData();
      onReloadUnidades && onReloadUnidades();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al eliminar reserva');
    } finally {
      setSaving(false);
    }
  };

  // Función para recalcular monto actual
  const handleRecalcularMonto = async () => {
    if (!contratoForm.monto_inicial || !contratoForm.fecha_inicio) {
      toast.error('Ingresá el monto inicial y fecha de inicio primero');
      return;
    }
    
    try {
      const res = await contratosApi.calcularMonto({
        monto_inicial: parseFloat(contratoForm.monto_inicial),
        fecha_inicio: contratoForm.fecha_inicio,
        indice_tipo: contratoForm.indice_tipo,
        indice_frecuencia: contratoForm.indice_frecuencia,
        indice_valor: contratoForm.indice_tipo === 'porcentaje' ? parseFloat(contratoForm.indice_valor) : null,
      });
      
      setContratoForm(prev => ({
        ...prev,
        monto_actual: res.data.monto_actual.toString(),
        montoCalculadoAuto: true,
      }));
      
      toast.success(`Monto actual recalculado: ${contratoForm.moneda} ${res.data.monto_actual.toLocaleString()}`);
    } catch (err) {
      toast.error('Error al calcular monto');
    }
  };

  // Handler para analizar contrato con IA
  const handleAnalizarContrato = async (file) => {
    // En modo multi-unidad, si aún no hay unidad seleccionada, usamos la primera del catálogo
    // como contexto para el endpoint (solo para que la API tenga referencia).
    let unidadRef = selectedUnidad;
    if (!unidadRef && multiUnidadMode && catalogoMultiUnidad) {
      const cat = catalogoMultiUnidad;
      unidadRef = (cat.principales || [])[0] || (cat.piso1 || [])[0] || (cat.cocherasSueltas || [])[0] || null;
    }
    if (!unidadRef) return;
    setAnalizandoContrato(true);
    
    try {
      toast.info('Analizando contrato con IA...');
      const analisis = await unidadesApi.analizarContrato(unidadRef.unidad_id, file);
      
      if (analisis.data.mensaje) {
        // La IA puede devolver múltiples campos
        const razonSocial = analisis.data.razon_social || analisis.data.inquilino || '';
        const nombreComercial = analisis.data.nombre_comercial || '';
        
        // Auto-fill the form with ALL extracted data
        setContratoForm(prev => ({
          ...prev,
          // Datos básicos del inquilino
          razon_social: razonSocial || prev.razon_social,
          nombre_comercial: nombreComercial || prev.nombre_comercial,
          // Datos de contacto del inquilino (mapear a campos existentes)
          cuit: analisis.data.cuit_inquilino || prev.cuit,
          contacto_comercial_email: analisis.data.email_inquilino || prev.contacto_comercial_email,
          email_facturacion: analisis.data.email_inquilino || prev.email_facturacion,
          domicilio_fiscal: analisis.data.domicilio_inquilino || prev.domicilio_fiscal,
          condicion_iva: analisis.data.condicion_iva_inquilino || prev.condicion_iva,
          // Fechas
          fecha_inicio: analisis.data.fecha_inicio || prev.fecha_inicio,
          fecha_fin: analisis.data.fecha_fin || prev.fecha_fin,
          // Montos
          monto_inicial: analisis.data.monto_alquiler_inicio 
            ? analisis.data.monto_alquiler_inicio.toString()
            : prev.monto_inicial,
          monto_actual: analisis.data.monto_actual
            ? analisis.data.monto_actual.toString()
            : prev.monto_actual,
          moneda: analisis.data.moneda || prev.moneda,
          // Índices de ajuste
          indice_tipo: analisis.data.indice_tipo || prev.indice_tipo,
          indice_frecuencia: analisis.data.indice_frecuencia || prev.indice_frecuencia,
          // Datos de cobro
          dia_vencimiento: analisis.data.dia_vencimiento_pago ? analisis.data.dia_vencimiento_pago.toString() : prev.dia_vencimiento,
          // Archivo
          archivo_contrato_id: analisis.data.archivo_id || null,
          montoCalculadoAuto: true,
        }));
        
        // Contar campos completados
        const camposCompletados = [
          razonSocial, nombreComercial, analisis.data.cuit_inquilino,
          analisis.data.email_inquilino, analisis.data.telefono_inquilino,
          analisis.data.fecha_inicio, analisis.data.fecha_fin,
          analisis.data.monto_alquiler_inicio, analisis.data.indice_tipo
        ].filter(Boolean).length;
        
        const mensaje = razonSocial 
          ? `✨ Contrato analizado. ${camposCompletados} campos completados. Monto actual: ${analisis.data.moneda || 'ARS'} ${analisis.data.monto_actual?.toLocaleString() || analisis.data.monto_alquiler_inicio?.toLocaleString()}`
          : 'No se pudo extraer información del contrato';
        
        if (razonSocial) {
          toast.success(mensaje);
          // Cambiar automáticamente a la pestaña "Datos" después de un análisis exitoso
          setContratoTab('basico');
        } else {
          toast.warning(mensaje);
        }

        // En modo multi-unidad: si la IA detectó unidades en el contrato, preseleccionarlas
        // en la solapa "Unidades". Aceptamos varios shapes: unidades_detectadas[], unidades[],
        // unidad_detectada (string), unidad (string).
        if (multiUnidadMode && catalogoMultiUnidad) {
          const cat = catalogoMultiUnidad;
          const todasUnidadesCat = [
            ...(cat.principales || []),
            ...(cat.piso1 || []),
            ...(cat.cocherasComponentes || []),
            ...(cat.cocherasSueltas || []),
          ];
          // Normaliza un nombre para matchear (mayúsculas, sin espacios extras)
          const norm = (s) => String(s || '').trim().toUpperCase().replace(/\s+/g, '');
          const catByNombre = new Map();
          todasUnidadesCat.forEach(u => catByNombre.set(norm(u.nombre_unidad), u));

          // Coleccionamos nombres detectados desde varios shapes posibles
          const nombresDetectados = [];
          if (Array.isArray(analisis.data.unidades_detectadas)) {
            for (const x of analisis.data.unidades_detectadas) {
              nombresDetectados.push(typeof x === 'string' ? x : (x?.nombre_unidad || x?.nombre || ''));
            }
          }
          if (Array.isArray(analisis.data.unidades)) {
            for (const x of analisis.data.unidades) {
              nombresDetectados.push(typeof x === 'string' ? x : (x?.nombre_unidad || x?.nombre || ''));
            }
          }
          if (analisis.data.unidad_detectada) nombresDetectados.push(analisis.data.unidad_detectada);
          if (analisis.data.unidad) nombresDetectados.push(analisis.data.unidad);
          const detectadosLimpios = nombresDetectados.map(s => String(s || '').trim()).filter(Boolean);

          const matched = [];
          const yaIncluidas = new Set();
          for (const nombre of detectadosLimpios) {
            const k = norm(nombre);
            if (!k || yaIncluidas.has(k)) continue;
            const u = catByNombre.get(k);
            if (u) {
              matched.push({ kind: 'unidad', id: u.unidad_id, nombre: u.nombre_unidad, tipo: u.tipo });
              yaIncluidas.add(k);
            }
          }

          // Setear el estado de detección IA para mostrar el banner correspondiente.
          if (matched.length > 0) {
            setSeleccionMultiUnidad(matched);
            setSelectedUnidad(catByNombre.get(norm(matched[0].nombre)));
            setIaDeteccionUnidades({
              status: 'matched',
              detectados: detectadosLimpios,
              matched: matched.map(m => m.nombre),
            });
            toast.info(`IA preseleccionó ${matched.length} unidad${matched.length > 1 ? 'es' : ''}. Revisalas en la solapa "Unidades".`);
          } else if (detectadosLimpios.length > 0) {
            setIaDeteccionUnidades({
              status: 'detected_no_match',
              detectados: detectadosLimpios,
              matched: [],
            });
          } else {
            setIaDeteccionUnidades({ status: 'not_detected', detectados: [], matched: [] });
          }
        }
      } else {
        toast.warning('No se pudo extraer información del contrato');
      }
    } catch (err) {
      console.error('Error:', err);
      toast.error(getErrorMsg(err, 'Error al analizar contrato'));
    } finally {
      setAnalizandoContrato(false);
      setContratoFile(null);
    }
  };

  const handleSaveContrato = async (e) => {
    e.preventDefault();

    // En modo multi-unidad (Torre Emilia), validar que hay al menos una unidad/conjunto seleccionado.
    if (multiUnidadMode && (!seleccionMultiUnidad || seleccionMultiUnidad.length === 0)) {
      toast.error('Seleccioná al menos una unidad o conjunto en la solapa "Unidades del contrato".');
      setContratoTab('unidades');
      return;
    }

    // Validate required fields before sending
    const missingFields = [];
    if (!contratoForm.razon_social && !contratoForm.nombre_comercial) missingFields.push('Razón Social o Nombre Comercial');
    if (!contratoForm.fecha_inicio) missingFields.push('Fecha de inicio');
    if (!contratoForm.fecha_fin) missingFields.push('Fecha de fin');
    if (!contratoForm.monto_inicial) missingFields.push('Monto inicial');
    
    if (missingFields.length > 0) {
      toast.error(`Completá los campos obligatorios en la pestaña "Datos": ${missingFields.join(', ')}`);
      return;
    }

    // Multi-índice: validar suma de pesos = 100% si hay ajuste_indices
    if (contratoForm.ajuste_indices && contratoForm.ajuste_indices.length > 0) {
      const suma = contratoForm.ajuste_indices.reduce((s, i) => s + (parseFloat(i.peso) || 0), 0);
      if (Math.abs(suma - 100) > 0.01) {
        toast.error(`La suma de pesos de los índices debe ser 100% (actual: ${suma.toFixed(2)}%)`);
        setContratoTab('condiciones');
        return;
      }
    }

    // Emails de facturación: validar formato/duplicados y exigir al menos uno si el método es email/ambos
    {
      const _metodo = contratoForm.facturacion_metodo || 'email';
      const _list = (contratoForm.emails_facturacion && contratoForm.emails_facturacion.length)
        ? contratoForm.emails_facturacion
        : (contratoForm.email_facturacion ? [contratoForm.email_facturacion] : []);
      if (emailsFacturacionTienenError(_list)) {
        toast.error('Revisá los emails de facturación: hay formato inválido o duplicados.');
        setContratoTab('fiscal');
        return;
      }
      if ((_metodo === 'email' || _metodo === 'ambos') && cleanEmailsFacturacion(_list).length === 0) {
        toast.error('Agregá al menos un email de facturación (el método de envío es Email/Ambas).');
        setContratoTab('fiscal');
        return;
      }
    }
    
    setSaving(true);
    try {
      // Usar nombre_comercial si está, sino usar razon_social como inquilino para display
      const inquilinoDisplay = contratoForm.nombre_comercial || contratoForm.razon_social;
      
      // Parsear montos que pueden venir con formato
      const montoInicial = parseFloat(String(contratoForm.monto_inicial).replace(/[^\d.-]/g, '')) || 0;
      const montoActual = contratoForm.monto_actual 
        ? parseFloat(String(contratoForm.monto_actual).replace(/[^\d.-]/g, '')) 
        : null;
      
      const data = {
        unidad_id: selectedUnidad.unidad_id,
        inquilino: inquilinoDisplay,
        nombre_comercial: contratoForm.nombre_comercial || null,
        razon_social: contratoForm.razon_social,
        fecha_inicio: contratoForm.fecha_inicio,
        fecha_fin: contratoForm.fecha_fin,
        monto_inicial: montoInicial,
        monto_actual: montoActual,
        fecha_carga_monto: new Date().toISOString().split('T')[0], // Fecha de carga del monto
        moneda: contratoForm.moneda,
        // Valoración por cochera (unidad_id -> {modo, valor_base})
        cocheras_valuacion: contratoForm.cocheras_valuacion || {},
        indice_tipo: contratoForm.indice_tipo,
        indice_frecuencia: contratoForm.indice_tipo !== 'otro' ? contratoForm.indice_frecuencia : null,
        indice_valor: contratoForm.indice_tipo === 'porcentaje' ? parseFloat(contratoForm.indice_valor) : null,
        indice_condiciones: contratoForm.indice_tipo === 'otro' ? contratoForm.indice_condiciones : null,
        // Multi-índice + Valor referencia USD (Jun 2026)
        ajuste_indices: contratoForm.ajuste_indices && contratoForm.ajuste_indices.length > 0
          ? contratoForm.ajuste_indices.map(i => ({ tipo: (i.tipo || 'IPC').toUpperCase(), peso: parseFloat(i.peso) || 0 }))
          : null,
        ajuste_frecuencia: contratoForm.ajuste_frecuencia || contratoForm.indice_frecuencia || null,
        valor_referencia: contratoForm.valor_referencia && (contratoForm.valor_referencia.monto != null)
          ? { monto: parseFloat(contratoForm.valor_referencia.monto) || 0, moneda: contratoForm.valor_referencia.moneda || 'USD' }
          : null,
        archivo_contrato_id: contratoForm.archivo_contrato_id || null,
        // Gestión financiera
        fecha_inicio_gestion: contratoForm.fecha_inicio_gestion || null,
        monto_alquiler_gestion: contratoForm.monto_alquiler_gestion 
          ? parseFloat(String(contratoForm.monto_alquiler_gestion).replace(/[^\d.-]/g, '')) 
          : null,
        // Tipo de inquilino + firmante (persona humana)
        tipo_inquilino: contratoForm.tipo_inquilino || 'juridica',
        firmante_nombre: contratoForm.firmante_nombre || null,
        firmante_email: contratoForm.firmante_email || null,
        firmante_whatsapp: contratoForm.firmante_whatsapp || null,
        // Flags "mismo que..."
        contacto_comercial_mismo_firmante: contratoForm.contacto_comercial_mismo_firmante ?? null,
        contacto_admin_mismo_firmante: contratoForm.contacto_admin_mismo_firmante ?? null,
        contacto_admin_mismo_comercial: contratoForm.contacto_admin_mismo_comercial ?? null,
        email_facturacion_default: contratoForm.email_facturacion_default ?? null,
        // Alta de acceso a plataforma
        acceso_nombre: contratoForm.acceso_nombre || null,
        acceso_email: contratoForm.acceso_email || null,
        acceso_mismo_firmante: contratoForm.acceso_mismo_firmante ?? null,
        acceso_mismo_admin: contratoForm.acceso_mismo_admin ?? null,
        // Contactos
        contacto_comercial_nombre: contratoForm.contacto_comercial_nombre || null,
        contacto_comercial_email: contratoForm.contacto_comercial_email || null,
        contacto_comercial_telefono: contratoForm.contacto_comercial_telefono || null,
        contacto_admin_nombre: contratoForm.contacto_admin_nombre || null,
        contacto_admin_email: contratoForm.contacto_admin_email || null,
        contacto_admin_telefono: contratoForm.contacto_admin_telefono || null,
        // Descripción de empresa
        descripcion_empresa: contratoForm.descripcion_empresa || null,
        // Datos fiscales
        cuit: contratoForm.cuit || null,
        domicilio_fiscal: contratoForm.domicilio_fiscal || null,
        condicion_iva: contratoForm.condicion_iva || null,
        email_facturacion: (() => {
          const _l = (contratoForm.emails_facturacion && contratoForm.emails_facturacion.length) ? contratoForm.emails_facturacion : (contratoForm.email_facturacion ? [contratoForm.email_facturacion] : []);
          return cleanEmailsFacturacion(_l)[0] || null;
        })(),
        emails_facturacion: cleanEmailsFacturacion((contratoForm.emails_facturacion && contratoForm.emails_facturacion.length) ? contratoForm.emails_facturacion : (contratoForm.email_facturacion ? [contratoForm.email_facturacion] : [])),
        orden_compra: contratoForm.orden_compra || null,
        orden_compra_tipo: contratoForm.orden_compra_tipo || null,
        retencion_tipo: contratoForm.retencion_tipo || 'sin_retenciones',
        retencion_porcentaje: contratoForm.retencion_tipo === 'otro' ? (contratoForm.retencion_porcentaje ?? null) : null,
        // Plataforma de proveedores + facturación contacto + emergencia (Fase 3)
        proveedor_plataforma_nombre: contratoForm.proveedor_plataforma_nombre || null,
        proveedor_plataforma_url: contratoForm.proveedor_plataforma_url || null,
        proveedor_plataforma_usuario: contratoForm.proveedor_plataforma_usuario || null,
        proveedor_plataforma_password: contratoForm.proveedor_plataforma_password || null,
        proveedor_plataforma_instrucciones: contratoForm.proveedor_plataforma_instrucciones || null,
        facturacion_mismo_admin: contratoForm.facturacion_mismo_admin ?? null,
        facturacion_metodo: contratoForm.facturacion_metodo || null,
        orden_compra_instruccion: contratoForm.orden_compra_instruccion || null,
        facturacion_contacto_nombre: (contratoForm.facturacion_mismo_admin !== false ? contratoForm.contacto_admin_nombre : contratoForm.facturacion_contacto_nombre) || null,
        facturacion_contacto_apellido: (contratoForm.facturacion_mismo_admin !== false ? '' : contratoForm.facturacion_contacto_apellido) || null,
        facturacion_contacto_telefono: (contratoForm.facturacion_mismo_admin !== false ? contratoForm.contacto_admin_telefono : contratoForm.facturacion_contacto_telefono) || null,
        facturacion_contacto_email: (contratoForm.facturacion_mismo_admin !== false ? contratoForm.contacto_admin_email : contratoForm.facturacion_contacto_email) || null,
        emergencia_nombre: contratoForm.emergencia_nombre || null,
        emergencia_telefono: contratoForm.emergencia_telefono || null,
        emergencia_email: contratoForm.emergencia_email || null,
        // Datos de cobro
        dia_emision: contratoForm.dia_emision ? parseInt(contratoForm.dia_emision) : null,
        dia_vencimiento: contratoForm.dia_vencimiento ? parseInt(contratoForm.dia_vencimiento) : null,
        penalidad_enabled: contratoForm.penalidad_enabled,
        penalidad_dias_gracia: parseInt(contratoForm.penalidad_dias_gracia) || 10,
        penalidad_porcentaje_1: parseFloat(contratoForm.penalidad_porcentaje_1) || 10,
        penalidad_porcentaje_2: parseFloat(contratoForm.penalidad_porcentaje_2) || 20,
        // Colppy
        colppy_nro_factura: contratoForm.colppy_nro_factura || null,
        colppy_factura_electronica: contratoForm.colppy_factura_electronica,
        colppy_condicion_venta: contratoForm.colppy_condicion_venta || null,
        colppy_tipo_comprobante: contratoForm.colppy_tipo_comprobante || null,
        colppy_tipo_factura: contratoForm.colppy_tipo_factura || null,
        colppy_descripcion: contratoForm.colppy_descripcion || null,
        colppy_tipo_operacion: contratoForm.colppy_tipo_operacion || null,
        colppy_cuenta: contratoForm.colppy_cuenta || null,
        colppy_moneda: contratoForm.colppy_moneda || 'ARS',
        colppy_percepciones: contratoForm.colppy_percepciones,
        // Comisión (solo comercial)
        porcentaje_comision: isComercial ? (parseFloat(contratoForm.porcentaje_comision) || 50) : null,
      };
      
      // Build unidades_contrato for multi-unit contracts
      if (contratoForm.incluye_cochera && contratoForm.cocheras_incluidas?.length > 0) {
        const unidadesContrato = [];
        // Primary unit (oficina/local)
        const montoCocheras = Object.values(contratoForm.cocheras_config || {}).reduce((sum, c) => sum + (parseFloat(c.monto) || 0), 0);
        const montoUnidadPrincipal = montoInicial - montoCocheras;
        unidadesContrato.push({
          unidad_id: selectedUnidad.unidad_id,
          monto: montoUnidadPrincipal > 0 ? montoUnidadPrincipal : montoInicial,
          tipo_cochera: null
        });
        // Cocheras
        for (const cocheraId of contratoForm.cocheras_incluidas) {
          const config = contratoForm.cocheras_config?.[cocheraId] || { tipo_cochera: 'estandar', monto: 0 };
          unidadesContrato.push({
            unidad_id: cocheraId,
            monto: parseFloat(config.monto) || 0,
            tipo_cochera: config.tipo_cochera
          });
        }
        data.unidades_contrato = unidadesContrato;
      }

      // Modo multi-unidad (Torre Emilia): expandir la selección a unidades_contrato[].
      // Si el usuario eligió un conjunto (ej. Subsuelo), lo expandimos a sus miembros.
      // Esto SOBREESCRIBE cualquier unidades_contrato armado por el bloque de cocheras
      // de arriba (el flujo multi-unidad es excluyente).
      if (multiUnidadMode && seleccionMultiUnidad && seleccionMultiUnidad.length > 0) {
        const unidadesMulti = [];
        for (const sel of seleccionMultiUnidad) {
          if (sel.kind === 'unidad') {
            unidadesMulti.push({
              unidad_id: sel.id,
              monto: 0,
              tipo_cochera: sel.tipo === 'cochera' ? 'estandar' : null,
            });
          } else if (sel.kind === 'conjunto') {
            for (const m of (sel.miembros || [])) {
              unidadesMulti.push({
                unidad_id: m.unidad_id,
                monto: 0,
                tipo_cochera: m.tipo === 'cochera' ? 'estandar' : null,
              });
            }
          }
        }
        // La primera unidad lleva el monto total (el usuario puede distribuirlo manualmente luego).
        if (unidadesMulti.length > 0) {
          unidadesMulti[0].monto = montoInicial;
          data.unidad_id = unidadesMulti[0].unidad_id;  // Aseguramos coherencia con la primera unidad real.
          // Si solo hay 1 unidad, no enviamos unidades_contrato (legacy single-unit).
          data.unidades_contrato = unidadesMulti.length > 1 ? unidadesMulti : null;
        }
      }
      
      const createRes = await contratosApi.create(data);
      const nuevoContratoId = createRes?.data?.contrato_id;
      toast.success('Contrato creado');
      // Volcar etapas/documentos preparados localmente antes de crear el contrato
      if (nuevoContratoId && documentosRef.current?.hasPending?.()) {
        try {
          const flush = await documentosRef.current.flushTo(nuevoContratoId);
          if (flush.ok) {
            if (flush.etapas > 0) toast.success(`${flush.etapas} etapa(s) y ${flush.archivos} archivo(s) cargados al contrato`);
          } else {
            toast.error(`Algunos documentos fallaron al subir: ${flush.errors.slice(0, 2).join(' · ')}`);
          }
        } catch (err) {
          toast.error('Error al subir documentos al nuevo contrato');
        }
      }
      setIsContratoDialogOpen(false);
      // Limpiar modo multi-unidad
      setMultiUnidadMode(false);
      setSeleccionMultiUnidad([]);
      setCatalogoMultiUnidad(null);
      fetchData();
      onReloadUnidades && onReloadUnidades();
    } catch (err) {
      const errorMsg = getErrorMsg(err, 'Error al crear contrato');
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleFinalizarContrato = async () => {
    setSaving(true);
    try {
      await contratosApi.finalizar(selectedContrato.contrato_id, fechaBajaContrato);
      toast.success(`Contrato finalizado (baja ${formatDate(fechaBajaContrato)})`);
      setIsFinalizarContratoOpen(false);
      setSelectedContrato(null);
      fetchData();
      onReloadUnidades && onReloadUnidades();
    } catch (err) {
      toast.error(getErrorMsg(err, 'Error al finalizar contrato'));
    } finally {
      setSaving(false);
    }
  };

  // ============ CARGA MASIVA HANDLERS ============
  const handleCargaMasivaFiles = (e) => {
    const files = Array.from(e.target.files || []);
    setCargaMasivaFiles(prev => [...prev, ...files]);
  };

  const handleCargaMasivaDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files || []);
    setCargaMasivaFiles(prev => [...prev, ...files]);
  };

  const handleRemoveCargaMasivaFile = (index) => {
    setCargaMasivaFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleProcesarCargaMasiva = async () => {
    if (cargaMasivaFiles.length === 0) {
      toast.error('Seleccioná al menos un archivo');
      return;
    }
    
    setProcesandoCargaMasiva(true);
    setResultadosCargaMasiva(null);
    
    try {
      const res = await contratosApi.cargaMasivaIA(edificioId, cargaMasivaFiles);
      setResultadosCargaMasiva(res.data);
      toast.success(`${res.data.pendientes} contratos procesados correctamente`);
      
      // Refresh data to show new borradores
      fetchData();
      
      // Clear files
      setCargaMasivaFiles([]);
    } catch (err) {
      toast.error(getErrorMsg(err, 'Error al procesar archivos'));
    } finally {
      setProcesandoCargaMasiva(false);
    }
  };

  const handleCloseCargaMasiva = () => {
    setIsCargaMasivaOpen(false);
    setCargaMasivaFiles([]);
    setResultadosCargaMasiva(null);
  };

  // ============ BORRADORES HANDLERS ============
  const handleEditBorrador = (borrador) => {
    setSelectedBorrador(borrador);
    setBorradorForm({
      unidad_id: borrador.unidad_id || '',
      nombre_comercial: borrador.nombre_comercial || '',
      razon_social: borrador.razon_social || '',
      fecha_inicio: borrador.fecha_inicio || '',
      fecha_fin: borrador.fecha_fin || '',
      monto_inicial: borrador.monto_inicial || '',
      monto_actual: borrador.monto_actual || '',
      moneda: borrador.moneda || 'ARS',
      indice_tipo: borrador.indice_tipo || 'ipc',
      indice_frecuencia: borrador.indice_frecuencia || 'trimestral',
    });
    setIsEditBorradorOpen(true);
  };

  const handleSaveBorrador = async () => {
    setSaving(true);
    try {
      await contratosApi.updateBorrador(selectedBorrador.borrador_id, borradorForm);
      toast.success('Borrador actualizado');
      setIsEditBorradorOpen(false);
      fetchData();
    } catch (err) {
      toast.error(getErrorMsg(err, 'Error al actualizar borrador'));
    } finally {
      setSaving(false);
    }
  };

  const handleAprobarBorrador = async (borrador) => {
    if (!borrador.unidad_id) {
      toast.error('Debe asignar una unidad antes de aprobar');
      handleEditBorrador(borrador);
      return;
    }
    
    setSaving(true);
    try {
      const res = await contratosApi.aprobarBorrador(borrador.borrador_id);
      toast.success(`Contrato ${res.data.numero_contrato} creado`);
      fetchData();
    } catch (err) {
      toast.error(getErrorMsg(err, 'Error al aprobar borrador'));
    } finally {
      setSaving(false);
    }
  };

  const handleDescartarBorrador = async (borrador) => {
    setSaving(true);
    try {
      await contratosApi.descartarBorrador(borrador.borrador_id);
      toast.success('Borrador descartado');
      fetchData();
    } catch (err) {
      toast.error(getErrorMsg(err, 'Error al descartar borrador'));
    } finally {
      setSaving(false);
    }
  };

  // ============ DEPOSITO HANDLERS ============
  const handleOpenDeposito = (unidad, contrato) => {
    setSelectedUnidad(unidad);
    setSelectedContrato(contrato);
    setSelectedDeposito(null);
    setDepositoForm(initialDepositoForm);
    setTenedorGarantia('administrador'); // Reset al abrir
    setGarantiaNoDisponible(contrato?.garantia_no_disponible || false);
    setIsDepositoDialogOpen(true);
  };

  const handleSaveDeposito = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Opción "No la tenemos": no crea depósito, solo marca el contrato.
      if (garantiaNoDisponible) {
        await contratosApi.update(selectedContrato.contrato_id, { garantia_no_disponible: true });
        toast.success('Garantía marcada como "No la tenemos"');
        setIsDepositoDialogOpen(false);
        fetchData();
        setSaving(false);
        return;
      }
      const data = {
        contrato_id: selectedContrato.contrato_id,
        unidad_id: selectedUnidad.unidad_id,
        tipo_deposito: depositoForm.tipo_deposito,
        monto: parseFloat(depositoForm.monto) || 0,
        moneda: depositoForm.moneda,
        fecha_cobro: depositoForm.fecha_cobro,
        medio_cobro: depositoForm.tipo_deposito === 'dinero' ? depositoForm.medio_cobro : null,
        cuenta_destino: (depositoForm.tipo_deposito === 'dinero' && (depositoForm.medio_cobro === 'transferencia' || depositoForm.medio_cobro === 'deposito')) 
          ? depositoForm.cuenta_destino : null,
      };
      
      const response = await depositosApi.create(data);
      // Si el contrato estaba marcado como "No la tenemos", limpiar el flag al cargar garantía real.
      if (selectedContrato.garantia_no_disponible) {
        try { await contratosApi.update(selectedContrato.contrato_id, { garantia_no_disponible: false }); } catch (e) { /* noop */ }
      }
      
      // Si hay documento adjunto (para pagaré o cheque), subirlo
      if (depositoForm.documento && (depositoForm.tipo_deposito === 'pagare' || depositoForm.tipo_deposito === 'cheque')) {
        try {
          await depositosApi.uploadDocumento(response.data.deposito_id, depositoForm.documento);
        } catch (uploadErr) {
          console.error('Error subiendo documento:', uploadErr);
          toast.warning('Depósito creado pero hubo un error al subir el documento');
        }
      }
      
      toast.success('Depósito registrado');
      setIsDepositoDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(getErrorMsg(err, 'Error al crear depósito'));
    } finally {
      setSaving(false);
    }
  };

  // ============ DETALLE DEPOSITO HANDLER ============
  const handleOpenDetalleDeposito = (deposito) => {
    setSelectedDeposito(deposito);
    setIsEditGarantiaMode(false); // Siempre iniciar en modo solo lectura
    // Preparar formulario de edición con datos actuales
    setEditGarantiaForm({
      tipo_deposito: deposito.tipo_deposito || 'dinero',
      monto: deposito.monto?.toString() || '',
      moneda: deposito.moneda || 'ARS',
      fecha_cobro: deposito.fecha_cobro || '',
      medio_cobro: deposito.medio_cobro || 'transferencia',
      cuenta_destino: deposito.cuenta_destino || '',
    });
    setIsDetalleDepositoOpen(true);
  };

  // Handler para guardar edición de garantía
  const handleSaveEditGarantia = async (e) => {
    e.preventDefault();
    if (!selectedDeposito) return;
    setSaving(true);
    try {
      await depositosApi.update(selectedDeposito.deposito_id, {
        tipo_deposito: editGarantiaForm.tipo_deposito,
        monto: parseFloat(editGarantiaForm.monto) || 0,
        moneda: editGarantiaForm.moneda,
        fecha_cobro: editGarantiaForm.fecha_cobro,
        medio_cobro: editGarantiaForm.tipo_deposito === 'dinero' ? editGarantiaForm.medio_cobro : null,
        cuenta_destino: (editGarantiaForm.tipo_deposito === 'dinero' && (editGarantiaForm.medio_cobro === 'transferencia' || editGarantiaForm.medio_cobro === 'deposito')) 
          ? editGarantiaForm.cuenta_destino : null,
      });
      toast.success('Garantía actualizada');
      setIsEditGarantiaMode(false);
      setIsDetalleDepositoOpen(false);
      fetchData();
    } catch (err) {
      toast.error(getErrorMsg(err, 'Error al actualizar garantía'));
    } finally {
      setSaving(false);
    }
  };

  // ============ DEVOLUCION HANDLERS ============
  const handleOpenDevolucion = (deposito) => {
    setSelectedDeposito(deposito);
    setDevolucionForm({ 
      monto: deposito.monto.toString(), 
      fecha: new Date().toISOString().split('T')[0],
      comentario: ''
    });
    setIsDevolucionDialogOpen(true);
  };

  const handleSaveDevolucion = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await depositosApi.update(selectedDeposito.deposito_id, {
        estado: 'devuelto',
        monto_devuelto: parseFloat(devolucionForm.monto) || 0,
        fecha_devolucion: devolucionForm.fecha,
        comentario_devolucion: devolucionForm.comentario,
      });
      toast.success('Depósito devuelto');
      setIsDevolucionDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(getErrorMsg(err, 'Error al registrar devolución'));
    } finally {
      setSaving(false);
    }
  };

  // ============ DELETE DEPOSITO HANDLER ============
  const handleDeleteDeposito = async () => {
    if (!selectedDeposito) return;
    setSaving(true);
    try {
      await depositosApi.delete(selectedDeposito.deposito_id);
      toast.success('Depósito eliminado');
      setIsDeleteDepositoOpen(false);
      setSelectedDeposito(null);
      fetchData();
    } catch (err) {
      toast.error(getErrorMsg(err, 'Error al eliminar depósito'));
    } finally {
      setSaving(false);
    }
  };

  // ============ DELETE CONTRATO HANDLER ============
  const handleDeleteContrato = async () => {
    if (!selectedContrato) return;
    setSaving(true);
    try {
      await contratosApi.delete(selectedContrato.contrato_id);
      toast.success('Contrato eliminado');
      setIsDeleteContratoOpen(false);
      setSelectedContrato(null);
      fetchData();
      onReloadUnidades && onReloadUnidades();
    } catch (err) {
      toast.error(getErrorMsg(err, 'Error al eliminar contrato'));
    } finally {
      setSaving(false);
    }
  };

  // ============ EDIT CONTRATO HANDLERS ============
  const handleOpenEditContrato = (contrato) => {
    setIsEditMode(false); // Siempre iniciar en modo solo lectura
    setEditContratoForm({
      contrato_id: contrato.contrato_id,
      inquilino: contrato.inquilino || '',
      nombre_comercial: contrato.nombre_comercial || '',
      razon_social: contrato.razon_social || '',
      descripcion_empresa: contrato.descripcion_empresa || '',
      // Tipo de inquilino + firmante (default a 'juridica' para compat con contratos legacy)
      tipo_inquilino: contrato.tipo_inquilino || 'juridica',
      firmante_nombre: contrato.firmante_nombre || '',
      firmante_email: contrato.firmante_email || '',
      firmante_whatsapp: contrato.firmante_whatsapp || '',
      contacto_comercial_mismo_firmante: contrato.contacto_comercial_mismo_firmante ?? true,
      contacto_admin_mismo_firmante: contrato.contacto_admin_mismo_firmante ?? true,
      contacto_admin_mismo_comercial: contrato.contacto_admin_mismo_comercial ?? true,
      email_facturacion_default: contrato.email_facturacion_default ?? true,
      // Alta acceso a plataforma
      acceso_nombre: contrato.acceso_nombre || '',
      acceso_email: contrato.acceso_email || '',
      acceso_mismo_firmante: contrato.acceso_mismo_firmante ?? true,
      acceso_mismo_admin: contrato.acceso_mismo_admin ?? true,
      fecha_inicio: contrato.fecha_inicio || '',
      fecha_fin: contrato.fecha_fin || '',
      monto_inicial: contrato.monto_inicial?.toString() || '',
      monto_actual: contrato.monto_actual?.toString() || '',
      fecha_carga_monto: contrato.fecha_carga_monto || '',
      moneda: contrato.moneda || 'ARS',
      indice_tipo: contrato.indice_tipo || 'ipc',
      indice_frecuencia: contrato.indice_frecuencia || 'trimestral',
      indice_valor: contrato.indice_valor?.toString() || '',
      indice_condiciones: contrato.indice_condiciones || '',
      // Multi-índice + Valor referencia USD (Jun 2026) — backward compat: si no hay,
      // hydratamos con [{tipo: indice_tipo.toUpperCase(), peso: 100}].
      ajuste_indices: (contrato.ajuste_indices && contrato.ajuste_indices.length > 0)
        ? contrato.ajuste_indices.map(i => ({ tipo: (i.tipo || 'IPC').toUpperCase(), peso: i.peso ?? 100 }))
        : [{ tipo: (contrato.indice_tipo || 'IPC').toUpperCase(), peso: 100 }],
      ajuste_frecuencia: contrato.ajuste_frecuencia || contrato.indice_frecuencia || 'trimestral',
      valor_referencia: contrato.valor_referencia || null,
      fecha_inicio_gestion: contrato.fecha_inicio_gestion || '',
      monto_alquiler_gestion: contrato.monto_alquiler_gestion?.toString() || '',
      contacto_comercial_nombre: contrato.contacto_comercial_nombre || '',
      contacto_comercial_email: contrato.contacto_comercial_email || '',
      contacto_comercial_telefono: contrato.contacto_comercial_telefono || '',
      contacto_admin_nombre: contrato.contacto_admin_nombre || '',
      contacto_admin_email: contrato.contacto_admin_email || '',
      contacto_admin_telefono: contrato.contacto_admin_telefono || '',
      cuit: contrato.cuit || '',
      domicilio_fiscal: contrato.domicilio_fiscal || '',
      condicion_iva: contrato.condicion_iva || '',
      email_facturacion: contrato.email_facturacion || '',
      emails_facturacion: (contrato.emails_facturacion && contrato.emails_facturacion.length) ? contrato.emails_facturacion : (contrato.email_facturacion ? [contrato.email_facturacion] : ['']),
      orden_compra: contrato.orden_compra || '',
      orden_compra_tipo: contrato.orden_compra_tipo || '',
      retencion_tipo: contrato.retencion_tipo || 'sin_retenciones',
      retencion_porcentaje: contrato.retencion_porcentaje ?? null,
      // Plataforma de proveedores + facturación contacto + emergencia (Fase 3)
      proveedor_plataforma_nombre: contrato.proveedor_plataforma_nombre || '',
      proveedor_plataforma_url: contrato.proveedor_plataforma_url || '',
      proveedor_plataforma_usuario: contrato.proveedor_plataforma_usuario || '',
      proveedor_plataforma_password: contrato.proveedor_plataforma_password || '',
      facturacion_mismo_admin: contrato.facturacion_mismo_admin ?? true,
      facturacion_contacto_nombre: contrato.facturacion_contacto_nombre || '',
      facturacion_contacto_telefono: contrato.facturacion_contacto_telefono || '',
      facturacion_contacto_email: contrato.facturacion_contacto_email || '',
      facturacion_metodo: contrato.facturacion_metodo || (contrato.proveedor_plataforma_url || contrato.proveedor_plataforma_usuario ? 'ambos' : 'email'),
      facturacion_contacto_apellido: contrato.facturacion_contacto_apellido || '',
      proveedor_plataforma_instrucciones: contrato.proveedor_plataforma_instrucciones || '',
      orden_compra_instruccion: contrato.orden_compra_instruccion || '',
      emergencia_nombre: contrato.emergencia_nombre || '',
      emergencia_telefono: contrato.emergencia_telefono || '',
      emergencia_email: contrato.emergencia_email || '',
      dia_emision: contrato.dia_emision?.toString() || '',
      dia_vencimiento: contrato.dia_vencimiento?.toString() || '',
      penalidad_enabled: contrato.penalidad_enabled || false,
      penalidad_dias_gracia: contrato.penalidad_dias_gracia?.toString() || '10',
      penalidad_porcentaje_1: contrato.penalidad_porcentaje_1?.toString() || '10',
      penalidad_porcentaje_2: contrato.penalidad_porcentaje_2?.toString() || '20',
      // Colppy
      colppy_nro_factura: contrato.colppy_nro_factura || '004',
      colppy_factura_electronica: contrato.colppy_factura_electronica !== false,
      colppy_fce: !!contrato.colppy_fce,
      colppy_condicion_venta: contrato.colppy_condicion_venta || 'cuenta_corriente',
      colppy_tipo_comprobante: contrato.colppy_tipo_comprobante || '',
      colppy_tipo_factura: contrato.colppy_tipo_factura || '',
      colppy_descripcion: contrato.colppy_descripcion || '',
      colppy_tipo_operacion: contrato.colppy_tipo_operacion || 'servicios',
      colppy_cuenta: contrato.colppy_cuenta || '',
      colppy_moneda: contrato.colppy_moneda || 'ARS',
      colppy_percepciones: contrato.colppy_percepciones || false,
      // Cochera / Multi-unidad
      incluye_cochera: !!(contrato.unidades_contrato?.length > 1 || contrato.incluye_cochera),
      cocheras_incluidas: contrato.unidades_contrato
        ? contrato.unidades_contrato.filter(uc => uc.tipo_cochera).map(uc => uc.unidad_id)
        : (contrato.cocheras_incluidas || []),
      cocheras_config: contrato.unidades_contrato
        ? contrato.unidades_contrato
            .filter(uc => uc.tipo_cochera)
            .reduce((acc, uc) => ({ ...acc, [uc.unidad_id]: { tipo_cochera: uc.tipo_cochera, monto: uc.monto } }), {})
        : {},
      unidades_contrato: contrato.unidades_contrato || null,
      // Valoración por cochera (unidad_id -> {modo, valor_base})
      cocheras_valuacion: contrato.cocheras_valuacion || {},
      // Compat backend: se conservan campos legacy si existen, pero NO se exponen en la UI.
      adendas: Array.isArray(contrato.adendas) ? contrato.adendas : [],
      archivo_contrato_id: contrato.archivo_contrato_id || null,
      archivo_contrato_nombre: contrato.archivo_contrato_nombre || null,
    });
    setIsEditContratoOpen(true);
  };

  const handleSaveEditContrato = async (e) => {
    e.preventDefault();
    if (!editContratoForm) return;
    // Multi-índice: validar suma de pesos = 100 si hay ajuste_indices definido
    if (editContratoForm.ajuste_indices && editContratoForm.ajuste_indices.length > 0) {
      const suma = editContratoForm.ajuste_indices.reduce((s, i) => s + (parseFloat(i.peso) || 0), 0);
      if (Math.abs(suma - 100) > 0.01) {
        toast.error(`La suma de pesos de los índices debe ser 100% (actual: ${suma.toFixed(2)}%)`);
        return;
      }
    }
    // Emails de facturación: validar formato/duplicados y exigir al menos uno si el método es email/ambos
    {
      const _metodo = editContratoForm.facturacion_metodo || 'email';
      const _list = (editContratoForm.emails_facturacion && editContratoForm.emails_facturacion.length)
        ? editContratoForm.emails_facturacion
        : (editContratoForm.email_facturacion ? [editContratoForm.email_facturacion] : []);
      if (emailsFacturacionTienenError(_list)) {
        toast.error('Revisá los emails de facturación: hay formato inválido o duplicados.');
        return;
      }
      if ((_metodo === 'email' || _metodo === 'ambos') && cleanEmailsFacturacion(_list).length === 0) {
        toast.error('Agregá al menos un email de facturación (el método de envío es Email/Ambas).');
        return;
      }
    }
    setSaving(true);
    try {
      const payload = {
        inquilino: editContratoForm.nombre_comercial || editContratoForm.razon_social || undefined,
        nombre_comercial: editContratoForm.nombre_comercial || undefined,
        razon_social: editContratoForm.razon_social || undefined,
        descripcion_empresa: editContratoForm.descripcion_empresa || undefined,
        fecha_inicio: editContratoForm.fecha_inicio || undefined,
        fecha_fin: editContratoForm.fecha_fin || undefined,
        monto_inicial: editContratoForm.monto_inicial ? parseFloat(String(editContratoForm.monto_inicial).replace(/[^\d.-]/g, '')) : undefined,
        monto_actual: editContratoForm.monto_actual ? parseFloat(String(editContratoForm.monto_actual).replace(/[^\d.-]/g, '')) : undefined,
        fecha_carga_monto: editContratoForm.fecha_carga_monto || undefined,
        moneda: editContratoForm.moneda,
        // Valoración por cochera (unidad_id -> {modo, valor_base})
        cocheras_valuacion: editContratoForm.cocheras_valuacion || {},
        // Unidades y cocheras del contrato (Fase 4: edición de unidades)
        unidades_contrato: editContratoForm.unidades_contrato && editContratoForm.unidades_contrato.length > 0
          ? editContratoForm.unidades_contrato.map(u => ({ unidad_id: u.unidad_id, tipo: u.tipo, monto: u.monto || 0, tipo_cochera: u.tipo_cochera || null }))
          : undefined,
        unidad_id: editContratoForm.unidad_id || undefined,
        unidad_principal_comercial_id: editContratoForm.unidad_principal_comercial_id
          || (editContratoForm.unidades_contrato || []).find(u => u.tipo !== 'cochera')?.unidad_id
          || editContratoForm.unidad_id || undefined,
        indice_tipo: editContratoForm.indice_tipo,
        indice_frecuencia: editContratoForm.indice_frecuencia,
        indice_valor: editContratoForm.indice_valor ? parseFloat(editContratoForm.indice_valor) : undefined,
        indice_condiciones: editContratoForm.indice_condiciones || undefined,
        // Multi-índice + Valor referencia USD (Jun 2026)
        ajuste_indices: editContratoForm.ajuste_indices && editContratoForm.ajuste_indices.length > 0
          ? editContratoForm.ajuste_indices.map(i => ({ tipo: (i.tipo || 'IPC').toUpperCase(), peso: parseFloat(i.peso) || 0 }))
          : undefined,
        ajuste_frecuencia: editContratoForm.ajuste_frecuencia || editContratoForm.indice_frecuencia || undefined,
        valor_referencia: editContratoForm.valor_referencia && (editContratoForm.valor_referencia.monto != null)
          ? { monto: parseFloat(editContratoForm.valor_referencia.monto) || 0, moneda: editContratoForm.valor_referencia.moneda || 'USD' }
          : undefined,
        fecha_inicio_gestion: editContratoForm.fecha_inicio_gestion || undefined,
        monto_alquiler_gestion: editContratoForm.monto_alquiler_gestion ? parseFloat(String(editContratoForm.monto_alquiler_gestion).replace(/[^\d.-]/g, '')) : undefined,
        contacto_comercial_nombre: editContratoForm.contacto_comercial_nombre || undefined,
        contacto_comercial_email: editContratoForm.contacto_comercial_email || undefined,
        contacto_comercial_telefono: editContratoForm.contacto_comercial_telefono || undefined,
        contacto_admin_nombre: editContratoForm.contacto_admin_nombre || undefined,
        contacto_admin_email: editContratoForm.contacto_admin_email || undefined,
        contacto_admin_telefono: editContratoForm.contacto_admin_telefono || undefined,
        // Tipo de inquilino + firmante (persona humana)
        tipo_inquilino: editContratoForm.tipo_inquilino || undefined,
        firmante_nombre: editContratoForm.firmante_nombre || undefined,
        firmante_email: editContratoForm.firmante_email || undefined,
        firmante_whatsapp: editContratoForm.firmante_whatsapp || undefined,
        // Flags "mismo que..."
        contacto_comercial_mismo_firmante: editContratoForm.contacto_comercial_mismo_firmante ?? undefined,
        contacto_admin_mismo_firmante: editContratoForm.contacto_admin_mismo_firmante ?? undefined,
        contacto_admin_mismo_comercial: editContratoForm.contacto_admin_mismo_comercial ?? undefined,
        email_facturacion_default: editContratoForm.email_facturacion_default ?? undefined,
        // Alta de acceso a plataforma
        acceso_nombre: editContratoForm.acceso_nombre || undefined,
        acceso_email: editContratoForm.acceso_email || undefined,
        acceso_mismo_firmante: editContratoForm.acceso_mismo_firmante ?? undefined,
        acceso_mismo_admin: editContratoForm.acceso_mismo_admin ?? undefined,
        cuit: editContratoForm.cuit || undefined,
        domicilio_fiscal: editContratoForm.domicilio_fiscal || undefined,
        condicion_iva: editContratoForm.condicion_iva || undefined,
        email_facturacion: (() => {
          const _l = (editContratoForm.emails_facturacion && editContratoForm.emails_facturacion.length) ? editContratoForm.emails_facturacion : (editContratoForm.email_facturacion ? [editContratoForm.email_facturacion] : []);
          return cleanEmailsFacturacion(_l)[0] || null;
        })(),
        emails_facturacion: cleanEmailsFacturacion((editContratoForm.emails_facturacion && editContratoForm.emails_facturacion.length) ? editContratoForm.emails_facturacion : (editContratoForm.email_facturacion ? [editContratoForm.email_facturacion] : [])),
        orden_compra: editContratoForm.orden_compra || undefined,
        orden_compra_tipo: editContratoForm.orden_compra_tipo || undefined,
        retencion_tipo: editContratoForm.retencion_tipo || 'sin_retenciones',
        retencion_porcentaje: editContratoForm.retencion_tipo === 'otro' ? (editContratoForm.retencion_porcentaje ?? null) : null,
        // Plataforma de proveedores + facturación contacto + emergencia (Fase 3)
        proveedor_plataforma_nombre: editContratoForm.proveedor_plataforma_nombre || undefined,
        proveedor_plataforma_url: editContratoForm.proveedor_plataforma_url || undefined,
        proveedor_plataforma_usuario: editContratoForm.proveedor_plataforma_usuario || undefined,
        proveedor_plataforma_password: editContratoForm.proveedor_plataforma_password || undefined,
        proveedor_plataforma_instrucciones: editContratoForm.proveedor_plataforma_instrucciones || undefined,
        facturacion_mismo_admin: editContratoForm.facturacion_mismo_admin ?? undefined,
        facturacion_metodo: editContratoForm.facturacion_metodo || undefined,
        orden_compra_instruccion: editContratoForm.orden_compra_instruccion || undefined,
        facturacion_contacto_nombre: (editContratoForm.facturacion_mismo_admin !== false ? editContratoForm.contacto_admin_nombre : editContratoForm.facturacion_contacto_nombre) || undefined,
        facturacion_contacto_apellido: (editContratoForm.facturacion_mismo_admin !== false ? '' : editContratoForm.facturacion_contacto_apellido) || undefined,
        facturacion_contacto_telefono: (editContratoForm.facturacion_mismo_admin !== false ? editContratoForm.contacto_admin_telefono : editContratoForm.facturacion_contacto_telefono) || undefined,
        facturacion_contacto_email: (editContratoForm.facturacion_mismo_admin !== false ? editContratoForm.contacto_admin_email : editContratoForm.facturacion_contacto_email) || undefined,
        emergencia_nombre: editContratoForm.emergencia_nombre || undefined,
        emergencia_telefono: editContratoForm.emergencia_telefono || undefined,
        emergencia_email: editContratoForm.emergencia_email || undefined,
        dia_emision: editContratoForm.dia_emision ? parseInt(editContratoForm.dia_emision) : undefined,
        dia_vencimiento: editContratoForm.dia_vencimiento ? parseInt(editContratoForm.dia_vencimiento) : undefined,
        penalidad_enabled: editContratoForm.penalidad_enabled,
        penalidad_dias_gracia: editContratoForm.penalidad_dias_gracia ? parseInt(editContratoForm.penalidad_dias_gracia) : undefined,
        penalidad_porcentaje_1: editContratoForm.penalidad_porcentaje_1 ? parseFloat(editContratoForm.penalidad_porcentaje_1) : undefined,
        penalidad_porcentaje_2: editContratoForm.penalidad_porcentaje_2 ? parseFloat(editContratoForm.penalidad_porcentaje_2) : undefined,
        // Colppy
        colppy_nro_factura: editContratoForm.colppy_nro_factura || '004',
        colppy_factura_electronica: editContratoForm.colppy_factura_electronica,
        colppy_fce: !!editContratoForm.colppy_fce,
        colppy_condicion_venta: editContratoForm.colppy_condicion_venta || undefined,
        colppy_tipo_comprobante: editContratoForm.colppy_tipo_comprobante || undefined,
        colppy_tipo_factura: editContratoForm.colppy_tipo_factura || undefined,
        colppy_descripcion: editContratoForm.colppy_descripcion || undefined,
        colppy_tipo_operacion: editContratoForm.colppy_tipo_operacion || undefined,
        colppy_cuenta: editContratoForm.colppy_cuenta || undefined,
        colppy_moneda: editContratoForm.colppy_moneda || undefined,
        colppy_percepciones: editContratoForm.colppy_percepciones,
      };

      // NOTA: la solapa "Unidades" (UnidadesEditSection) es la única fuente de verdad
      // para las cocheras asociadas. `payload.unidades_contrato` ya se arma arriba desde
      // editContratoForm.unidades_contrato. NO reconstruir desde cocheras_incluidas/config
      // (campos legacy que no se actualizan al editar cocheras y pisaban la lista correcta).

      // Remove undefined keys
      Object.keys(payload).forEach(key => payload[key] === undefined && delete payload[key]);

      await contratosApi.update(editContratoForm.contrato_id, payload);
      toast.success('Contrato actualizado');
      setIsEditContratoOpen(false);
      setEditContratoForm(null);
      fetchData();
    } catch (err) {
      toast.error(getErrorMsg(err, 'Error al actualizar contrato'));
    } finally {
      setSaving(false);
    }
  };

  // Filter only units that are in administration and rented
  const unidadesAlquiladas = unidades.filter(u => 
    u.en_administracion && u.estado_alquiler === 'alquilada'
  ).sort(compareUnidades);

  // Función para exportar contratos a Excel
  // Regla: exporta SOLO contratos `vigente` por defecto.
  // Los `finalizado` se ven en la sub-tab Históricos, no en este Excel.
  // ─────────────────────────────────────────────────────────────────
  // CONTRATO_EXPORT_COLUMNS
  // Mapa centralizado de columnas para el Excel de contratos.
  // ▶ Mantener sincronizado con los campos del modal Nuevo/Editar Contrato
  //   (tabs: Datos · Condiciones · Fiscal · Veraz · Colppy · Documentos).
  // ▶ Cada entry: { header, value: (ctx) => any, width? }
  //   ctx = { contrato, unidad, edificio, deposito }
  // ─────────────────────────────────────────────────────────────────
  const CONTRATO_EXPORT_COLUMNS = [
    // === Identificación ===
    { header: 'Edificio',          width: 18, value: ({ edificio })       => edificio?.nombre || '' },
    { header: 'Unidad',            width: 12, value: ({ unidad, contrato })=> unidad?.nombre_unidad || contrato.unidad_id || '' },
    { header: 'Tipo de unidad',    width: 12, value: ({ unidad })         => (unidad?.tipo || '').replace(/^./, c => c.toUpperCase()) },
    { header: 'Propietario',       width: 22, value: ({ unidad })         => (unidad?.propietarios?.map(p => p.propietario_nombre).filter(Boolean).join(' / ')) || '' },
    { header: 'Tipo inquilino',    width: 12, value: ({ contrato })       => contrato.tipo_inquilino === 'humana' ? 'Persona Humana' : (contrato.tipo_inquilino === 'juridica' ? 'Persona Jurídica' : '') },
    { header: 'Inquilino',         width: 25, value: ({ contrato })       => contrato.inquilino || '' },
    { header: 'Razón social',      width: 25, value: ({ contrato })       => contrato.razon_social || '' },
    { header: 'Nombre comercial',  width: 20, value: ({ contrato })       => contrato.nombre_comercial || '' },
    { header: 'Descripción empresa', width: 28, value: ({ contrato })     => contrato.descripcion_empresa || '' },
    { header: 'Firmante',          width: 22, value: ({ contrato })       => contrato.firmante_nombre || '' },
    { header: 'Email firmante',    width: 24, value: ({ contrato })       => contrato.firmante_email || '' },
    { header: 'WhatsApp firmante', width: 16, value: ({ contrato })       => contrato.firmante_whatsapp || '' },
    // === Contactos ===
    { header: 'Contacto comercial', width: 22, value: ({ contrato })      => contrato.contacto_comercial_nombre || '' },
    { header: 'Email comercial',    width: 24, value: ({ contrato })      => contrato.contacto_comercial_email || '' },
    { header: 'Tel. comercial',     width: 16, value: ({ contrato })      => contrato.contacto_comercial_telefono || '' },
    { header: 'Contacto admin',     width: 22, value: ({ contrato })      => contrato.contacto_admin_nombre || '' },
    { header: 'Email admin',        width: 24, value: ({ contrato })      => contrato.contacto_admin_email || '' },
    { header: 'Tel. admin',         width: 16, value: ({ contrato })      => contrato.contacto_admin_telefono || '' },
    // === Fechas ===
    { header: 'Fecha inicio',      width: 12, value: ({ contrato })       => formatDate(contrato.fecha_inicio) },
    { header: 'Fecha fin',         width: 12, value: ({ contrato })       => formatDate(contrato.fecha_fin) },
    { header: 'Fecha carga monto', width: 14, value: ({ contrato })       => formatDate(contrato.fecha_carga_monto) },
    { header: 'Inicio gestión',    width: 12, value: ({ contrato })       => formatDate(contrato.fecha_inicio_gestion) },
    { header: 'Fecha creación',    width: 12, value: ({ contrato })       => formatDate(contrato.created_at) },
    // === Estado ===
    { header: 'Estado',            width: 11, value: ({ contrato })       => contrato.estado || '' },
    // === Condiciones económicas ===
    { header: 'Moneda',            width: 8,  value: ({ contrato })       => contrato.moneda || '' },
    { header: 'Monto inicial',     width: 14, value: ({ contrato })       => Number(contrato.monto_inicial) || 0 },
    { header: 'Monto actual',      width: 14, value: ({ contrato })       => Number(contrato.monto_actual) || 0 },
    { header: 'Monto gestión',     width: 14, value: ({ contrato })       => Number(contrato.monto_alquiler_gestion) || 0 },
    { header: 'Día emisión',       width: 10, value: ({ contrato })       => contrato.dia_emision ?? '' },
    { header: 'Día vencimiento',   width: 12, value: ({ contrato })       => contrato.dia_vencimiento ?? '' },
    // === Ajuste ===
    { header: 'Tipo índice',       width: 12, value: ({ contrato })       => contrato.indice_tipo || '' },
    { header: 'Frecuencia índice', width: 13, value: ({ contrato })       => contrato.indice_frecuencia || '' },
    { header: 'Valor índice (%)',  width: 13, value: ({ contrato })       => contrato.indice_valor ?? '' },
    { header: 'Condiciones índice', width: 22, value: ({ contrato })      => contrato.indice_condiciones || '' },
    // Multi-índice + Valor referencia USD (Jun 2026)
    { header: 'Índices ajuste',    width: 22, value: ({ contrato })       => Array.isArray(contrato.ajuste_indices) && contrato.ajuste_indices.length
                                                                              ? contrato.ajuste_indices.map(i => i.tipo).join(' / ')
                                                                              : (contrato.indice_tipo ? contrato.indice_tipo.toUpperCase() : '') },
    { header: 'Pesos ajuste',      width: 18, value: ({ contrato })       => Array.isArray(contrato.ajuste_indices) && contrato.ajuste_indices.length
                                                                              ? contrato.ajuste_indices.map(i => `${i.peso}%`).join(' / ')
                                                                              : (contrato.indice_tipo ? '100%' : '') },
    { header: 'Frecuencia ajuste', width: 14, value: ({ contrato })       => contrato.ajuste_frecuencia || contrato.indice_frecuencia || '' },
    { header: 'Valor referencia USD', width: 16, value: ({ contrato })    => (contrato.valor_referencia && contrato.valor_referencia.monto != null) ? Number(contrato.valor_referencia.monto) : '' },
    // === Penalidades ===
    { header: 'Penalidad activa',  width: 12, value: ({ contrato })       => contrato.penalidad_enabled ? 'Sí' : 'No' },
    { header: 'Días gracia',       width: 10, value: ({ contrato })       => contrato.penalidad_dias_gracia ?? '' },
    { header: 'Penalidad % 1',     width: 12, value: ({ contrato })       => contrato.penalidad_porcentaje_1 ?? '' },
    { header: 'Penalidad % 2',     width: 12, value: ({ contrato })       => contrato.penalidad_porcentaje_2 ?? '' },
    // === Depósito ===
    { header: 'Depósito monto',    width: 14, value: ({ deposito })       => Number(deposito?.monto) || 0 },
    { header: 'Depósito moneda',   width: 10, value: ({ deposito })       => deposito?.moneda || '' },
    { header: 'Tipo depósito',     width: 12, value: ({ deposito })       => deposito?.tipo_deposito || '' },
    // === Fiscal ===
    { header: 'CUIT',              width: 14, value: ({ contrato })       => contrato.cuit || '' },
    { header: 'Condición IVA',     width: 18, value: ({ contrato })       => contrato.condicion_iva || '' },
    { header: 'Domicilio fiscal',  width: 24, value: ({ contrato })       => contrato.domicilio_fiscal || '' },
    { header: 'Email facturación', width: 24, value: ({ contrato })       => contrato.email_facturacion || '' },
    { header: 'Orden de compra',   width: 14, value: ({ contrato })       => contrato.orden_compra || '' },
    { header: 'Tipo OC',           width: 10, value: ({ contrato })       => contrato.orden_compra_tipo || '' },
    { header: 'Tipo retención',    width: 14, value: ({ contrato })       => contrato.retencion_tipo || '' },
    { header: '% Retención',       width: 12, value: ({ contrato })       => contrato.retencion_porcentaje ?? '' },
    // === Colppy ===
    { header: 'Colppy Nº factura',   width: 14, value: ({ contrato })     => contrato.colppy_nro_factura || '' },
    { header: 'Colppy fact. elec.',  width: 14, value: ({ contrato })     => contrato.colppy_factura_electronica ? 'Sí' : 'No' },
    { header: 'Colppy cond. venta',  width: 16, value: ({ contrato })     => contrato.colppy_condicion_venta || '' },
    { header: 'Colppy tipo comprob.', width: 16, value: ({ contrato })    => contrato.colppy_tipo_comprobante || '' },
    { header: 'Colppy tipo factura',  width: 14, value: ({ contrato })    => contrato.colppy_tipo_factura || '' },
    { header: 'Colppy tipo operación', width: 16, value: ({ contrato })   => contrato.colppy_tipo_operacion || '' },
    { header: 'Colppy cuenta',       width: 14, value: ({ contrato })     => contrato.colppy_cuenta || '' },
    { header: 'Colppy moneda',       width: 12, value: ({ contrato })     => contrato.colppy_moneda || '' },
    { header: 'Colppy percepciones', width: 14, value: ({ contrato })     => contrato.colppy_percepciones ? 'Sí' : 'No' },
    { header: 'Colppy descripción',  width: 26, value: ({ contrato })     => contrato.colppy_descripcion || '' },
    // === Documentación ===
    { header: 'Documentos', width: 14, value: ({ contrato }) => {
        const n = (contrato.etapas_contractuales || []).reduce((s, e) => s + ((e.archivos || []).length), 0);
        return n > 0 ? `${n} documento${n === 1 ? '' : 's'}` : 'Sin documentos';
      } },
  ];

  const handleExportExcel = () => {
    try {
      const contratosVigentes = contratos.filter(c => c.estado === 'vigente');
      // Preparar datos para el Excel a partir del mapa centralizado
      const dataToExport = contratosVigentes.map(contrato => {
        const ctx = {
          contrato,
          unidad: unidades.find(u => u.unidad_id === contrato.unidad_id),
          edificio,
          deposito: depositos.find(d => d.contrato_id === contrato.contrato_id && d.estado === 'vigente'),
        };
        const row = {};
        for (const col of CONTRATO_EXPORT_COLUMNS) {
          try { row[col.header] = col.value(ctx); } catch (_) { row[col.header] = ''; }
        }
        return row;
      });

      // Crear workbook y worksheet. Si no hay contratos vigentes, sheet con SOLO encabezados.
      const headers = CONTRATO_EXPORT_COLUMNS.map(c => c.header);
      const wb = XLSX.utils.book_new();
      const ws = dataToExport.length > 0
        ? XLSX.utils.json_to_sheet(dataToExport, { header: headers })
        : XLSX.utils.aoa_to_sheet([headers]);

      // Ajustar ancho de columnas según el mapa
      ws['!cols'] = CONTRATO_EXPORT_COLUMNS.map(c => ({ wch: c.width || 14 }));

      // Agregar worksheet al workbook
      XLSX.utils.book_append_sheet(wb, ws, 'Contratos');

      // Generar nombre del archivo con fecha
      const fecha = new Date().toLocaleDateString('es-AR').replace(/\//g, '-');
      const fileName = `Contratos_${fecha}.xlsx`;

      // Descargar archivo
      XLSX.writeFile(wb, fileName);
      
      toast.success(`Excel exportado: ${contratosVigentes.length} contrato${contratosVigentes.length !== 1 ? 's' : ''} vigente${contratosVigentes.length !== 1 ? 's' : ''}`);
    } catch (error) {
      console.error('Error al exportar Excel:', error);
      toast.error('Error al exportar Excel');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  // Títulos según el modo (usado solo para modo 'all')
  const titles = {
    all: 'Contratos y Depósitos',
    contratos: '',
    depositos: ''
  };

  // Obtener contratos vigentes SOLO de las unidades pasadas como prop (administradas)
  const unidadIds = new Set(unidades.map(u => u.unidad_id));
  const contratosVigentes = contratos.filter(c => c.estado === 'vigente' && unidadIds.has(c.unidad_id));
  // KPIs y semáforo de vencimiento — misma base de días para que coincidan.
  // dias<0: vencido (rojo) | 0-30: vencen30 (rojo) | 31-89: renovar90 (amarillo) | >=90: verde
  const kpiVencimientos = (() => {
    const hoy = new Date();
    let vencidos = 0, vencen30 = 0, renovar90 = 0;
    contratosVigentes.forEach(c => {
      if (!c.fecha_fin) return;
      const dias = Math.ceil((new Date(c.fecha_fin) - hoy) / 86400000);
      if (dias < 0) vencidos++;
      else if (dias <= 30) vencen30++;
      else if (dias <= 89) renovar90++;
    });
    return { vencidos, vencen30, renovar90 };
  })();
  const contratosSinDeposito = contratosVigentes.filter(c => 
    !depositos.some(d => d.contrato_id === c.contrato_id && ['vigente', 'parcialmente_aplicado'].includes(d.estado))
  );
  
  // Unidades en administración (para crear nuevo contrato)
  const unidadesEnAdmin = unidades.filter(u => u.en_administracion).sort(compareUnidades);
  
  // Unidades en admin sin contrato vigente (para pre-selección)
  const unidadesSinContrato = unidadesEnAdmin.filter(u => 
    !contratosVigentes.some(c => c.unidad_id === u.unidad_id)
  );

  // Handler para abrir modal de nuevo depósito desde tab depositos
  const handleOpenNuevoDeposito = () => {
    if (contratosVigentes.length === 0) {
      toast.error('No hay contratos vigentes para asociar un depósito');
      return;
    }
    // Pre-seleccionar el primer contrato vigente
    const primerContrato = contratosVigentes[0];
    const unidad = unidades.find(u => u.unidad_id === primerContrato.unidad_id);
    setSelectedContrato(primerContrato);
    setSelectedUnidad(unidad);
    setDepositoForm(initialDepositoForm);
    setIsDepositoDialogOpen(true);
  };
  
  // Handler para abrir modal de nuevo contrato desde tab contratos
  const handleOpenNuevoContrato = () => {
    if (unidadesSinContrato.length === 0) {
      toast.error('Todas las unidades en administración ya tienen contrato vigente');
      return;
    }
    // Pre-seleccionar la primera unidad sin contrato
    const primeraUnidad = unidadesSinContrato[0];
    setSelectedUnidad(primeraUnidad);
    setSelectedContrato(null);
    setContratoForm(initialContratoForm);
    setIsContratoDialogOpen(true);
  };

  return (
    <div className="space-y-4 h-full flex flex-col min-h-0 overflow-y-auto">
      {/* Header - solo mostrar si hay título (modo 'all') */}
      {titles[mode] && (
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">{titles[mode]}</h2>
          </div>
        </div>
      )}

      {/* Stats - Compacto para modo depositos (con botón integrado) */}
      {mode === 'depositos' && (
        <div className="flex items-center justify-between py-2 px-4 bg-slate-50 rounded-lg text-sm">
          <div className="flex items-center gap-6">
          <div className="flex items-center gap-2">
            <span className="text-slate-500">Oficinas en Admin:</span>
            <span className="font-bold text-slate-900">{unidadesAlquiladas.length}</span>
          </div>
          <div className="h-4 w-px bg-slate-300"></div>
          <div className="flex items-center gap-2">
            <span className="text-slate-500">Depósitos Vigentes:</span>
            <span className="font-bold text-slate-900">
              {depositos.filter(d => d.estado === 'vigente').length}
            </span>
          </div>
          <div className="h-4 w-px bg-slate-300"></div>
          <div className="flex items-center gap-2">
            <span className="text-slate-500">Pasivo ARS:</span>
            <span className="font-bold text-green-600">
              {formatCurrency(
                depositos
                  .filter(d => d.estado === 'vigente' && d.moneda === 'ARS' && d.tipo_deposito === 'dinero')
                  .reduce((acc, d) => acc + (d.monto || 0), 0),
                'ARS'
              )}
            </span>
          </div>
          <div className="h-4 w-px bg-slate-300"></div>
          <div className="flex items-center gap-2">
            <span className="text-slate-500">Pasivo USD:</span>
            <span className="font-bold text-blue-600">
              {formatCurrency(
                depositos
                  .filter(d => d.estado === 'vigente' && d.moneda === 'USD' && d.tipo_deposito === 'dinero')
                  .reduce((acc, d) => acc + (d.monto || 0), 0),
                'USD'
              )}
            </span>
          </div>
          </div>
          {contratosVigentes.length > 0 && (
            <Button onClick={handleOpenNuevoDeposito} data-testid="nuevo-deposito-btn" size="sm">
              <Plus className="w-4 h-4 mr-1" />
              Nuevo Depósito
            </Button>
          )}
        </div>
      )}

      {/* Stats - Compacto para modo contratos (con botón integrado). Visible para analista, admin y comercial. */}
      {mode === 'contratos' && (
        <div className="flex items-center justify-between gap-3 py-2 px-4 bg-slate-50 rounded-lg text-sm">
          <div className="flex items-center gap-3 flex-nowrap overflow-x-auto min-w-0">
            <div className="flex items-center gap-1.5 whitespace-nowrap">
              <span className="text-slate-500">Vigentes:</span>
              <span className="font-bold text-slate-900">
                {contratosVigentes.length}
              </span>
            </div>
            <div className="h-4 w-px bg-slate-300"></div>
            <div className="flex items-center gap-1.5">
              <span className="text-slate-500">Vencidos:</span>
              <span className={cn("font-bold", kpiVencimientos.vencidos > 0 ? "text-red-700" : "text-slate-900")} data-testid="kpi-vencidos">
                {kpiVencimientos.vencidos}
              </span>
            </div>
            <div className="h-4 w-px bg-slate-300"></div>
            <div className="flex items-center gap-1.5">
              <span className="text-slate-500">Vencen 30d:</span>
              <span className={cn("font-bold", kpiVencimientos.vencen30 > 0 ? "text-red-600" : "text-slate-900")} data-testid="kpi-vencen30">
                {kpiVencimientos.vencen30}
              </span>
            </div>
            <div className="h-4 w-px bg-slate-300"></div>
            <div className="flex items-center gap-1.5">
              <span className="text-slate-500">Renovar 90d:</span>
              <span className={cn("font-bold", kpiVencimientos.renovar90 > 0 ? "text-amber-600" : "text-slate-900")} data-testid="kpi-renovar90">
                {kpiVencimientos.renovar90}
              </span>
            </div>
            {isTorreEmilia && (
              <>
                <div className="h-4 w-px bg-slate-300"></div>
                <div className="flex items-center gap-1.5">
                  <span className="text-slate-500">Pasivo ARS:</span>
                  <span className="font-bold text-green-600">
                    {formatCurrency(
                      depositos
                        .filter(d => d.estado === 'vigente' && d.moneda === 'ARS' && d.tipo_deposito === 'dinero')
                        .reduce((acc, d) => acc + (d.monto || 0), 0),
                      'ARS'
                    )}
                  </span>
                </div>
                <div className="h-4 w-px bg-slate-300"></div>
                <div className="flex items-center gap-1.5">
                  <span className="text-slate-500">Pasivo USD:</span>
                  <span className="font-bold text-blue-600">
                    {formatCurrency(
                      depositos
                        .filter(d => d.estado === 'vigente' && d.moneda === 'USD' && d.tipo_deposito === 'dinero')
                        .reduce((acc, d) => acc + (d.monto || 0), 0),
                      'USD'
                    )}
                  </span>
                </div>
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            {mode === 'contratos' && (
              <div className="flex items-center gap-2 mr-2" data-testid="contrato-filtros">
                <Button size="sm" variant={contratoFilter === 'oficina' ? 'default' : 'outline'} onClick={() => setContratoFilter('oficina')} data-testid="filtro-oficina-btn">
                  Oficina
                </Button>
                <Button size="sm" variant={contratoFilter === 'cowork' ? 'default' : 'outline'} onClick={() => setContratoFilter('cowork')} data-testid="filtro-cowork-btn">
                  Cowork{coworkSuscriptores.length > 0 ? ` (${coworkSuscriptores.length})` : ''}
                </Button>
              </div>
            )}
            {/* Botón de exportar Excel — siempre habilitado: si no hay vigentes, descarga sólo encabezados */}
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleExportExcel}
              className="text-xs text-green-600 hover:text-green-700 hover:bg-green-50"
              data-testid="exportar-excel-btn"
            >
              <FileSpreadsheet className="w-3.5 h-3.5 mr-1" />
              Exportar Excel
            </Button>
            {/* Botón Nuevo contrato — Torre Emilia: dispara modal del componente hijo via ref */}
            {isTorreEmilia && (
              <Button
                size="sm"
                onClick={() => openTipoContratoChoice(() => teViewRef.current?.openNuevo?.())}
                data-testid="te-alq-nuevo-btn-top"
              >
                <Plus className="w-3.5 h-3.5 mr-1" />
                Nuevo contrato
              </Button>
            )}
            {/* Botón Nuevo contrato — Foresta: dispara modal multi-unidad con IA */}
            {!isTorreEmilia && unidades.length > 0 && (
              <>
                <Button
                  size="sm"
                  onClick={() => openTipoContratoChoice(() => {
                    const principales = unidades.filter(u =>
                      u.rol_unidad !== 'componente'
                      && u.estado_alquiler !== 'alquilada'
                    );
                    const catalogo = {
                      principales,
                      piso1: [],
                      cocherasComponentes: [],
                      cocherasSueltas: [],
                      conjuntos: [],
                    };
                    handleOpenContratoMultiUnidad(catalogo);
                  })}
                  data-testid="fst-alq-nuevo-btn-top"
                >
                  <Plus className="w-3.5 h-3.5 mr-1" />
                  Nuevo contrato
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setReservaToEdit(null);
                    setReservaDialogMode('create');
                    setIsReservaDialogOpen(true);
                  }}
                  data-testid="fst-alq-cargar-reserva-btn"
                >
                  <FileText className="w-3.5 h-3.5 mr-1" />
                  Cargar reserva
                </Button>
              </>
            )}
            {unidades.filter(u => u.en_administracion).length > 0 && borradores.length > 0 && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setIsBorradoresOpen(true)}
                className="relative"
                data-testid="borradores-btn"
              >
                <Files className="w-4 h-4 mr-1" />
                Borradores
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-amber-500 text-white text-xs rounded-full flex items-center justify-center">
                  {borradores.length}
                </span>
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Stats Cards - Para modo 'all' solamente */}
      {mode === 'all' && (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
            <FileText className="w-4 h-4" />
            Contratos Vigentes
          </div>
          <p className="text-2xl font-bold text-slate-900">
            {contratos.filter(c => c.estado === 'vigente').length}
          </p>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
            <Wallet className="w-4 h-4" />
            Depósitos Vigentes
          </div>
          <p className="text-2xl font-bold text-slate-900">
            {depositos.filter(d => d.estado === 'vigente').length}
          </p>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
            <DollarSign className="w-4 h-4" />
            Pasivo ARS
          </div>
          <p className="text-2xl font-bold text-slate-900">
            {formatCurrency(
              depositos
                .filter(d => d.estado === 'vigente' && d.moneda === 'ARS' && d.tipo_deposito === 'dinero')
                .reduce((acc, d) => acc + (d.monto || 0), 0),
              'ARS'
            )}
          </p>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
            <DollarSign className="w-4 h-4" />
            Pasivo USD
          </div>
          <p className="text-2xl font-bold text-slate-900">
            {formatCurrency(
              depositos
                .filter(d => d.estado === 'vigente' && d.moneda === 'USD' && d.tipo_deposito === 'dinero')
                .reduce((acc, d) => acc + (d.monto || 0), 0),
              'USD'
            )}
          </p>
        </div>
      </div>
      )}

      {/* Vista específica para DEPOSITOS - Lista de todos los depósitos */}
      {mode === 'depositos' && (
        <>
          {/* Filtro de estado y buscador */}
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <span className="text-sm text-slate-500">Filtrar:</span>
              <div className="flex gap-1">
                {[
                  { value: 'todos', label: 'Todos' },
                  { value: 'vigente', label: 'Vigentes' },
                  { value: 'devuelto', label: 'Devueltos' },
                ].map(opt => (
                  <Button
                    key={opt.value}
                    variant={filtroEstadoDeposito === opt.value ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setFiltroEstadoDeposito(opt.value)}
                    className="text-xs"
                  >
                    {opt.label}
                    {opt.value !== 'todos' && (
                      <span className="ml-1 text-xs opacity-70">
                        ({depositos.filter(d => d.estado === opt.value).length})
                      </span>
                    )}
                    {opt.value === 'todos' && (
                      <span className="ml-1 text-xs opacity-70">
                        ({depositos.length})
                      </span>
                    )}
                  </Button>
                ))}
              </div>
            </div>
            
            {/* Buscador */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                type="text"
                placeholder="Buscar por oficina, inquilino o contrato..."
                value={busquedaDeposito}
                onChange={(e) => setBusquedaDeposito(e.target.value)}
                className="pl-9 w-72 h-9 text-sm"
                data-testid="busqueda-depositos"
              />
              {busquedaDeposito && (
                <button
                  onClick={() => setBusquedaDeposito('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {depositos.length === 0 ? (
            <div className="bg-slate-50 rounded-lg p-8 text-center">
              <Wallet className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 font-medium">No hay depósitos registrados</p>
              <p className="text-sm text-slate-400 mt-1">
                Los depósitos se crean desde la sección &quot;Contratos Alquiler&quot; al registrar un contrato
              </p>
            </div>
          ) : (
            <>
            {(() => {
              // Primero filtrar por estado
              let depositosFiltrados = filtroEstadoDeposito === 'todos' 
                ? depositos 
                : depositos.filter(d => d.estado === filtroEstadoDeposito);
              
              // Luego filtrar por búsqueda
              if (busquedaDeposito.trim()) {
                const busqueda = busquedaDeposito.toLowerCase().trim();
                depositosFiltrados = depositosFiltrados.filter(deposito => {
                  const unidad = unidades.find(u => u.unidad_id === deposito.unidad_id);
                  const contrato = contratos.find(c => c.contrato_id === deposito.contrato_id);
                  
                  const nombreUnidad = (unidad?.nombre_unidad || '').toLowerCase();
                  const inquilino = (contrato?.inquilino || '').toLowerCase();
                  const numeroContrato = (contrato?.numero_contrato || '').toLowerCase();
                  
                  return nombreUnidad.includes(busqueda) || 
                         inquilino.includes(busqueda) || 
                         numeroContrato.includes(busqueda);
                });
              }
              
              if (depositosFiltrados.length === 0) {
                return (
                  <div className="bg-slate-50 rounded-lg p-6 text-center">
                    <p className="text-slate-500">
                      {busquedaDeposito.trim() 
                        ? `No se encontraron resultados para "${busquedaDeposito}"`
                        : `No hay depósitos con estado "${estadosDeposito[filtroEstadoDeposito]?.label || filtroEstadoDeposito}"`
                      }
                    </p>
                  </div>
                );
              }
              
              return (
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500 py-3 px-4">Unidad</th>
                    <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500 py-3 px-3">Contrato</th>
                    <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500 py-3 px-3">Tipo</th>
                    <th className="text-right text-xs font-semibold uppercase tracking-wider text-slate-500 py-3 px-3">Monto</th>
                    <th className="text-center text-xs font-semibold uppercase tracking-wider text-slate-500 py-3 px-3">Estado</th>
                    <th className="text-center text-xs font-semibold uppercase tracking-wider text-slate-500 py-3 px-3">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {depositosFiltrados.map((deposito) => {
                    const unidad = unidades.find(u => u.unidad_id === deposito.unidad_id);
                    const contrato = contratos.find(c => c.contrato_id === deposito.contrato_id);
                    const isVigente = deposito.estado === 'vigente';
                    
                    return (
                      <tr key={deposito.deposito_id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-3 px-4">
                          <p className="font-medium text-slate-900">{unidad?.nombre_unidad || '-'}</p>
                          <p className="text-xs text-slate-500">{contrato?.inquilino || '-'}</p>
                        </td>
                        <td className="py-3 px-3">
                          <span className="text-sm font-mono text-slate-700">{contrato?.numero_contrato || '-'}</span>
                        </td>
                        <td className="py-3 px-3">
                          <span className="text-sm text-slate-600 capitalize">{deposito.tipo_deposito}</span>
                        </td>
                        <td className="py-3 px-3 text-right">
                          <span className="font-mono text-sm font-semibold text-slate-900">
                            {formatCurrency(deposito.monto, deposito.moneda)}
                          </span>
                        </td>
                        <td className="py-3 px-3 text-center">
                          <span className={cn(
                            "px-2 py-0.5 rounded-full text-xs",
                            estadosDeposito[deposito.estado]?.color
                          )}>
                            {estadosDeposito[deposito.estado]?.label}
                          </span>
                        </td>
                        <td className="py-3 px-3">
                          <div className="flex items-center justify-center gap-1">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleOpenDetalleDeposito(deposito)}
                              title="Ver detalle"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            {isVigente && (
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handleOpenDevolucion(deposito)}
                                title="Registrar devolución"
                              >
                                <Banknote className="w-4 h-4" />
                              </Button>
                            )}
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => { setSelectedDeposito(deposito); setIsDeleteDepositoOpen(true); }}
                              title="Eliminar depósito"
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
              );
            })()}
            </>
          )}
        </>
      )}

      {/* Vista para CONTRATOS - Tabla de alquileres */}
      {mode === 'contratos' && contratoFilter === 'oficina' && (
        <>
      {/* ── Torre Emilia: vista nueva (Fase 4). Foresta sigue al render legacy de abajo. ── */}
      {edificio?.modelo_administracion === 'gc_factura_y_cobra' ? (
        <AlquileresTorreEmiliaView
          ref={teViewRef}
          edificio={edificio}
          unidades={unidades}
          contratos={contratos}
          depositos={depositos}
          fetchData={fetchData}
          onReloadUnidades={onReloadUnidades}
          isComercial={isComercial}
          verazResumen={verazResumen}
          onOpenNuevoContratoMulti={handleOpenContratoMultiUnidad}
          onOpenEditContrato={handleOpenEditContrato}
          onOpenDeleteContrato={(contrato) => {
            setSelectedContrato(contrato);
            setIsDeleteContratoOpen(true);
          }}
          onOpenDeposito={handleOpenDeposito}
          onOpenDetalleDeposito={handleOpenDetalleDeposito}
        />
      ) : (
      <>
      {unidades.length === 0 ? (
        <div className="bg-slate-50 rounded-lg p-8 text-center">
          <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 font-medium">No hay unidades en administración</p>
          <p className="text-sm text-slate-400 mt-1">
            Primero da de alta unidades en &quot;2. Administración&quot;
          </p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-lg overflow-auto flex-1 min-h-0">
          <table className="w-full min-h-full [&_thead_th]:sticky [&_thead_th]:top-0 [&_thead_th]:z-10 [&_thead_th]:bg-slate-50" data-testid="tabla-alquileres">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <SortHeader label="Unidad" sortKey="unidad" sortConfig={sortAlq} onSort={requestSortAlq} align="left" className="text-left text-xs py-3 px-4" />
                <SortHeader label="Inquilino" sortKey="inquilino" sortConfig={sortAlq} onSort={requestSortAlq} align="left" className="text-left text-xs py-3 px-3" />
                <SortHeader label="Número de contrato" sortKey="numero" sortConfig={sortAlq} onSort={requestSortAlq} align="left" className="text-left text-xs py-3 px-3" />
                <SortHeader label="Vencimiento" sortKey="vencimiento" sortConfig={sortAlq} onSort={requestSortAlq} align="center" className="text-center text-xs py-3 px-3" />
                <th className="text-center text-xs font-semibold uppercase tracking-wider text-slate-500 py-3 px-3">Reserva</th>
                <th className="text-center text-xs font-semibold uppercase tracking-wider text-slate-500 py-3 px-3">Contrato</th>
                <th className="text-center text-xs font-semibold uppercase tracking-wider text-slate-500 py-3 px-3">Garantía</th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                // FILTRO Foresta: la tabla muestra SOLO contratos vigentes y reservas activas.
                // Las unidades sin contrato ni reserva NO se renderizan como fila.

                const rendered = new Set();
                const rows = [];

                // ── 1) Filas de RESERVAS ACTIVAS (entidad propia, multi-unidad) ──
                for (const reserva of reservasActivas) {
                  const unidadesRes = reserva.unidades_reserva || [];
                  if (unidadesRes.length === 0) continue;
                  // Marcar unidades como ya renderizadas para evitar doble fila
                  unidadesRes.forEach(ur => rendered.add(ur.unidad_id));

                  const nombresUnidades = unidadesRes
                    .map(ur => ur.nombre_unidad)
                    .filter(Boolean)
                    .join(', ');
                  const isHumanaReserva = (reserva.tipo_inquilino || 'juridica') === 'humana';
                  const inquilinoDisplay = isHumanaReserva
                    ? (reserva.firmante_nombre || reserva.razon_social || reserva.inquilino || '—')
                    : (reserva.nombre_comercial || reserva.razon_social || reserva.inquilino || '—');
                  const inquilinoTooltip = isHumanaReserva
                    ? (reserva.firmante_nombre || reserva.razon_social || reserva.inquilino || '')
                    : (reserva.razon_social || reserva.nombre_comercial || reserva.inquilino || '');
                  const montoDisplay = reserva.monto_total
                    ? `${reserva.moneda || 'ARS'} ${formatMoneyInput(reserva.monto_total)}`
                    : '—';
                  const fechaReservaDisplay = reserva.fecha_reserva
                    ? formatDate(reserva.fecha_reserva)
                    : '—';

                  rows.push(
                    <tr
                      key={`reserva-${reserva.reserva_id}`}
                      className="border-b border-slate-100 bg-purple-50/30 hover:bg-purple-50/50 text-[13px]"
                      data-testid={`fila-reserva-${reserva.reserva_id}`}
                    >
                      {/* Unidad/es */}
                      <td className="py-1.5 px-3 whitespace-nowrap">
                        <div className="font-semibold text-slate-900 leading-tight" title={nombresUnidades}>{nombresUnidades}</div>
                      </td>
                      {/* Inquilino */}
                      <td className="py-1.5 px-2 max-w-[220px]">
                        <div
                          className="font-medium text-slate-900 leading-tight truncate"
                          title={inquilinoTooltip || inquilinoDisplay}
                        >
                          {inquilinoDisplay}
                        </div>
                      </td>
                      {/* Número de contrato → badge "Reservada" */}
                      <td className="py-1.5 px-2">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-purple-100 text-purple-700 ring-1 ring-purple-200">
                          Reservada
                        </span>
                      </td>
                      {/* Vencimiento → fecha de reserva */}
                      <td className="py-1.5 px-2 text-center text-[12px] text-slate-700">
                        {fechaReservaDisplay}
                      </td>
                      {/* Reserva → monto */}
                      <td className="py-1.5 px-2 text-center">
                        <span className="text-[12px] font-semibold text-slate-800">{montoDisplay}</span>
                      </td>
                      {/* Contrato → archivo descargable */}
                      <td className="py-1.5 px-2 text-center">
                        {reserva.archivo?.archivo_id ? (
                          <a
                            href={reservasApi.downloadArchivoUrl(reserva.reserva_id)}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 text-[11px] text-emerald-700 hover:underline"
                            data-testid={`fst-reserva-download-${reserva.reserva_id}`}
                            title={reserva.archivo.nombre}
                          >
                            <Download className="w-3 h-3" />
                            {reserva.archivo.nombre || 'Ver'}
                          </a>
                        ) : (
                          <span className="text-[11px] text-slate-400 italic">Sin doc.</span>
                        )}
                      </td>
                      {/* Garantía → acciones */}
                      <td className="py-1.5 px-2">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            size="sm"
                            onClick={() => handleCompletarContratoDesdeReserva(reserva)}
                            data-testid={`fst-reserva-completar-${reserva.reserva_id}`}
                            className="bg-emerald-600 hover:bg-emerald-700 h-7 text-[11px] px-2"
                          >
                            Completar contrato
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEditReserva(reserva)}
                            data-testid={`fst-reserva-editar-${reserva.reserva_id}`}
                            className="h-7 px-1.5"
                            title="Editar reserva"
                          >
                            <Edit className="w-3 h-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleAskDeleteReserva(reserva)}
                            data-testid={`fst-reserva-eliminar-${reserva.reserva_id}`}
                            className="h-7 px-1.5 text-red-600 hover:bg-red-50 hover:text-red-700"
                            title="Eliminar reserva"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                }

                // ── 2) Filas de CONTRATOS VIGENTES ──
                const sortValForesta = (u, key) => {
                  const c = getContratoVigente(u.unidad_id);
                  switch (key) {
                    case 'inquilino': {
                      if (!c) return '\uffff';
                      const isHumana = (c.tipo_inquilino || 'juridica') === 'humana';
                      return (isHumana
                        ? (c.firmante_nombre || c.razon_social || c.inquilino || '')
                        : (c.nombre_comercial || c.razon_social || c.inquilino || '')).toLowerCase() || '\uffff';
                    }
                    case 'numero':
                      return c?.numero_contrato ? String(c.numero_contrato) : '\uffff';
                    case 'vencimiento':
                      return c?.fecha_fin ? new Date(c.fecha_fin).getTime() : Infinity;
                    default:
                      return u.nombre_unidad || '';
                  }
                };
                const sortedAll = [...unidades].sort((a, b) => {
                  if (!sortAlq.key || sortAlq.key === 'unidad') {
                    const base = a.nombre_unidad.localeCompare(b.nombre_unidad, 'es', { numeric: true });
                    return sortAlq.direction === 'desc' ? -base : base;
                  }
                  const cmp = compareValues(sortValForesta(a, sortAlq.key), sortValForesta(b, sortAlq.key), sortAlq.direction);
                  if (cmp !== 0) return cmp;
                  return a.nombre_unidad.localeCompare(b.nombre_unidad, 'es', { numeric: true });
                });
                const sorted = sortedAll.filter(u => !!getContratoVigente(u.unidad_id));

                for (const unidad of sorted) {
                  if (rendered.has(unidad.unidad_id)) continue;

                  const contratoVigente = getContratoVigente(unidad.unidad_id);

                  // Check if this unit is a secondary unit in a multi-contract (not the primary)
                  if (contratoVigente?.unidades_contrato?.length > 1 && contratoVigente.unidad_id !== unidad.unidad_id) {
                    continue; // Will be rendered as sub-row of the primary
                  }
                  // Find secondary units for this contract
                  const secondaryUnits = [];
                  if (contratoVigente?.unidades_contrato?.length > 1) {
                    for (const uc of contratoVigente.unidades_contrato) {
                      if (uc.unidad_id !== unidad.unidad_id) {
                        const secUnit = unidades.find(u => u.unidad_id === uc.unidad_id);
                        if (secUnit) {
                          secondaryUnits.push({ ...secUnit, contrato_monto: uc.monto, tipo_cochera: uc.tipo_cochera });
                          rendered.add(uc.unidad_id);
                        }
                      }
                    }
                  }
                  rendered.add(unidad.unidad_id);
                  
                  // Semáforo
                  let semaforoColor = null;
                  let fechaVencimiento = null;
                  if (contratoVigente) {
                    const fechaFinStr = contratoVigente.fecha_fin;
                    // Fecha "YYYY-MM-DD" como fecha LOCAL (mediodía): evita el corrimiento
                    // de un día por UTC y el error de un día en el semáforo.
                    const fechaFin = fechaFinStr ? new Date(String(fechaFinStr).length === 10 ? `${fechaFinStr}T12:00:00` : fechaFinStr) : null;
                    if (fechaFin && !isNaN(fechaFin.getTime())) {
                      const hoy = new Date();
                      const diasRestantes = Math.ceil((fechaFin - hoy) / (1000 * 60 * 60 * 24));
                      fechaVencimiento = formatDate(fechaFinStr);
                      if (diasRestantes <= 30) semaforoColor = 'bg-red-500';
                      else if (diasRestantes <= 89) semaforoColor = 'bg-yellow-500';
                      else semaforoColor = 'bg-green-500';
                    }
                  }

                  const totalRows = 1 + secondaryUnits.length;
                  const depositoVigente = contratoVigente ? getDepositoContrato(contratoVigente.contrato_id) : null;

                  // Main row
                  rows.push(
                    <tr 
                      key={unidad.unidad_id} 
                      className={cn("border-b border-slate-100 hover:bg-slate-50 text-[13px]", secondaryUnits.length > 0 && "border-b-0")}
                      data-testid={`fila-alquiler-${unidad.unidad_id}`}
                    >
                      <td className="py-1.5 px-3 whitespace-nowrap" rowSpan={totalRows}>
                        <div className="font-semibold text-slate-900 leading-tight" title={unidad.nombre_unidad}>{unidad.nombre_unidad}</div>
                        {secondaryUnits.length > 0 && (
                          <div className="mt-1 space-y-0.5">
                            {secondaryUnits.map(su => (
                              <div key={su.unidad_id} className="flex items-center gap-1 text-[11px] text-indigo-700 whitespace-nowrap">
                                <span className="text-slate-300">└</span>
                                <span className="font-medium">{su.nombre_unidad}</span>
                                {su.tipo_cochera === 'estandar' && <span className="bg-indigo-50 px-1 py-0.5 rounded text-[10px]">Estándar</span>}
                                {su.tipo_cochera === 'personalizado' && <span className="bg-amber-50 text-amber-700 px-1 py-0.5 rounded text-[10px]">${formatMoneyInput(su.contrato_monto)}</span>}
                              </div>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="py-1.5 px-2 max-w-[220px]">
                        {contratoVigente ? (() => {
                          const isHumana = (contratoVigente.tipo_inquilino || 'juridica') === 'humana';
                          const inqDisplay = isHumana
                            ? (contratoVigente.firmante_nombre || contratoVigente.razon_social || contratoVigente.inquilino || '—')
                            : (contratoVigente.nombre_comercial || contratoVigente.razon_social || contratoVigente.inquilino || '—');
                          const inqTooltip = isHumana
                            ? (contratoVigente.firmante_nombre || contratoVigente.razon_social || contratoVigente.inquilino || '')
                            : (contratoVigente.razon_social || contratoVigente.nombre_comercial || contratoVigente.inquilino || '');
                          const verazCount = verazResumen[contratoVigente.contrato_id] || 0;
                          return (
                            <div className="leading-tight">
                              <div
                                className="font-medium text-slate-900 truncate"
                                title={inqTooltip || inqDisplay}
                              >
                                {inqDisplay}
                              </div>
                            </div>
                          );
                        })() : (
                          <span className="text-slate-400 text-[12px]">—</span>
                        )}
                      </td>
                      {/* Número de contrato */}
                      <td className="py-1.5 px-2">
                        {!contratoVigente ? (
                          <span className="text-[12px] text-amber-700">Sin contrato</span>
                        ) : contratoVigente.numero_contrato ? (
                          <span className="text-[12px] font-medium text-slate-700">Nº {contratoVigente.numero_contrato}</span>
                        ) : (
                          <span className="text-[12px] text-slate-400 italic">Sin número</span>
                        )}
                      </td>
                      {/* Vencimiento */}
                      <td className="py-1.5 px-2 text-center">
                        {contratoVigente && fechaVencimiento ? (
                          <div className="flex items-center justify-center gap-1.5">
                            <span className={cn("w-2 h-2 rounded-full", semaforoColor)}></span>
                            <span className="text-[12px] text-slate-700">{fechaVencimiento}</span>
                          </div>
                        ) : contratoVigente ? (
                          <span className="text-slate-400 text-[12px] italic">Sin fecha</span>
                        ) : (
                          <span className="text-slate-400 text-[12px]">—</span>
                        )}
                      </td>
                      {/* Reserva */}
                      <td className="py-1.5 px-2 text-center">
                        {contratoVigente ? (
                          <span className="text-slate-300 text-[11px]">—</span>
                        ) : (
                          <ReservaCell unidad={unidad} onUpdated={() => { fetchData(); onReloadUnidades && onReloadUnidades(); }} />
                        )}
                      </td>
                      {/* Contrato (acciones) */}
                      <td className="py-1.5 px-2">
                        <div className="flex items-center justify-center gap-1">
                          {contratoVigente ? (
                            <>
                              <Button 
                                variant="outline" 
                                size="sm"
                                className="h-7 text-[11px] px-2"
                                onClick={() => handleOpenEditContrato(contratoVigente)}
                                data-testid={`ver-contrato-${unidad.unidad_id}`}
                              >
                                Ver/Editar
                              </Button>
                              {contratoVigente.archivo_contrato_id && (
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  className="h-7 px-1.5"
                                  onClick={() => {
                                    const url = contratosApi.downloadArchivo(contratoVigente.archivo_contrato_id);
                                    window.open(url, '_blank');
                                  }}
                                  title="Descargar contrato"
                                >
                                  <Download className="w-3.5 h-3.5" />
                                </Button>
                              )}
                            </>
                          ) : (
                            <Button 
                              size="sm" 
                              className="h-7 text-[11px] px-2"
                              onClick={() => handleOpenContrato(unidad)}
                              data-testid={`nuevo-contrato-${unidad.unidad_id}`}
                            >
                              <Plus className="w-3 h-3 mr-1" />
                              Contrato
                            </Button>
                          )}
                        </div>
                      </td>
                      {/* Garantía */}
                      <td className="py-1.5 px-2">
                        <div className="flex items-center justify-center gap-1">
                          {(() => {
                            if (depositoVigente) {
                              return (
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  className="h-7 text-[11px] px-2"
                                  onClick={() => handleOpenDetalleDeposito(depositoVigente)}
                                >
                                  Ver/Editar
                                </Button>
                              );
                            } else if (contratoVigente && contratoVigente.garantia_no_disponible) {
                              return (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 text-[11px] px-2 text-slate-500 border-slate-300 hover:bg-slate-50"
                                  onClick={() => handleOpenDeposito(unidad, contratoVigente)}
                                  data-testid={`garantia-no-disponible-${unidad.unidad_id}`}
                                  title="Marcada como no disponible. Click para editar."
                                >
                                  No la tenemos
                                </Button>
                              );
                            } else if (contratoVigente) {
                              return (
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  className="h-7 text-[11px] px-2 text-red-600 border-red-300 hover:bg-red-50"
                                  onClick={() => handleOpenDeposito(unidad, contratoVigente)}
                                  data-testid={`nueva-garantia-${unidad.unidad_id}`}
                                >
                                  <Plus className="w-3 h-3 mr-1" />
                                  Falta Garantía
                                </Button>
                              );
                            } else {
                              return <span className="text-slate-400 text-[12px]">—</span>;
                            }
                          })()}
                        </div>
                      </td>
                    </tr>
                  );
                }

                if (rows.length === 0) {
                  return (
                    <tr>
                      <td colSpan={20} className="text-center py-12 text-sm text-slate-500">
                        <div className="space-y-1">
                          <p className="font-medium text-slate-700">No hay contratos vigentes ni reservas activas</p>
                          <p className="text-xs">Tocá <strong>Nuevo contrato</strong> o <strong>Cargar reserva</strong> para arrancar.</p>
                        </div>
                      </td>
                    </tr>
                  );
                }
                return rows;
              })()}
            </tbody>
          </table>
        </div>
      )}
      </>
      )}
        </>
      )}

      {/* ============ DIALOGS ============ */}
      
      {/* Contrato Dialog */}
      <Dialog open={isContratoDialogOpen} onOpenChange={(open) => {
        setIsContratoDialogOpen(open);
        if (!open) {
          setContratoTab(multiUnidadMode ? 'unidades' : 'carga_ia');
          setMultiUnidadMode(false);
          setSeleccionMultiUnidad([]);
          setCatalogoMultiUnidad(null);
          setIaDeteccionUnidades({ status: 'none', detectados: [], matched: [] });
        }
      }}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              {multiUnidadMode
                ? (seleccionMultiUnidad.length === 0
                    ? 'Nuevo Contrato'
                    : `Nuevo Contrato - ${seleccionMultiUnidad.length} unidad${seleccionMultiUnidad.length > 1 ? 'es' : ''} seleccionada${seleccionMultiUnidad.length > 1 ? 's' : ''}`)
                : `Nuevo Contrato - ${selectedUnidad?.nombre_unidad || ''}`}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveContrato}>
            <Tabs value={contratoTab} onValueChange={setContratoTab} className="w-full">
              <TabsList className={cn("grid w-full mb-4", (() => {
                const isSimple = selectedUnidad?.tipo_administracion === 'simple';
                // Tabs base: documentos + carga_ia + basico + condiciones + fiscal + veraz = 6.
                // +1 multi-unidad (unidades), +1 colppy (no-simple/multi), +1 comision (isComercial).
                if (multiUnidadMode) {
                  if (isComercial) return "grid-cols-9";
                  return "grid-cols-8";
                }
                if (isSimple && isComercial) return "grid-cols-7";
                if (isSimple) return "grid-cols-6";
                if (isComercial) return "grid-cols-8";
                return "grid-cols-7";
              })())}>
                <TabsTrigger value="documentos" data-testid="tab-documentos">Documentos</TabsTrigger>
                <TabsTrigger value="carga_ia">Carga IA</TabsTrigger>
                {multiUnidadMode && <TabsTrigger value="unidades" data-testid="tab-unidades">Unidades</TabsTrigger>}
                <TabsTrigger value="basico">Datos</TabsTrigger>
                <TabsTrigger value="condiciones">Condiciones</TabsTrigger>
                <TabsTrigger value="fiscal">Fiscal</TabsTrigger>
                <TabsTrigger value="veraz" data-testid="tab-veraz-nuevo">Veraz</TabsTrigger>
                {(multiUnidadMode || selectedUnidad?.tipo_administracion !== 'simple') && <TabsTrigger value="colppy">Colppy</TabsTrigger>}
                {isComercial && <TabsTrigger value="comision">Comisión</TabsTrigger>}
              </TabsList>

              {/* Tab: Documentos (etapas contractuales) */}
              <TabsContent value="documentos" className="space-y-3">
                <DocumentosSection ref={documentosRef} contratoId={contratoForm?.contrato_id || null} readOnly={false} />
              </TabsContent>

              {/* Tab: Unidades del contrato (Torre Emilia, multi-unidad) */}
              {multiUnidadMode && (
                <TabsContent value="unidades" className="space-y-3">
                  {/* Banner de resultado de detección IA */}
                  {iaDeteccionUnidades.status === 'matched' && (
                    <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-xs text-emerald-900" data-testid="ia-banner-matched">
                      <strong>IA detectó y preseleccionó estas unidades:</strong>{' '}
                      <span className="font-mono">{iaDeteccionUnidades.matched.join(', ')}</span>.
                      {' '}Revisá y confirmá la selección.
                    </div>
                  )}
                  {iaDeteccionUnidades.status === 'detected_no_match' && (
                    <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-900" data-testid="ia-banner-no-match">
                      <strong>IA detectó posibles unidades en el contrato, pero no pudo relacionarlas con unidades disponibles.</strong>{' '}
                      Revisá y seleccioná manualmente.
                      {iaDeteccionUnidades.detectados.length > 0 && (
                        <div className="mt-1 text-amber-800">
                          <em>Detectado por IA:</em> {iaDeteccionUnidades.detectados.join(', ')}
                        </div>
                      )}
                    </div>
                  )}
                  {iaDeteccionUnidades.status === 'not_detected' && (
                    <div className="bg-slate-50 border border-slate-200 rounded p-3 text-xs text-slate-700" data-testid="ia-banner-not-detected">
                      <strong>IA no encontró unidades en el contrato.</strong> Seleccioná manualmente las unidades.
                    </div>
                  )}

                  {(() => {
                    const cat = catalogoMultiUnidad || {};
                    // IDs de unidades sintéticas "padre" de bloques: representan al bloque
                    // pero NO se alquilan como unidad completa (sus miembros internos sí).
                    const ID_UNIDADES_PADRE_BLOQUE = new Set(['unit_piso1_te', 'unit_subsuelo_te']);
                    // Aplanamos las unidades alquilables (principales + miembros del Piso 1 + cocheras).
                    const unidadesPlanas = [
                      ...(cat.principales || []),
                      ...(cat.piso1 || []),
                      ...(cat.cocherasComponentes || []),
                      ...(cat.cocherasSueltas || []),
                    ];
                    // Set de unidad_ids YA asociadas a contratos vigentes (no disponibles).
                    const unidadIdsAlquiladas = new Set();
                    for (const c of (contratos || [])) {
                      if (c.estado !== 'vigente') continue;
                      if (c.unidad_id) unidadIdsAlquiladas.add(c.unidad_id);
                      for (const u of (c.unidades_contrato || [])) {
                        if (u?.unidad_id) unidadIdsAlquiladas.add(u.unidad_id);
                      }
                    }
                    // Filtrar: excluir padres sintéticos + ya alquiladas + reservadas no pre-seleccionadas
                    // + deduplicar + ordenar natural creciente.
                    // Reservadas: si la unidad está reservada y NO está ya en seleccionMultiUnidad
                    // (caso "Completar contrato desde reserva" que las preselecciona), se oculta.
                    const idsPreseleccionados = new Set(
                      seleccionMultiUnidad.filter(s => s.kind === 'unidad').map(s => s.id)
                    );
                    const seen = new Set();
                    const unidadesAlquilables = unidadesPlanas
                      .filter(u => {
                        if (ID_UNIDADES_PADRE_BLOQUE.has(u.unidad_id)) return false; // Piso 1 no se alquila como un todo
                        if (seen.has(u.unidad_id)) return false;
                        seen.add(u.unidad_id);
                        if (unidadIdsAlquiladas.has(u.unidad_id)) return false;
                        if (u.estado_alquiler === 'reservada' && !idsPreseleccionados.has(u.unidad_id)) return false;
                        return true;
                      })
                      .sort((a, b) => {
                        const an = a._noAdmin ? 1 : 0, bn = b._noAdmin ? 1 : 0;
                        if (an !== bn) return an - bn; // no administradas al final
                        return String(a.nombre_unidad || '').localeCompare(
                          String(b.nombre_unidad || ''),
                          'es',
                          { numeric: true, sensitivity: 'base' }
                        );
                      });
                    const conjuntos = cat.conjuntos || [];
                    const subsueloId = 'virt_subsuelo';
                    const subsueloSeleccionado = seleccionMultiUnidad.some(s => s.kind === 'conjunto' && s.id === subsueloId);

                    // Filtro de texto por número/nombre de unidad.
                    const q = (unidadBusqueda || '').trim().toLowerCase();
                    const unidadesFiltradas = q
                      ? unidadesAlquilables.filter(u => String(u.nombre_unidad || '').toLowerCase().includes(q))
                      : unidadesAlquilables;

                    const toggleU = (u) => {
                      if (u._noAdmin) return; // unidad sin administración: no seleccionable
                      if (subsueloSeleccionado) {
                        toast.error('Si seleccionás "Cocheras Subsuelo", no podés combinarlo con otras unidades.');
                        return;
                      }
                      setSeleccionMultiUnidad(prev => {
                        if (prev.some(s => s.kind === 'unidad' && s.id === u.unidad_id)) {
                          return prev.filter(s => !(s.kind === 'unidad' && s.id === u.unidad_id));
                        }
                        return [...prev, { kind: 'unidad', id: u.unidad_id, nombre: u.nombre_unidad, tipo: u.tipo }];
                      });
                      setSelectedUnidad(u);
                    };

                    const toggleC = (c) => {
                      setSeleccionMultiUnidad(prev => {
                        if (prev.some(s => s.kind === 'conjunto' && s.id === c.virtual_id)) {
                          return prev.filter(s => !(s.kind === 'conjunto' && s.id === c.virtual_id));
                        }
                        if (prev.length > 0) toast.info('Limpiamos selección: el conjunto se contrata solo.');
                        return [{ kind: 'conjunto', id: c.virtual_id, nombre: c.nombre, miembros: c.miembros }];
                      });
                      if (c.miembros && c.miembros.length > 0) setSelectedUnidad(c.miembros[0]);
                    };

                    return (
                      <>
                        {/* Conjuntos */}
                        {conjuntos.length > 0 && (
                          <div className="space-y-2">
                            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Conjuntos alquilables</p>
                            <div className="grid gap-2">
                              {conjuntos.map(c => {
                                const checked = seleccionMultiUnidad.some(s => s.kind === 'conjunto' && s.id === c.virtual_id);
                                return (
                                  <label key={c.virtual_id} className="flex items-start gap-2 p-2 border border-slate-200 rounded hover:bg-slate-50 cursor-pointer">
                                    <input type="checkbox" checked={checked} onChange={() => toggleC(c)} className="mt-0.5" data-testid={`multi-conjunto-${c.virtual_id}`} />
                                    <div className="text-sm">
                                      <div className="font-medium text-slate-900">{c.nombre}</div>
                                      <div className="text-[11px] text-slate-500">{c.miembros?.length || 0} miembros</div>
                                    </div>
                                  </label>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Unidades sueltas */}
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                              Unidades disponibles ({unidadesFiltradas.length}{q ? ` de ${unidadesAlquilables.length}` : ''})
                            </p>
                            {seleccionMultiUnidad.length > 0 && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-7 text-xs text-slate-600 hover:text-red-600"
                                onClick={() => setSeleccionMultiUnidad([])}
                                data-testid="multi-limpiar-seleccion"
                              >
                                <X className="w-3 h-3 mr-1" /> Limpiar selección
                              </Button>
                            )}
                          </div>
                          {/* Buscador rápido por número de oficina */}
                          <div className="relative">
                            <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                            <Input
                              type="text"
                              value={unidadBusqueda}
                              onChange={(e) => setUnidadBusqueda(e.target.value)}
                              placeholder="Buscar oficina por número (ej: 308)"
                              className="h-8 pl-8 text-sm"
                              data-testid="multi-unidad-buscador"
                            />
                            {q && (
                              <button
                                type="button"
                                onClick={() => setUnidadBusqueda('')}
                                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                                data-testid="multi-unidad-buscador-limpiar"
                              >
                                <X className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                          {unidadesAlquilables.length === 0 ? (
                            <div className="text-xs text-slate-500 italic px-3 py-4 border border-dashed border-slate-200 rounded text-center">
                              No hay unidades disponibles. Las unidades deben estar administradas por GC y sin contrato vigente.
                            </div>
                          ) : unidadesFiltradas.length === 0 ? (
                            <div className="text-xs text-slate-500 italic px-3 py-4 border border-dashed border-slate-200 rounded text-center" data-testid="multi-unidad-sin-resultados">
                              Sin resultados para "{unidadBusqueda}".
                            </div>
                          ) : (
                            <div className="max-h-[300px] overflow-y-auto border border-slate-200 rounded">
                              {unidadesFiltradas.map(u => {
                                const checked = seleccionMultiUnidad.some(s => s.kind === 'unidad' && s.id === u.unidad_id);
                                const noAdmin = u._noAdmin === true;
                                return (
                                  <label
                                    key={u.unidad_id}
                                    title={noAdmin ? 'Sin administración. Activá la administración en la tab "2. Admin" para poder cargarle un contrato.' : undefined}
                                    className={cn(
                                      'flex items-center gap-2 px-3 py-1.5 border-b border-slate-100',
                                      noAdmin ? 'bg-slate-50 opacity-60 cursor-not-allowed' : 'hover:bg-slate-50 cursor-pointer'
                                    )}
                                  >
                                    {noAdmin ? (
                                      <Lock className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" data-testid={`multi-unidad-lock-${u.unidad_id}`} />
                                    ) : (
                                      <input type="checkbox" checked={checked} onChange={() => toggleU(u)} disabled={subsueloSeleccionado} data-testid={`multi-unidad-${u.unidad_id}`} />
                                    )}
                                    <span className={cn('text-sm flex-1', noAdmin && 'text-slate-500')}>{u.nombre_unidad}</span>
                                    <span className="text-[11px] text-slate-500 capitalize">{noAdmin ? 'sin administración' : u.tipo}</span>
                                  </label>
                                );
                              })}
                            </div>
                          )}
                        </div>

                        {seleccionMultiUnidad.length > 0 && (
                          <div className="bg-emerald-50 border border-emerald-200 rounded p-2 text-xs text-emerald-800 flex items-center justify-between gap-2" data-testid="multi-seleccion-resumen">
                            <span><strong>Seleccionado:</strong> {seleccionMultiUnidad.map(s => s.nombre).join(', ')}</span>
                            <button
                              type="button"
                              onClick={() => setSeleccionMultiUnidad([])}
                              className="text-emerald-700 hover:text-red-600 shrink-0"
                              title="Limpiar selección"
                              data-testid="multi-limpiar-resumen"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </TabsContent>
              )}

              {/* Tab: Carga IA */}
              <TabsContent value="carga_ia" className="space-y-4">
                <div className="border border-dashed border-slate-300 rounded-lg p-6 bg-slate-50/50">
                  <div className="flex items-center gap-2 mb-4">
                    <Sparkles className="w-5 h-5 text-purple-500" />
                    <span className="text-base font-medium text-slate-700">Autocompletar con IA</span>
                    <span className="text-sm text-slate-500">(opcional)</span>
                  </div>
                  <p className="text-sm text-slate-500 mb-4">
                    Subí un contrato o adenda para que la IA complete automáticamente los datos del contrato. Sirve solo para llenar las demás solapas — los archivos del expediente se administran en la solapa <strong>Documentos</strong>.
                  </p>
                  
                  <div className="flex items-center justify-center w-full">
                    <label className={cn(
                      "flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer transition-colors",
                      analizandoContrato 
                        ? "bg-purple-50 border-purple-300" 
                        : "bg-white hover:bg-slate-50 border-slate-300"
                    )}>
                      <div className="flex flex-col items-center justify-center py-4">
                        {analizandoContrato ? (
                          <>
                            <Loader2 className="w-8 h-8 text-purple-500 mb-2 animate-spin" />
                            <p className="text-sm text-purple-600 font-medium">Analizando contrato...</p>
                          </>
                        ) : (
                          <>
                            <div className="flex items-center gap-2 mb-2">
                              <Upload className="w-6 h-6 text-slate-400" />
                              <Sparkles className="w-4 h-4 text-purple-500" />
                            </div>
                            <p className="text-sm text-slate-600 font-medium">
                              Hacé clic para subir el contrato
                            </p>
                            <p className="text-xs text-slate-400 mt-1">PDF, DOC, DOCX, JPG, PNG</p>
                          </>
                        )}
                      </div>
                      <input 
                        type="file" 
                        className="hidden" 
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                        disabled={analizandoContrato}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            setContratoFile(file);
                            handleAnalizarContrato(file);
                          }
                        }}
                      />
                    </label>
                  </div>
                  
                  <p className="text-xs text-slate-400 mt-4 text-center">
                    Podés completar los datos manualmente en las siguientes pestañas si preferís no usar esta función.
                  </p>
                </div>
              </TabsContent>

              {/* Tab: Datos Básicos */}
              <TabsContent value="basico" className="space-y-4">
                {/* Tipo de inquilino — primer campo */}
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <Label className="text-sm font-medium mb-2 block">Tipo de inquilino *</Label>
                  <div className="flex gap-2">
                    {[{value:'humana',label:'Persona humana'},{value:'juridica',label:'Persona jurídica'}].map(opt => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setContratoForm({...contratoForm, tipo_inquilino: opt.value})}
                        data-testid={`tipo-inquilino-${opt.value}`}
                        className={`px-4 py-1.5 rounded-md text-sm font-medium border transition-colors ${(contratoForm.tipo_inquilino || 'juridica') === opt.value ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'}`}
                      >{opt.label}</button>
                    ))}
                  </div>
                </div>

                {(contratoForm.tipo_inquilino || 'juridica') === 'humana' ? (
                  <>
                    <div>
                      <h4 className="text-sm font-medium text-slate-700 mb-3">Datos del firmante</h4>
                      <div className="grid grid-cols-2 gap-4 mb-3">
                        <div className="space-y-2">
                          <Label>Nombre y apellido *</Label>
                          <Input value={contratoForm.firmante_nombre || ''} onChange={(e)=>{const v=e.target.value; setContratoForm(f=>({...f, firmante_nombre:v, razon_social:v, ...(f.contacto_comercial_mismo_firmante!==false?{contacto_comercial_nombre:v}:{}),...(f.contacto_admin_mismo_firmante!==false?{contacto_admin_nombre:v}:{})}))}} placeholder="Nombre y apellido" data-testid="firmante-nombre" />
                        </div>
                        <div className="space-y-2">
                          <Label>Email *</Label>
                          <Input type="email" value={contratoForm.firmante_email || ''} onChange={(e)=>{const v=e.target.value; setContratoForm(f=>({...f, firmante_email:v, ...(f.contacto_comercial_mismo_firmante!==false?{contacto_comercial_email:v}:{}),...(f.contacto_admin_mismo_firmante!==false?{contacto_admin_email:v}:{}),...((!f.email_facturacion||f.email_facturacion_default)?{email_facturacion:v,email_facturacion_default:true,emails_facturacion:[v,...((f.emails_facturacion||[]).slice(1))]}:{})}))}} placeholder="email@ejemplo.com" data-testid="firmante-email" />
                        </div>
                        <div className="space-y-2">
                          <Label>WhatsApp</Label>
                          <Input type="tel" value={contratoForm.firmante_whatsapp || ''} onChange={(e)=>{const v=e.target.value; setContratoForm(f=>({...f, firmante_whatsapp:v, ...(f.contacto_comercial_mismo_firmante!==false?{contacto_comercial_telefono:v}:{}),...(f.contacto_admin_mismo_firmante!==false?{contacto_admin_telefono:v}:{})}))}} placeholder="+54 261 ..." />
                        </div>
                        <div className="space-y-2">
                          <Label>Descripción de la actividad</Label>
                          <Input value={contratoForm.descripcion_empresa} onChange={(e)=>setContratoForm({...contratoForm, descripcion_empresa:e.target.value})} placeholder="Ej: Profesional independiente, freelance..." />
                        </div>
                      </div>
                    </div>
                    <div className="border-t pt-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-medium text-slate-700">Contacto Comercial</h4>
                        <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
                          <input type="checkbox" checked={contratoForm.contacto_comercial_mismo_firmante!==false} onChange={(e)=>{const c=e.target.checked; setContratoForm(f=>({...f, contacto_comercial_mismo_firmante:c, ...(c?{contacto_comercial_nombre:f.firmante_nombre, contacto_comercial_email:f.firmante_email, contacto_comercial_telefono:f.firmante_whatsapp}:{})}))}} data-testid="comercial-mismo-firmante" />
                          Mismo que el firmante
                        </label>
                      </div>
                      {contratoForm.contacto_comercial_mismo_firmante === false && (
                        <div className="grid grid-cols-3 gap-4">
                          <Input value={contratoForm.contacto_comercial_nombre} onChange={(e)=>setContratoForm({...contratoForm, contacto_comercial_nombre:e.target.value})} placeholder="Nombre y apellido" />
                          <Input type="email" value={contratoForm.contacto_comercial_email} onChange={(e)=>setContratoForm({...contratoForm, contacto_comercial_email:e.target.value})} placeholder="email@ejemplo.com" />
                          <Input type="tel" value={contratoForm.contacto_comercial_telefono} onChange={(e)=>setContratoForm({...contratoForm, contacto_comercial_telefono:e.target.value})} placeholder="+54 261 ..." />
                        </div>
                      )}
                    </div>
                    <div className="border-t pt-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-medium text-slate-700">Contacto Administrativo</h4>
                        <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
                          <input type="checkbox" checked={contratoForm.contacto_admin_mismo_firmante!==false} onChange={(e)=>{const c=e.target.checked; setContratoForm(f=>({...f, contacto_admin_mismo_firmante:c, ...(c?{contacto_admin_nombre:f.firmante_nombre, contacto_admin_email:f.firmante_email, contacto_admin_telefono:f.firmante_whatsapp}:{})}))}} data-testid="admin-mismo-firmante" />
                          Mismo que el firmante
                        </label>
                      </div>
                      {contratoForm.contacto_admin_mismo_firmante === false && (
                        <div className="grid grid-cols-3 gap-4">
                          <Input value={contratoForm.contacto_admin_nombre} onChange={(e)=>setContratoForm({...contratoForm, contacto_admin_nombre:e.target.value})} placeholder="Nombre y apellido" />
                          <Input type="email" value={contratoForm.contacto_admin_email} onChange={(e)=>setContratoForm({...contratoForm, contacto_admin_email:e.target.value})} placeholder="email@ejemplo.com" />
                          <Input type="tel" value={contratoForm.contacto_admin_telefono} onChange={(e)=>setContratoForm({...contratoForm, contacto_admin_telefono:e.target.value})} placeholder="+54 261 ..." />
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <h4 className="text-sm font-medium text-slate-700 mb-3">Datos de la empresa</h4>
                      <div className="grid grid-cols-2 gap-4 mb-3">
                        <div className="space-y-2">
                          <Label>Razón Social *</Label>
                          <Input value={contratoForm.razon_social} onChange={(e)=>setContratoForm({...contratoForm, razon_social:e.target.value})} placeholder="Razón social" required />
                        </div>
                        <div className="space-y-2">
                          <Label>Nombre Comercial</Label>
                          <Input value={contratoForm.nombre_comercial} onChange={(e)=>setContratoForm({...contratoForm, nombre_comercial:e.target.value})} placeholder="Nombre comercial" />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Descripción de la empresa</Label>
                        <Input value={contratoForm.descripcion_empresa} onChange={(e)=>setContratoForm({...contratoForm, descripcion_empresa:e.target.value})} placeholder="Ej: Consultora de tecnología, Estudio contable..." />
                      </div>
                    </div>
                    <div className="border-t pt-4">
                      <h4 className="text-sm font-medium text-slate-700 mb-3">Contacto Comercial</h4>
                      <div className="grid grid-cols-3 gap-4">
                        <Input value={contratoForm.contacto_comercial_nombre} onChange={(e)=>{const v=e.target.value; setContratoForm(f=>({...f, contacto_comercial_nombre:v, ...(f.contacto_admin_mismo_comercial!==false?{contacto_admin_nombre:v}:{})}))}} placeholder="Nombre y apellido" data-testid="comercial-nombre" />
                        <Input type="email" value={contratoForm.contacto_comercial_email} onChange={(e)=>{const v=e.target.value; setContratoForm(f=>({...f, contacto_comercial_email:v, ...(f.contacto_admin_mismo_comercial!==false?{contacto_admin_email:v}:{}),...((!f.email_facturacion||f.email_facturacion_default)?{email_facturacion:v, email_facturacion_default:true,emails_facturacion:[v,...((f.emails_facturacion||[]).slice(1))]}:{})}))}} placeholder="email@ejemplo.com" />
                        <Input type="tel" value={contratoForm.contacto_comercial_telefono} onChange={(e)=>{const v=e.target.value; setContratoForm(f=>({...f, contacto_comercial_telefono:v, ...(f.contacto_admin_mismo_comercial!==false?{contacto_admin_telefono:v}:{})}))}} placeholder="+54 261 ..." />
                      </div>
                    </div>
                    <div className="border-t pt-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-medium text-slate-700">Contacto Administrativo</h4>
                        <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
                          <input type="checkbox" checked={contratoForm.contacto_admin_mismo_comercial!==false} onChange={(e)=>{const c=e.target.checked; setContratoForm(f=>({...f, contacto_admin_mismo_comercial:c, ...(c?{contacto_admin_nombre:f.contacto_comercial_nombre, contacto_admin_email:f.contacto_comercial_email, contacto_admin_telefono:f.contacto_comercial_telefono}:{})}))}} data-testid="admin-mismo-comercial" />
                          Mismo que el contacto comercial
                        </label>
                      </div>
                      {contratoForm.contacto_admin_mismo_comercial === false && (
                        <div className="grid grid-cols-3 gap-4">
                          <Input value={contratoForm.contacto_admin_nombre} onChange={(e)=>setContratoForm({...contratoForm, contacto_admin_nombre:e.target.value})} placeholder="Nombre y apellido" />
                          <Input type="email" value={contratoForm.contacto_admin_email} onChange={(e)=>setContratoForm({...contratoForm, contacto_admin_email:e.target.value})} placeholder="email@ejemplo.com" />
                          <Input type="tel" value={contratoForm.contacto_admin_telefono} onChange={(e)=>setContratoForm({...contratoForm, contacto_admin_telefono:e.target.value})} placeholder="+54 261 ..." />
                        </div>
                      )}
                    </div>
                  </>
                )}
                {/* === Alta en sistema / Acceso a plataforma === */}
                <div className="border-t pt-4 bg-blue-50/30 -mx-4 px-4 pb-4 mt-2">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h4 className="text-sm font-medium text-slate-700">Alta en sistema / Acceso a plataforma</h4>
                      <p className="text-[11px] text-slate-500">Persona que podrá activar la cuenta y entrar al portal del inquilino.</p>
                    </div>
                    <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={
                          (contratoForm.tipo_inquilino || 'juridica') === 'humana'
                            ? contratoForm.acceso_mismo_firmante !== false
                            : contratoForm.acceso_mismo_admin !== false
                        }
                        onChange={(e) => {
                          const c = e.target.checked;
                          const isHumana = (contratoForm.tipo_inquilino || 'juridica') === 'humana';
                          setContratoForm(f => isHumana ? ({
                            ...f,
                            acceso_mismo_firmante: c,
                            ...(c ? { acceso_nombre: f.firmante_nombre || '', acceso_email: f.firmante_email || '' } : {}),
                          }) : ({
                            ...f,
                            acceso_mismo_admin: c,
                            ...(c ? { acceso_nombre: f.contacto_admin_nombre || '', acceso_email: f.contacto_admin_email || '' } : {}),
                          }));
                        }}
                        data-testid="acceso-mismo-toggle"
                      />
                      {(contratoForm.tipo_inquilino || 'juridica') === 'humana'
                        ? 'Mismo que el firmante'
                        : 'Mismo que el contacto administrativo'}
                    </label>
                  </div>
                  {((contratoForm.tipo_inquilino || 'juridica') === 'humana'
                    ? contratoForm.acceso_mismo_firmante !== false
                    : contratoForm.acceso_mismo_admin !== false) ? (
                    <div className="text-xs text-slate-600 bg-white border border-slate-200 rounded p-2">
                      Se usarán los datos de <strong>
                        {(contratoForm.tipo_inquilino || 'juridica') === 'humana' ? 'el firmante' : 'el contacto administrativo'}
                      </strong> para el alta: <em>{
                        ((contratoForm.tipo_inquilino || 'juridica') === 'humana' ? contratoForm.firmante_nombre : contratoForm.contacto_admin_nombre) || '(sin nombre)'
                      }</em> · <em>{
                        ((contratoForm.tipo_inquilino || 'juridica') === 'humana' ? contratoForm.firmante_email : contratoForm.contacto_admin_email) || '(sin email)'
                      }</em>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <Label className="text-xs">Nombre y apellido *</Label>
                        <Input value={contratoForm.acceso_nombre || ''} onChange={(e) => setContratoForm({...contratoForm, acceso_nombre: e.target.value})} placeholder="Nombre y apellido" data-testid="acceso-nombre" />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Email de acceso *</Label>
                        <Input type="email" value={contratoForm.acceso_email || ''} onChange={(e) => setContratoForm({...contratoForm, acceso_email: e.target.value})} placeholder="email@ejemplo.com" data-testid="acceso-email" />
                      </div>
                    </div>
                  )}
                </div>

                {/* Contacto de emergencia — Fase 3 */}
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-3" data-testid="nuevo-contacto-emergencia">
                  <Label className="text-sm font-medium">Contacto de emergencia</Label>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Nombre</Label>
                      <Input value={contratoForm.emergencia_nombre || ''} onChange={(e) => setContratoForm({...contratoForm, emergencia_nombre: e.target.value})} placeholder="Nombre y apellido" data-testid="nuevo-emergencia-nombre" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Teléfono</Label>
                      <Input type="tel" value={contratoForm.emergencia_telefono || ''} onChange={(e) => setContratoForm({...contratoForm, emergencia_telefono: e.target.value})} placeholder="+54 261 123-4567" data-testid="nuevo-emergencia-telefono" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Email</Label>
                      <Input type="email" value={contratoForm.emergencia_email || ''} onChange={(e) => setContratoForm({...contratoForm, emergencia_email: e.target.value})} placeholder="email@ejemplo.com" data-testid="nuevo-emergencia-email" />
                    </div>
                  </div>
                </div>
              </TabsContent>
              <TabsContent value="condiciones" className="space-y-4">
                {/* Vigencia */}
                <div>
                  <h4 className="text-sm font-medium text-slate-700 mb-3">Vigencia</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Fecha Inicio *</Label>
                      <FechaARInput
                        value={contratoForm.fecha_inicio}
                        onChange={(newFechaInicio) => {
                          setContratoForm(prev => ({
                            ...prev,
                            fecha_inicio: newFechaInicio,
                            fecha_fin: prev.fecha_fin && newFechaInicio && prev.fecha_fin < newFechaInicio ? '' : prev.fecha_fin
                          }));
                        }}
                        required
                        testId="te-form-fecha-inicio-ar"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Fecha Fin *</Label>
                      <FechaARInput
                        value={contratoForm.fecha_fin}
                        onChange={(v) => setContratoForm({...contratoForm, fecha_fin: v})}
                        min={contratoForm.fecha_inicio || undefined}
                        required
                        testId="te-form-fecha-fin-ar"
                      />
                      {contratoForm.fecha_inicio && (
                        <p className="text-xs text-slate-400">Debe ser posterior a {formatDate(contratoForm.fecha_inicio)}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Montos */}
                <div className="border-t pt-4">
                  <h4 className="text-sm font-medium text-slate-700 mb-1">Montos</h4>
                  <p className="text-xs text-slate-400 mb-3">Todos los valores son netos (sin IVA)</p>
                  <div className="grid grid-cols-4 gap-3 mb-4">
                    <div className="space-y-2">
                      <Label className="text-xs">Monto Inicio Contrato *</Label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">
                          {contratoForm.moneda === 'USD' ? 'US$' : '$'}
                        </span>
                        <Input 
                          type="text" 
                          value={formatMoneyInput(contratoForm.monto_inicial)}
                          onChange={(e) => setContratoForm({...contratoForm, monto_inicial: parseMoneyInput(e.target.value)})}
                          placeholder="0"
                          className="pl-10 text-right"
                          required
                        />
                      </div>
                      <p className="text-xs text-slate-400">Primer monto del contrato</p>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">Monto Sugerido por Sistema</Label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">
                          {contratoForm.moneda === 'USD' ? 'US$' : '$'}
                        </span>
                        <Input 
                          type="text" 
                          value={montoActHoyNuevo && !montoActHoyNuevo.error && montoActHoyNuevo.monto_actualizado != null ? formatMoneyInput(montoActHoyNuevo.monto_actualizado) : '-'}
                          disabled
                          className="pl-10 text-right bg-slate-100 text-slate-700 font-medium"
                          data-testid="monto-sugerido-sistema-nuevo"
                        />
                      </div>
                      <p className={`text-xs ${montoActHoyNuevo?.error ? 'text-red-600' : 'text-slate-400'}`} data-testid="monto-sugerido-explicacion-nuevo" title={montoActHoyNuevo?.explicacion || ''}>
                        {montoActHoyNuevo?.error
                          ? `⚠ ${montoActHoyNuevo.error}`
                          : montoActHoyNuevo?.explicacion
                            ? `Calculado por sistema · corte ${formatDate(montoActHoyNuevo.fecha_corte)}`
                            : 'Faltan datos para calcular'}
                      </p>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label className="text-xs">Monto Confirmado Manualmente</Label>
                        {montoActHoyNuevo && !montoActHoyNuevo.error && montoActHoyNuevo.monto_actualizado != null && (
                          <button
                            type="button"
                            className="text-[11px] text-violet-700 hover:underline"
                            onClick={() => setContratoForm({...contratoForm, monto_actual: montoActHoyNuevo.monto_actualizado, fecha_carga_monto: new Date().toISOString().slice(0,10)})}
                            data-testid="usar-sugerido-nuevo"
                          >Usar sugerido</button>
                        )}
                      </div>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">
                          {contratoForm.moneda === 'USD' ? 'US$' : '$'}
                        </span>
                        <Input 
                          type="text" 
                          value={formatMoneyInput(contratoForm.monto_actual)}
                          onChange={(e) => setContratoForm({...contratoForm, monto_actual: parseMoneyInput(e.target.value), fecha_carga_monto: new Date().toISOString().slice(0,10)})}
                          placeholder="Manual"
                          className="pl-10 text-right"
                          data-testid="monto-confirmado-manual-nuevo"
                        />
                      </div>
                      <p className="text-xs text-slate-400">Valor final revisado · pasa a facturación</p>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">Moneda *</Label>
                      <Select value={contratoForm.moneda} onValueChange={(v) => setContratoForm({...contratoForm, moneda: v})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="ARS">ARS</SelectItem>
                          <SelectItem value="USD">USD</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  {/* Ajustes — Multi-índice + Valor referencia USD (Jun 2026) */}
                  <div className="bg-slate-50 rounded-lg p-3 space-y-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        className="rounded border-slate-300"
                        checked={contratoForm.indice_tipo === 'sin_ajuste'}
                        onChange={(e) => setContratoForm({
                          ...contratoForm,
                          indice_tipo: e.target.checked ? 'sin_ajuste' : 'ipc',
                          ajuste_indices: e.target.checked ? [{ tipo: 'sin_ajuste', peso: 100 }] : null,
                        })}
                        data-testid="sin-ajuste-create-check"
                      />
                      <span className="text-xs font-medium text-slate-700">Sin ajuste (contrato no se actualiza por índice — ej. USD)</span>
                    </label>
                    {contratoForm.indice_tipo === 'sin_ajuste' ? (
                      <p className="text-xs text-slate-500">Este contrato no aplica IPC/CAC/Otro. El monto operativo será el Monto Confirmado Manualmente o el monto base vigente.</p>
                    ) : (
                    <>
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-medium text-slate-600">Ajustes — Índices</p>
                      <Button type="button" size="sm" variant="outline" className="h-7 text-xs" onClick={() => {
                        const arr = [...(contratoForm.ajuste_indices || [])];
                        arr.push({ tipo: 'IPC', peso: 0 });
                        setContratoForm({ ...contratoForm, ajuste_indices: arr });
                      }} data-testid="add-indice-create-btn">+ Agregar índice</Button>
                    </div>
                    {(() => {
                      const indices = contratoForm.ajuste_indices && contratoForm.ajuste_indices.length > 0
                        ? contratoForm.ajuste_indices
                        : [{ tipo: 'IPC', peso: 100 }];
                      const sumaPesos = indices.reduce((s, i) => s + (parseFloat(i.peso) || 0), 0);
                      const ok = Math.abs(sumaPesos - 100) < 0.01;
                      return (
                        <>
                          {indices.map((idx, i) => (
                            <div key={i} className="flex items-center gap-2" data-testid={`indice-create-row-${i}`}>
                              <Select
                                value={(idx.tipo || 'IPC').toUpperCase()}
                                onValueChange={(v) => {
                                  const arr = [...indices];
                                  arr[i] = { ...arr[i], tipo: v };
                                  setContratoForm({ ...contratoForm, ajuste_indices: arr });
                                }}
                              >
                                <SelectTrigger className="h-9 flex-1" data-testid={`indice-create-tipo-${i}`}><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="IPC">IPC (INDEC)</SelectItem>
                                  <SelectItem value="CAC">CAC</SelectItem>
                                  <SelectItem value="ICL">ICL</SelectItem>
                                  <SelectItem value="OTRO">Otro</SelectItem>
                                </SelectContent>
                              </Select>
                              <div className="flex items-center gap-1">
                                <Input type="number" min="0" max="100" step="0.01"
                                  value={idx.peso ?? ''}
                                  onChange={(e) => {
                                    const arr = [...indices];
                                    arr[i] = { ...arr[i], peso: parseFloat(e.target.value) || 0 };
                                    setContratoForm({ ...contratoForm, ajuste_indices: arr });
                                  }}
                                  className="h-9 w-20 text-right"
                                  data-testid={`indice-create-peso-${i}`}
                                  placeholder="0"
                                />
                                <span className="text-xs text-slate-500">%</span>
                              </div>
                              {indices.length > 1 && (
                                <Button type="button" size="sm" variant="ghost" className="h-9 text-rose-600 px-2"
                                  onClick={() => {
                                    const arr = indices.filter((_, j) => j !== i);
                                    setContratoForm({ ...contratoForm, ajuste_indices: arr });
                                  }}
                                  data-testid={`indice-create-remove-${i}`}
                                >✕</Button>
                              )}
                            </div>
                          ))}
                          <div className={`text-xs ${ok ? 'text-emerald-600' : 'text-rose-600'} font-medium`} data-testid="suma-pesos-create">
                            Suma: {sumaPesos.toFixed(2)}% {ok ? '✓' : '· debe ser 100%'}
                          </div>
                        </>
                      );
                    })()}
                    <div className="grid grid-cols-2 gap-4 pt-2 border-t">
                      <div className="space-y-2">
                        <Label className="text-xs">Frecuencia de ajuste</Label>
                        <Select
                          value={contratoForm.ajuste_frecuencia || contratoForm.indice_frecuencia || 'trimestral'}
                          onValueChange={(v) => setContratoForm({ ...contratoForm, ajuste_frecuencia: v, indice_frecuencia: v })}
                        >
                          <SelectTrigger className="h-9" data-testid="ajuste-frecuencia-create"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="mensual">Mensual</SelectItem>
                            <SelectItem value="bimestral">Bimestral</SelectItem>
                            <SelectItem value="trimestral">Trimestral</SelectItem>
                            <SelectItem value="cuatrimestral">Cuatrimestral</SelectItem>
                            <SelectItem value="semestral">Semestral</SelectItem>
                            <SelectItem value="anual">Anual</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs">Valor de referencia (USD)</Label>
                        <Input type="number" min="0" step="0.01"
                          value={contratoForm.valor_referencia?.monto ?? ''}
                          onChange={(e) => {
                            const v = e.target.value;
                            setContratoForm({
                              ...contratoForm,
                              valor_referencia: v === '' ? null : { monto: parseFloat(v) || 0, moneda: 'USD' },
                            });
                          }}
                          className="h-9"
                          placeholder="Ej: 4000"
                          data-testid="valor-referencia-usd-create"
                        />
                        <p className="text-xs text-slate-400">Piso en USD · se multiplica por cotización BNA para obtener el piso ARS.</p>
                      </div>
                    </div>
                    </>
                    )}
                  </div>
                </div>
                
                {/* Condición de Cobro */}
                <div className="border-t pt-4">
                  <h4 className="text-sm font-medium text-slate-700 mb-3">Condición de Cobro</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Día de Emisión</Label>
                      <Input 
                        type="number"
                        min="1"
                        max="28"
                        value={contratoForm.dia_emision} 
                        onChange={(e) => setContratoForm({...contratoForm, dia_emision: e.target.value})}
                        placeholder="Ej: 1"
                      />
                      <p className="text-xs text-slate-400">Día del mes (1-28)</p>
                    </div>
                    <div className="space-y-2">
                      <Label>Día de Vencimiento</Label>
                      <Input 
                        type="number"
                        min="1"
                        max="28"
                        value={contratoForm.dia_vencimiento} 
                        onChange={(e) => setContratoForm({...contratoForm, dia_vencimiento: e.target.value})}
                        placeholder="Ej: 10"
                      />
                      <p className="text-xs text-slate-400">Día del mes (1-28)</p>
                    </div>
                  </div>
                </div>
                
                {/* Penalidad */}
                <div className="border-t pt-4">
                  <h4 className="text-sm font-medium text-slate-700 mb-3">Penalidad</h4>
                  <div className="flex items-center gap-2">
                    <input 
                      type="checkbox"
                      id="penalidad_enabled_new"
                      checked={contratoForm.penalidad_enabled}
                      onChange={(e) => setContratoForm({...contratoForm, penalidad_enabled: e.target.checked})}
                      className="rounded border-slate-300"
                    />
                    <Label htmlFor="penalidad_enabled_new" className="cursor-pointer text-sm">
                      1% diaria sobre el monto adeudado
                    </Label>
                  </div>
                </div>

                {/* Cocheras: valoración por cochera (al final; se oculta si no hay cocheras) */}
                <CocherasModoSection
                  unidadIds={multiUnidadMode
                    ? seleccionMultiUnidad.flatMap(s => s.kind === 'unidad' ? [s.id] : (s.miembros || []).map(m => m.unidad_id))
                    : (selectedUnidad ? [selectedUnidad.unidad_id] : [])}
                  unidadesInfo={unidades || []}
                  valuacion={contratoForm.cocheras_valuacion || {}}
                  monedaContrato={contratoForm.moneda || 'ARS'}
                  onChange={(mapa) => setContratoForm(prev => ({ ...prev, cocheras_valuacion: mapa }))}
                  disabled={false}
                />
              </TabsContent>


              {/* Tab: Fiscal */}
              <TabsContent value="fiscal" className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>CUIT</Label>
                    <Input 
                      value={contratoForm.cuit} 
                      onChange={(e) => {
                        // Auto-formatear CUIT: XX-XXXXXXXX-X
                        let value = e.target.value.replace(/\D/g, '');
                        if (value.length > 11) value = value.slice(0, 11);
                        
                        let formatted = '';
                        if (value.length > 0) formatted = value.slice(0, 2);
                        if (value.length > 2) formatted += '-' + value.slice(2, 10);
                        if (value.length > 10) formatted += '-' + value.slice(10, 11);
                        
                        setContratoForm({...contratoForm, cuit: formatted});
                      }}
                      placeholder="20-12345678-9"
                      maxLength={13}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Condición IVA</Label>
                    <Select 
                      value={contratoForm.condicion_iva} 
                      onValueChange={(v) => setContratoForm({...contratoForm, condicion_iva: v})}
                    >
                      <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="responsable_inscripto">Resp. Insc.</SelectItem>
                        <SelectItem value="exento">Exento</SelectItem>
                        <SelectItem value="consumidor_final">Consumidor Final</SelectItem>
                        <SelectItem value="monotributista">Monotributista</SelectItem>
                        <SelectItem value="del_exterior">Del Exterior</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Domicilio Fiscal</Label>
                  <Input 
                    value={contratoForm.domicilio_fiscal} 
                    onChange={(e) => setContratoForm({...contratoForm, domicilio_fiscal: e.target.value})}
                    placeholder="Dirección completa"
                  />
                </div>
                {/* Orden de Compra (reorg: ANTES de Envío de factura) */}
                <div className="space-y-2 pt-3 border-t border-slate-200" data-testid="nuevo-orden-compra">
                  <Label>Orden de Compra</Label>
                  <Select
                    value={contratoForm.orden_compra_tipo || 'sin_oc'}
                    onValueChange={(v) => setContratoForm({...contratoForm, orden_compra_tipo: v === 'sin_oc' ? '' : v, orden_compra: v === 'sin_oc' ? '' : contratoForm.orden_compra})}
                  >
                    <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sin_oc">Sin OC</SelectItem>
                      <SelectItem value="anual">OC Anual</SelectItem>
                      <SelectItem value="mensual">OC Mensual</SelectItem>
                    </SelectContent>
                  </Select>
                  {contratoForm.orden_compra_tipo === 'anual' && (
                    <div className="space-y-1">
                      <Label className="text-xs">Número de OC</Label>
                      <Input
                        value={contratoForm.orden_compra}
                        onChange={(e) => setContratoForm({...contratoForm, orden_compra: e.target.value})}
                        placeholder="Ej: OC-2026-001"
                      />
                    </div>
                  )}
                  {contratoForm.orden_compra_tipo === 'mensual' && (
                    <p className="text-xs text-slate-500 bg-slate-50 p-2 rounded">La OC mensual se carga en la pestaña 5. Factura al generar cada mes.</p>
                  )}
                  <div className="space-y-1">
                    <Label className="text-xs">Instrucción</Label>
                    <Textarea
                      value={contratoForm.orden_compra_instruccion || ''}
                      onChange={(e) => setContratoForm({...contratoForm, orden_compra_instruccion: e.target.value})}
                      placeholder="Dónde cargar la OC, cuándo pedirla, si se carga todos los meses, si la envía el inquilino, si hay que adjuntarla a la factura..."
                      rows={2}
                      data-testid="nuevo-orden-compra-instruccion"
                    />
                  </div>
                </div>
                {/* ── Envío de factura (reorg) ── */}
                <div className="pt-3 border-t border-slate-200 space-y-3" data-testid="nuevo-envio-factura">
                  <Label className="text-sm font-medium">Envío de factura</Label>
                  <div className="space-y-1">
                    <Label className="text-xs">¿Cómo se gestiona la factura?</Label>
                    <Select
                      value={contratoForm.facturacion_metodo || 'email'}
                      onValueChange={(v) => setContratoForm({...contratoForm, facturacion_metodo: v})}
                    >
                      <SelectTrigger data-testid="nuevo-facturacion-metodo"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="portal">Se carga en portal / plataforma</SelectItem>
                        <SelectItem value="email">Se manda por email</SelectItem>
                        <SelectItem value="ambos">Ambas</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-400">Solo registra la forma de gestión. El sistema no envía emails automáticamente.</p>
                  </div>
                  {(() => {
                    const metodo = contratoForm.facturacion_metodo || 'email';
                    const showEmail = metodo === 'email' || metodo === 'ambos';
                    const showPortal = metodo === 'portal' || metodo === 'ambos';
                    const mismo = contratoForm.facturacion_mismo_admin !== false;
                    const cls = mismo ? 'bg-slate-50' : '';
                    const valNombre = mismo ? (contratoForm.contacto_admin_nombre || '') : (contratoForm.facturacion_contacto_nombre || '');
                    const valApellido = mismo ? '' : (contratoForm.facturacion_contacto_apellido || '');
                    const valTel = mismo ? (contratoForm.contacto_admin_telefono || '') : (contratoForm.facturacion_contacto_telefono || '');
                    return (
                      <>
                        <div className="flex items-center justify-between">
                          <Label className="text-xs font-medium text-slate-600">Contacto de facturación</Label>
                          <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={mismo}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                setContratoForm(f => ({
                                  ...f,
                                  facturacion_mismo_admin: checked,
                                  ...(checked ? {
                                    facturacion_contacto_nombre: f.contacto_admin_nombre || '',
                                    facturacion_contacto_apellido: '',
                                    facturacion_contacto_telefono: f.contacto_admin_telefono || '',
                                    facturacion_contacto_email: f.contacto_admin_email || '',
                                    emails_facturacion: (() => {
                                      const adminEmail = f.contacto_admin_email || '';
                                      const existing = (f.emails_facturacion && f.emails_facturacion.length) ? f.emails_facturacion : (f.email_facturacion ? [f.email_facturacion] : []);
                                      return adminEmail ? [adminEmail, ...existing.slice(1)] : existing;
                                    })(),
                                  } : {}),
                                }));
                              }}
                              data-testid="nuevo-facturacion-mismo-admin"
                            />
                            Mismo que el contacto administrativo
                          </label>
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                          <div className="space-y-1">
                            <Label className="text-xs">Nombre</Label>
                            <Input value={valNombre} onChange={(e) => setContratoForm({...contratoForm, facturacion_contacto_nombre: e.target.value})} placeholder="Nombre" disabled={mismo} className={cls} data-testid="nuevo-facturacion-nombre" />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Apellido</Label>
                            <Input value={valApellido} onChange={(e) => setContratoForm({...contratoForm, facturacion_contacto_apellido: e.target.value})} placeholder="Apellido" disabled={mismo} className={cls} data-testid="nuevo-facturacion-apellido" />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Teléfono</Label>
                            <Input type="tel" value={valTel} onChange={(e) => setContratoForm({...contratoForm, facturacion_contacto_telefono: e.target.value})} placeholder="+54 261 123-4567" disabled={mismo} className={cls} data-testid="nuevo-facturacion-telefono" />
                          </div>
                        </div>
                        {showEmail && (
                          <EmailsFacturacionList
                            emails={(contratoForm.emails_facturacion && contratoForm.emails_facturacion.length) ? contratoForm.emails_facturacion : (contratoForm.email_facturacion ? [contratoForm.email_facturacion] : [''])}
                            onChange={(next) => setContratoForm(f => ({ ...f, emails_facturacion: next, email_facturacion: (next.find(x => (x || '').trim()) || '').trim(), email_facturacion_default: false }))}
                            testidPrefix="nuevo-facturacion"
                          />
                        )}
                        {showPortal && (
                          <div className="pt-2 border-t border-slate-100 space-y-2" data-testid="nuevo-proveedor-plataforma">
                            <Label className="text-xs font-medium text-slate-600">Datos de portal / plataforma</Label>
                            <p className="text-xs text-slate-400 -mt-1">Datos de acceso e instrucciones para cargar facturas en el portal del inquilino. Solo se guardan, no se envían ni automatizan.</p>
                            <div className="grid grid-cols-2 gap-3">
                              <div className="space-y-1">
                                <Label className="text-xs">URL / Portal</Label>
                                <Input value={contratoForm.proveedor_plataforma_url || ''} onChange={(e) => setContratoForm({...contratoForm, proveedor_plataforma_url: e.target.value})} placeholder="https://..." data-testid="nuevo-proveedor-url" />
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs">Usuario</Label>
                                <Input value={contratoForm.proveedor_plataforma_usuario || ''} onChange={(e) => setContratoForm({...contratoForm, proveedor_plataforma_usuario: e.target.value})} placeholder="usuario" data-testid="nuevo-proveedor-usuario" />
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs">Contraseña</Label>
                                <div className="relative">
                                  <Input
                                    type={showProvPassNuevo ? 'text' : 'password'}
                                    value={contratoForm.proveedor_plataforma_password || ''}
                                    onChange={(e) => setContratoForm({...contratoForm, proveedor_plataforma_password: e.target.value})}
                                    placeholder="••••••••"
                                    className="pr-9"
                                    data-testid="nuevo-proveedor-password"
                                  />
                                  <button type="button" onClick={() => setShowProvPassNuevo(v => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" data-testid="nuevo-proveedor-password-toggle" tabIndex={-1}>
                                    {showProvPassNuevo ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                  </button>
                                </div>
                              </div>
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">Instrucciones</Label>
                              <Textarea value={contratoForm.proveedor_plataforma_instrucciones || ''} onChange={(e) => setContratoForm({...contratoForm, proveedor_plataforma_instrucciones: e.target.value})} placeholder="Dónde cargar la factura, pasos internos, si requiere OC, documentación a adjuntar, comentarios operativos..." rows={3} data-testid="nuevo-proveedor-instrucciones" />
                            </div>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>

                {/* Retenciones (reorg: al final) */}
                <div className="grid grid-cols-2 gap-4 pt-3 border-t border-slate-200">
                  <div className="space-y-2">
                    <Label>Retenciones</Label>
                    <Select
                      value={contratoForm.retencion_tipo || 'sin_retenciones'}
                      onValueChange={(v) => setContratoForm({
                        ...contratoForm,
                        retencion_tipo: v,
                        retencion_porcentaje: v === 'otro' ? (contratoForm.retencion_porcentaje || '') : null,
                      })}
                    >
                      <SelectTrigger data-testid="select-retencion-tipo"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sin_retenciones">Sin retenciones</SelectItem>
                        <SelectItem value="2">2%</SelectItem>
                        <SelectItem value="4">4%</SelectItem>
                        <SelectItem value="6">6%</SelectItem>
                        <SelectItem value="otro">Otro</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-400">Se aplica sobre el monto neto de la factura mensual.</p>
                  </div>
                  {contratoForm.retencion_tipo === 'otro' && (
                    <div className="space-y-2">
                      <Label>Porcentaje</Label>
                      <Input
                        type="number"
                        step="0.1"
                        min="0"
                        max="100"
                        value={contratoForm.retencion_porcentaje ?? ''}
                        onChange={(e) => setContratoForm({ ...contratoForm, retencion_porcentaje: e.target.value === '' ? null : parseFloat(e.target.value) })}
                        placeholder="Ej: 3.5"
                        data-testid="input-retencion-porcentaje"
                      />
                      <p className="text-xs text-slate-400">Ej: 3.5 = 3,5%</p>
                    </div>
                  )}
                </div>
              </TabsContent>

              {/* Tab: Veraz */}
              <TabsContent value="veraz" className="space-y-4">
                <VerazSection
                  reservaId={reservaParaContrato}
                  userRol={user?.rol}
                  disabled={false}
                  defaultInquilinoNombre={contratoForm.nombre_comercial || contratoForm.razon_social || ''}
                />
              </TabsContent>

              {/* Tab: Colppy */}
              <TabsContent value="colppy" className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Nro. Factura</Label>
                    <Select
                      value={contratoForm.colppy_nro_factura || '004'}
                      onValueChange={(v) => setContratoForm({...contratoForm, colppy_nro_factura: v})}
                    >
                      <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="004">004</SelectItem>
                        <SelectItem value="003">003</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {(contratoForm.colppy_nro_factura || '004') === '004' && (
                    <div className="space-y-2">
                      <Label>&nbsp;</Label>
                      <div className="flex items-center h-10 px-3 border rounded-md bg-green-50 border-green-200">
                        <Check className="w-4 h-4 text-green-600 mr-2" />
                        <span className="text-sm text-green-700 font-medium">Factura Electrónica</span>
                      </div>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Condición de Venta</Label>
                    <Select 
                      value={contratoForm.colppy_condicion_venta} 
                      onValueChange={(v) => setContratoForm({...contratoForm, colppy_condicion_venta: v})}
                    >
                      <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cuenta_corriente">Cuenta Corriente</SelectItem>
                        <SelectItem value="contado">Contado</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Tipo Comprobante por Tipo Cliente</Label>
                    <Select 
                      value={contratoForm.colppy_tipo_comprobante} 
                      onValueChange={(v) => setContratoForm({...contratoForm, colppy_tipo_comprobante: v})}
                    >
                      <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Fa">Fa</SelectItem>
                        <SelectItem value="Nd">Nd</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Tipo de Factura</Label>
                    <Select 
                      value={contratoForm.colppy_tipo_factura} 
                      onValueChange={(v) => setContratoForm({...contratoForm, colppy_tipo_factura: v})}
                    >
                      <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="A">A</SelectItem>
                        <SelectItem value="B">B</SelectItem>
                        <SelectItem value="E">E</SelectItem>
                        <SelectItem value="Z">Z</SelectItem>
                        <SelectItem value="I">I</SelectItem>
                        <SelectItem value="M">M</SelectItem>
                        <SelectItem value="X">X</SelectItem>
                        <SelectItem value="T">T</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Tipo de Operación</Label>
                    <Select 
                      value={contratoForm.colppy_tipo_operacion} 
                      onValueChange={(v) => setContratoForm({...contratoForm, colppy_tipo_operacion: v})}
                    >
                      <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="servicios">Venta de cosas Muebles, Obras, Locaciones y/o Prestaciones de Servicios</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Descripción</Label>
                  <Input 
                    value={contratoForm.colppy_descripcion || `Servicios de gestión de ${selectedUnidad?.tipo || 'unidad'}`}
                    onChange={(e) => setContratoForm({...contratoForm, colppy_descripcion: e.target.value})}
                    placeholder="Servicios de gestión de oficina"
                  />
                  <p className="text-xs text-slate-400">Se autocompleta según el tipo de unidad</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Cuenta</Label>
                    <Select 
                      value={contratoForm.colppy_cuenta} 
                      onValueChange={(v) => setContratoForm({...contratoForm, colppy_cuenta: v})}
                    >
                      <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ingresos_oficinas">Ingresos por alquiler Oficinas</SelectItem>
                        <SelectItem value="ingresos_coworking">Ingresos por Coworking</SelectItem>
                        <SelectItem value="ingresos_cocheras">Ingresos por Cocheras</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Moneda</Label>
                    <Select 
                      value={contratoForm.colppy_moneda} 
                      onValueChange={(v) => setContratoForm({...contratoForm, colppy_moneda: v})}
                    >
                      <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ARS">Pesos Argentinos</SelectItem>
                        <SelectItem value="USD">Dólar Estadounidense</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <input 
                      type="checkbox" 
                      id="percepciones"
                      checked={contratoForm.colppy_percepciones}
                      onChange={(e) => setContratoForm({...contratoForm, colppy_percepciones: e.target.checked})}
                      className="rounded border-slate-300"
                    />
                    <Label htmlFor="percepciones" className="cursor-pointer">Lleva Percepciones</Label>
                  </div>
                </div>
              </TabsContent>

              {/* Tab: Comisión (solo comercial) */}
              {isComercial && (
                <TabsContent value="comision" className="space-y-4">
                  <div>
                    <h4 className="text-sm font-medium text-slate-700 mb-1">Comisión del comercial</h4>
                    <p className="text-xs text-slate-400 mb-4">Se genera automáticamente al firmar el contrato. Default 50% del primer mes de alquiler.</p>
                    <div className="flex items-center gap-3 flex-wrap">
                      <Label className="text-sm text-slate-600 whitespace-nowrap">% sobre 1er mes</Label>
                      <div className="relative">
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          step="0.01"
                          value={contratoForm.porcentaje_comision}
                          onChange={(e) => setContratoForm({...contratoForm, porcentaje_comision: e.target.value})}
                          className="pr-8 w-24 text-right"
                          data-testid="input-porcentaje-comision"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">%</span>
                      </div>
                      <span className="text-sm text-slate-500">=</span>
                      <Label className="text-sm text-slate-600 whitespace-nowrap">Tu comisión</Label>
                      <div className="h-10 flex items-center px-3 border rounded-md bg-emerald-50 border-emerald-200 min-w-[160px]">
                        <span className="text-sm font-semibold text-emerald-800">
                          {contratoForm.moneda === 'USD' ? 'US$' : '$'} {new Intl.NumberFormat('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(
                            Math.round((parseFloat(contratoForm.monto_inicial || 0) * parseFloat(contratoForm.porcentaje_comision || 0) / 100) * 100) / 100
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                </TabsContent>
              )}
            </Tabs>
            
            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setIsContratoDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={saving}>{saving ? 'Guardando...' : 'Crear Contrato'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Deposito Dialog */}
      <Dialog open={isDepositoDialogOpen} onOpenChange={setIsDepositoDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wallet className="w-5 h-5 text-green-600" />
              Registrar Depósito en Garantía
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveDeposito} className="space-y-4">
            {/* Información del contrato (4 columnas: título arriba, valor abajo) */}
            {selectedContrato && selectedUnidad && (
              <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                <div className="grid grid-cols-4 gap-4 text-center">
                  <div>
                    <p className="text-xs text-slate-500">Unidad</p>
                    <p className="font-medium text-slate-900">{selectedUnidad.nombre_unidad}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Contrato</p>
                    <p className="font-mono text-slate-900">{selectedContrato.numero_contrato}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Inquilino</p>
                    <p className="font-medium text-slate-900">{selectedContrato.inquilino}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Alquiler</p>
                    <p className="font-semibold text-slate-900">{formatCurrency(selectedContrato.monto_actual || selectedContrato.monto_inicial, selectedContrato.moneda)}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Opción al inicio: contrato viejo sin garantía registrada */}
            <label className="flex items-start gap-2 p-3 rounded-lg border border-slate-200 bg-slate-50 cursor-pointer" data-testid="garantia-no-disponible-wrap">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={garantiaNoDisponible}
                onChange={(e) => setGarantiaNoDisponible(e.target.checked)}
                data-testid="garantia-no-disponible-check"
              />
              <span className="text-sm">
                <span className="font-medium text-slate-800">No tenemos la garantía / No la tenemos</span>
                <span className="block text-xs text-slate-500">Marcá esta opción para contratos viejos donde no contamos con la documentación o el dato de la garantía. No se exigirán los campos de carga.</span>
              </span>
            </label>

            {!garantiaNoDisponible && (
            <>
            {/* Tenedor de la Garantía */}
            <div className="space-y-2">
              <Label>Tenedor de la Garantía *</Label>
              <Select value={tenedorGarantia} onValueChange={setTenedorGarantia}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="propietario">Propietario</SelectItem>
                  <SelectItem value="administrador">Administrador</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Tipo de Garantía */}
            <div className="space-y-2">
              <Label>Tipo de Garantía *</Label>
              <Select 
                value={depositoForm.tipo_deposito} 
                onValueChange={(v) => setDepositoForm({...depositoForm, tipo_deposito: v, medio_cobro: v === 'dinero' ? 'transferencia' : ''})}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="dinero">Dinero</SelectItem>
                  <SelectItem value="pagare">Pagaré</SelectItem>
                  <SelectItem value="cheque">Cheque</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Campos comunes: Fecha, Monto, Moneda */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Fecha *</Label>
                <Input 
                  type="date" 
                  value={depositoForm.fecha_cobro} 
                  onChange={(e) => setDepositoForm({...depositoForm, fecha_cobro: e.target.value})}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Monto *</Label>
                <Input 
                  type="text" 
                  inputMode="numeric"
                  value={formatMoneyInput(depositoForm.monto)} 
                  onChange={(e) => setDepositoForm({...depositoForm, monto: parseMoneyInput(e.target.value)})}
                  placeholder="0"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Moneda *</Label>
                <Select 
                  value={depositoForm.moneda} 
                  onValueChange={(v) => setDepositoForm({...depositoForm, moneda: v})}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ARS">ARS</SelectItem>
                    <SelectItem value="USD">USD</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Campos específicos para DINERO */}
            {depositoForm.tipo_deposito === 'dinero' && (
              <div className="space-y-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div className="space-y-2">
                  <Label>Medio de Cobro *</Label>
                  <Select value={depositoForm.medio_cobro} onValueChange={(v) => setDepositoForm({...depositoForm, medio_cobro: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="transferencia">Transferencia</SelectItem>
                      <SelectItem value="deposito">Depósito Bancario</SelectItem>
                      <SelectItem value="efectivo">Efectivo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {(depositoForm.medio_cobro === 'transferencia' || depositoForm.medio_cobro === 'deposito') && (
                  <div className="space-y-2">
                    <Label>Cuenta de Banco</Label>
                    <Input 
                      value={depositoForm.cuenta_destino} 
                      onChange={(e) => setDepositoForm({...depositoForm, cuenta_destino: e.target.value})}
                      placeholder="Banco / CBU / Alias"
                    />
                  </div>
                )}
              </div>
            )}
            
            {/* Campos específicos para PAGARÉ o CHEQUE */}
            {(depositoForm.tipo_deposito === 'pagare' || depositoForm.tipo_deposito === 'cheque') && (
              <div className="space-y-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                <div className="space-y-2">
                  <Label>Adjuntar Documento</Label>
                  <div className="flex items-center gap-2">
                    <Input 
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => setDepositoForm({...depositoForm, documento: e.target.files[0]})}
                      className="text-sm"
                    />
                  </div>
                  <p className="text-xs text-slate-500">
                    Formatos: PDF, JPG, PNG
                  </p>
                </div>
                {depositoForm.documento && (
                  <p className="text-sm text-green-600 flex items-center gap-1">
                    <Check className="w-4 h-4" />
                    {depositoForm.documento.name}
                  </p>
                )}
              </div>
            )}
            </>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDepositoDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={saving}>{saving ? 'Guardando...' : (garantiaNoDisponible ? 'Guardar' : 'Registrar Depósito')}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Detalle Depósito Dialog - Con modo Ver/Editar */}
      <Dialog open={isDetalleDepositoOpen} onOpenChange={(open) => { setIsDetalleDepositoOpen(open); if (!open) setIsEditGarantiaMode(false); }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wallet className="w-5 h-5 text-blue-600" />
              {isEditGarantiaMode ? 'Editar Garantía' : 'Ver Garantía'}
            </DialogTitle>
          </DialogHeader>
          {selectedDeposito && (
            <form onSubmit={handleSaveEditGarantia}>
              <div className="space-y-4">
                {/* Info básica (siempre solo lectura) */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-slate-500">Unidad</p>
                    <p className="font-medium text-slate-900">
                      {unidades.find(u => u.unidad_id === selectedDeposito.unidad_id)?.nombre_unidad || '-'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Contrato</p>
                    <p className="font-mono text-slate-900">
                      {contratos.find(c => c.contrato_id === selectedDeposito.contrato_id)?.numero_contrato || '-'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Inquilino</p>
                    <p className="text-slate-900">
                      {contratos.find(c => c.contrato_id === selectedDeposito.contrato_id)?.inquilino || '-'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Estado</p>
                    <span className={cn("px-2 py-0.5 rounded-full text-xs", estadosDeposito[selectedDeposito.estado]?.color)}>
                      {estadosDeposito[selectedDeposito.estado]?.label}
                    </span>
                  </div>
                </div>
                
                <div className="border-t pt-4">
                  <h4 className="text-sm font-semibold text-slate-700 mb-3">Información del Depósito</h4>
                  
                  {/* Modo Lectura */}
                  {!isEditGarantiaMode ? (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-slate-500">Tipo de Garantía</p>
                        <p className="font-medium text-slate-900 capitalize">{selectedDeposito.tipo_deposito}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Monto</p>
                        <p className="font-semibold text-slate-900">
                          {formatCurrency(selectedDeposito.monto, selectedDeposito.moneda)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Fecha de Cobro</p>
                        <p className="text-slate-900">{formatDate(selectedDeposito.fecha_cobro)}</p>
                      </div>
                      {selectedDeposito.tipo_deposito === 'dinero' && (
                        <>
                          <div>
                            <p className="text-xs text-slate-500">Medio de Cobro</p>
                            <p className="text-slate-900 capitalize">{selectedDeposito.medio_cobro || '-'}</p>
                          </div>
                          {selectedDeposito.cuenta_destino && (
                            <div className="col-span-2">
                              <p className="text-xs text-slate-500">Cuenta Destino</p>
                              <p className="text-slate-900">{selectedDeposito.cuenta_destino}</p>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  ) : (
                    /* Modo Edición */
                    <div className="space-y-4">
                      {/* Tipo de Garantía */}
                      <div className="space-y-2">
                        <Label>Tipo de Garantía *</Label>
                        <Select value={editGarantiaForm.tipo_deposito} onValueChange={(v) => setEditGarantiaForm({...editGarantiaForm, tipo_deposito: v, medio_cobro: v === 'dinero' ? 'transferencia' : ''})}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="dinero">Dinero</SelectItem>
                            <SelectItem value="pagare">Pagaré</SelectItem>
                            <SelectItem value="cheque">Cheque</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {/* Campos comunes: Fecha, Monto, Moneda */}
                      <div className="grid grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label>Fecha *</Label>
                          <Input 
                            type="date" 
                            value={editGarantiaForm.fecha_cobro} 
                            onChange={(e) => setEditGarantiaForm({...editGarantiaForm, fecha_cobro: e.target.value})}
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Monto *</Label>
                          <Input 
                            type="text" 
                            inputMode="numeric"
                            value={formatMoneyInput(editGarantiaForm.monto)} 
                            onChange={(e) => setEditGarantiaForm({...editGarantiaForm, monto: parseMoneyInput(e.target.value)})}
                            placeholder="0"
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Moneda *</Label>
                          <Select value={editGarantiaForm.moneda} onValueChange={(v) => setEditGarantiaForm({...editGarantiaForm, moneda: v})}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="ARS">ARS</SelectItem>
                              <SelectItem value="USD">USD</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      
                      {/* Campos específicos para DINERO */}
                      {editGarantiaForm.tipo_deposito === 'dinero' && (
                        <div className="space-y-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                          <div className="space-y-2">
                            <Label>Medio de Cobro *</Label>
                            <Select value={editGarantiaForm.medio_cobro} onValueChange={(v) => setEditGarantiaForm({...editGarantiaForm, medio_cobro: v})}>
                              <SelectTrigger><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="transferencia">Transferencia</SelectItem>
                                <SelectItem value="deposito">Depósito Bancario</SelectItem>
                                <SelectItem value="efectivo">Efectivo</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          
                          {(editGarantiaForm.medio_cobro === 'transferencia' || editGarantiaForm.medio_cobro === 'deposito') && (
                            <div className="space-y-2">
                              <Label>Cuenta de Banco</Label>
                              <Input 
                                value={editGarantiaForm.cuenta_destino} 
                                onChange={(e) => setEditGarantiaForm({...editGarantiaForm, cuenta_destino: e.target.value})}
                                placeholder="Banco / CBU / Alias"
                              />
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                {/* Adjuntos */}
                {selectedDeposito.documentos && selectedDeposito.documentos.length > 0 && (
                  <div className="border-t pt-4">
                    <h4 className="text-sm font-semibold text-slate-700 mb-3">Documentos Adjuntos</h4>
                    <div className="space-y-2">
                      {selectedDeposito.documentos.map((doc, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                          <span className="text-sm text-slate-700">{doc.nombre || `Documento ${idx + 1}`}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => window.open(doc.url, '_blank')}
                          >
                            <Download className="w-4 h-4 mr-1" />
                            Descargar
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Info de devolución si aplica */}
                {selectedDeposito.estado === 'devuelto' && (
                  <div className="border-t pt-4">
                    <h4 className="text-sm font-semibold text-slate-700 mb-3">Información de Devolución</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-slate-500">Monto Devuelto</p>
                        <p className="font-semibold text-green-600">
                          {formatCurrency(selectedDeposito.monto_devuelto || 0, selectedDeposito.moneda)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Fecha de Devolución</p>
                        <p className="text-slate-900">{formatDate(selectedDeposito.fecha_devolucion)}</p>
                      </div>
                      {selectedDeposito.comentario_devolucion && (
                        <div className="col-span-2">
                          <p className="text-xs text-slate-500">Comentario</p>
                          <p className="text-slate-900">{selectedDeposito.comentario_devolucion}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              
              <DialogFooter className="mt-6">
                <div className="flex items-center justify-between w-full">
                  {/* Botón Devolver Garantía - Solo visible en modo edición y si está vigente */}
                  {isEditGarantiaMode && selectedDeposito.estado === 'vigente' && (
                    <Button 
                      type="button" 
                      variant="destructive" 
                      onClick={() => { 
                        handleOpenDevolucion(selectedDeposito); 
                        setIsDetalleDepositoOpen(false);
                      }}
                    >
                      Devolver Garantía
                    </Button>
                  )}
                  <div className={`flex gap-2 ${!isEditGarantiaMode || selectedDeposito.estado !== 'vigente' ? 'ml-auto' : ''}`}>
                    <Button type="button" variant="outline" onClick={() => { setIsDetalleDepositoOpen(false); setIsEditGarantiaMode(false); }}>
                      {isEditGarantiaMode ? 'Cancelar' : 'Cerrar'}
                    </Button>
                    {selectedDeposito.estado === 'vigente' && (
                      isEditGarantiaMode ? (
                        <Button type="submit" disabled={saving}>{saving ? 'Guardando...' : 'Guardar Cambios'}</Button>
                      ) : (
                        <Button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setIsEditGarantiaMode(true); }}>Editar</Button>
                      )
                    )}
                  </div>
                </div>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Devolución Dialog */}
      <Dialog open={isDevolucionDialogOpen} onOpenChange={setIsDevolucionDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Banknote className="w-5 h-5 text-green-600" />
              Registrar Devolución
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveDevolucion} className="space-y-4">
            <div className="bg-slate-50 p-3 rounded-lg text-sm">
              <p className="text-slate-500">
                Monto del depósito: <strong className="text-slate-900">
                  {formatCurrency(selectedDeposito?.monto || 0, selectedDeposito?.moneda || 'ARS')}
                </strong>
              </p>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Monto Devuelto *</Label>
                <Input 
                  type="text" 
                  inputMode="numeric"
                  value={formatMoneyInput(devolucionForm.monto)} 
                  onChange={(e) => setDevolucionForm({...devolucionForm, monto: parseMoneyInput(e.target.value)})}
                  placeholder="0"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Fecha de Devolución *</Label>
                <Input 
                  type="date" 
                  value={devolucionForm.fecha} 
                  onChange={(e) => setDevolucionForm({...devolucionForm, fecha: e.target.value})}
                  required
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Comentario</Label>
              <Textarea 
                value={devolucionForm.comentario}
                onChange={(e) => setDevolucionForm({...devolucionForm, comentario: e.target.value})}
                placeholder="Observaciones sobre la devolución..."
                rows={2}
              />
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDevolucionDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={saving}>{saving ? 'Registrando...' : 'Registrar Devolución'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Finalizar Contrato Alert */}
      <AlertDialog open={isFinalizarContratoOpen} onOpenChange={(open) => { setIsFinalizarContratoOpen(open); if (open) setFechaBajaContrato(new Date().toISOString().slice(0, 10)); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Finalizar contrato?</AlertDialogTitle>
            <AlertDialogDescription>
              Se marcará el contrato <strong>{selectedContrato?.numero_contrato}</strong> como finalizado
              y la unidad quedará vacante. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-2">
            <label htmlFor="fecha-baja-contrato" className="text-sm font-medium text-slate-700 block mb-1">
              Fecha de baja
            </label>
            <input
              id="fecha-baja-contrato"
              type="date"
              data-testid="finalizar-contrato-fecha-baja"
              value={fechaBajaContrato}
              onChange={(e) => setFechaBajaContrato(e.target.value)}
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
            />
            <p className="text-xs text-slate-500 mt-1">
              Desde el mes siguiente a esta fecha no se factura. El mes de la baja se prorratea por días.
            </p>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleFinalizarContrato} disabled={saving || !fechaBajaContrato} data-testid="finalizar-contrato-confirm">
              {saving ? 'Finalizando...' : 'Finalizar'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Eliminar Depósito Alert */}
      <AlertDialog open={isDeleteDepositoOpen} onOpenChange={setIsDeleteDepositoOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar depósito?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará el depósito de <strong>{formatCurrency(selectedDeposito?.monto || 0, selectedDeposito?.moneda || 'ARS')}</strong>.
              Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteDeposito} disabled={saving} className="bg-red-600 hover:bg-red-700">
              {saving ? 'Eliminando...' : 'Eliminar'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Eliminar Contrato Alert */}
      <AlertDialog open={isDeleteContratoOpen} onOpenChange={setIsDeleteContratoOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar contrato?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará el contrato <strong>{selectedContrato?.numero_contrato}</strong> de <strong>{selectedContrato?.inquilino}</strong>.
              Esta acción no se puede deshacer y la unidad quedará como vacante.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteContrato} disabled={saving} className="bg-red-600 hover:bg-red-700">
              {saving ? 'Eliminando...' : 'Eliminar'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Modal Carga Masiva IA */}
      <CargaMasivaDialog
        open={isCargaMasivaOpen}
        onOpenChange={handleCloseCargaMasiva}
        cargaMasivaFiles={cargaMasivaFiles}
        cargaMasivaInputRef={cargaMasivaInputRef}
        resultadosCargaMasiva={resultadosCargaMasiva}
        procesandoCargaMasiva={procesandoCargaMasiva}
        handleCargaMasivaFiles={handleCargaMasivaFiles}
        handleCargaMasivaDrop={handleCargaMasivaDrop}
        handleRemoveCargaMasivaFile={handleRemoveCargaMasivaFile}
        handleProcesarCargaMasiva={handleProcesarCargaMasiva}
        handleCloseCargaMasiva={handleCloseCargaMasiva}
      />

      {/* Modal Borradores Pendientes */}
      <BorradoresDialog
        open={isBorradoresOpen}
        onOpenChange={setIsBorradoresOpen}
        borradores={borradores}
        saving={saving}
        handleEditBorrador={handleEditBorrador}
        handleAprobarBorrador={handleAprobarBorrador}
        handleDescartarBorrador={handleDescartarBorrador}
      />

      {/* Modal Editar Borrador */}
      <EditBorradorDialog
        open={isEditBorradorOpen}
        onOpenChange={setIsEditBorradorOpen}
        unidades={unidades}
        contratos={contratos}
        selectedBorrador={selectedBorrador}
        borradorForm={borradorForm}
        setBorradorForm={setBorradorForm}
        saving={saving}
        handleSaveBorrador={handleSaveBorrador}
      />

      {/* Dialog Editar Contrato */}
      <EditContratoDialog
        open={isEditContratoOpen}
        onOpenChange={(open) => { setIsEditContratoOpen(open); if (!open) { setEditContratoTab('documentos'); setIsEditMode(false); } }}
        editContratoForm={editContratoForm}
        setEditContratoForm={setEditContratoForm}
        editContratoTab={editContratoTab}
        setEditContratoTab={setEditContratoTab}
        isEditMode={isEditMode}
        setIsEditMode={setIsEditMode}
        saving={saving}
        handleSaveEditContrato={handleSaveEditContrato}
        unidades={unidades}
        contratos={contratos}
        userRol={user?.rol}
        onFinalizarContrato={() => {
          setSelectedContrato(editContratoForm);
          setIsFinalizarContratoOpen(true);
          setIsEditContratoOpen(false);
        }}
      />

      {/* Dialog Cargar / Editar Reserva — solo Foresta (entidad propia, multi-unidad) */}
      <ReservaDialog
        open={isReservaDialogOpen}
        onOpenChange={(open) => {
          setIsReservaDialogOpen(open);
          if (!open) { setReservaToEdit(null); setReservaDialogMode('create'); }
        }}
        mode={reservaDialogMode}
        edificioId={edificioId}
        reserva={reservaToEdit}
        unidadesDisponibles={unidades.filter(u =>
          u.en_administracion && u.estado_alquiler !== 'alquilada' && u.estado_alquiler !== 'reservada'
        )}
        onSaved={async () => {
          await fetchData();
          onReloadUnidades && onReloadUnidades();
        }}
      />

      {/* AlertDialog: confirmar eliminar reserva */}
      <AlertDialog open={isDeleteReservaOpen} onOpenChange={setIsDeleteReservaOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar reserva</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Seguro que querés eliminar esta reserva? Las unidades vinculadas volverán a quedar vacantes. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={saving}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDeleteReserva}
              disabled={saving}
              data-testid="confirm-delete-reserva-btn"
              className="bg-red-600 hover:bg-red-700"
            >
              {saving ? 'Eliminando...' : 'Eliminar'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Paso previo: Oficina o Cowork */}
      <Dialog open={coworkChoiceOpen} onOpenChange={setCoworkChoiceOpen}>
        <DialogContent className="max-w-md" data-testid="tipo-contrato-choice-dialog">
          <DialogHeader>
            <DialogTitle>¿Qué tipo de contrato querés crear?</DialogTitle>
            <DialogDescription>Elegí el tipo para continuar.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-2">
            <button
              type="button"
              data-testid="tipo-contrato-oficina-btn"
              className="flex flex-col items-center gap-2 rounded-lg border border-slate-200 hover:border-[#1B4D3E] hover:bg-slate-50 p-5 transition-colors"
              onClick={() => {
                setCoworkChoiceOpen(false);
                if (typeof coworkOficinaAction === 'function') coworkOficinaAction();
              }}
            >
              <Building2 className="w-7 h-7 text-[#1B4D3E]" />
              <span className="text-sm font-medium">Oficina</span>
            </button>
            <button
              type="button"
              data-testid="tipo-contrato-cowork-btn"
              className="flex flex-col items-center gap-2 rounded-lg border border-slate-200 hover:border-[#1B4D3E] hover:bg-slate-50 p-5 transition-colors"
              onClick={() => {
                setCoworkChoiceOpen(false);
                setCoworkEditId(null);
                setCoworkForm(initialCoworkForm);
                loadCoworkDisponibles();
                setCoworkDialogOpen(true);
              }}
            >
              <Sparkles className="w-7 h-7 text-[#1B4D3E]" />
              <span className="text-sm font-medium">Cowork</span>
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Alta simplificada de suscriptor Cowork */}
      <Dialog open={coworkDialogOpen} onOpenChange={(o) => { setCoworkDialogOpen(o); if (!o) { setCoworkEditId(null); setCoworkForm(initialCoworkForm); } }}>
        <DialogContent className="max-w-lg" data-testid="cowork-suscriptor-dialog">
          <DialogHeader>
            <DialogTitle>{coworkEditId ? 'Editar suscriptor Cowork' : 'Nuevo suscriptor Cowork'}</DialogTitle>
            <DialogDescription>{edificio?.nombre}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Unidad de Cowork</Label>
                <Select value={coworkForm.unidad_id} onValueChange={(v) => setCoworkForm({ ...coworkForm, unidad_id: v })}>
                  <SelectTrigger data-testid="cowork-unidad-select">
                    <SelectValue placeholder={coworkUnidadOptions.length ? 'Elegí una disponible' : 'Sin unidades disponibles'} />
                  </SelectTrigger>
                  <SelectContent>
                    {coworkUnidadOptions.map((u) => (
                      <SelectItem key={u.unidad_id} value={u.unidad_id} data-testid={`cowork-unidad-${u.unidad_id}`}>
                        {u.nombre_unidad}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Tipo</Label>
                <Select value={coworkForm.tipo} onValueChange={(v) => setCoworkForm({ ...coworkForm, tipo: v })}>
                  <SelectTrigger data-testid="cowork-tipo-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="virtual" data-testid="cowork-tipo-virtual">Virtual</SelectItem>
                    <SelectItem value="full" data-testid="cowork-tipo-full">Full</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Nombre del suscriptor</Label>
              <Input
                data-testid="cowork-nombre-input"
                value={coworkForm.nombre}
                onChange={(e) => setCoworkForm({ ...coworkForm, nombre: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>CUIT</Label>
                <Input
                  data-testid="cowork-cuit-input"
                  placeholder="20-12345678-9"
                  value={coworkForm.cuit}
                  onChange={(e) => setCoworkForm({ ...coworkForm, cuit: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Empresa (opcional)</Label>
                <Input
                  data-testid="cowork-razon-social-input"
                  value={coworkForm.razon_social}
                  onChange={(e) => setCoworkForm({ ...coworkForm, razon_social: e.target.value })}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 items-start">
              <div className="space-y-1.5 col-span-2">
                <EmailsFacturacionList
                  emails={coworkForm.emails && coworkForm.emails.length ? coworkForm.emails : ['']}
                  onChange={(next) => setCoworkForm(f => ({ ...f, emails: next }))}
                  testidPrefix="cowork-email"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Teléfono</Label>
                <Input
                  data-testid="cowork-telefono-input"
                  type="tel"
                  placeholder="+54 9 261 123-4567"
                  value={coworkForm.telefono}
                  onChange={(e) => setCoworkForm({ ...coworkForm, telefono: e.target.value })}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Condición IVA</Label>
                <Select value={coworkForm.condicion_iva} onValueChange={(v) => setCoworkForm({ ...coworkForm, condicion_iva: v })}>
                  <SelectTrigger data-testid="cowork-condicion-iva-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="responsable_inscripto">Responsable Inscripto</SelectItem>
                    <SelectItem value="monotributo">Monotributo</SelectItem>
                    <SelectItem value="exento">Exento</SelectItem>
                    <SelectItem value="consumidor_final">Consumidor Final</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Fecha de inicio</Label>
                <Input
                  type="date"
                  data-testid="cowork-fecha-inicio-input"
                  value={coworkForm.fecha_inicio}
                  onChange={(e) => setCoworkForm({ ...coworkForm, fecha_inicio: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Descuento / bonificación (%) — opcional</Label>
              <Input
                type="number" min="0" max="100" step="0.01"
                data-testid="cowork-descuento-input"
                placeholder="0"
                value={coworkForm.descuento_pct}
                onChange={(e) => setCoworkForm({ ...coworkForm, descuento_pct: e.target.value })}
              />
            </div>
            <div className="rounded-md bg-slate-50 border border-slate-200 p-3 text-sm space-y-1">
              <div className="flex justify-between"><span className="text-slate-500">Monto base ({coworkForm.tipo})</span><span data-testid="cowork-monto-base">{formatCurrency(coworkMontoBase)}</span></div>
              {coworkDescuento > 0 && (
                <div className="flex justify-between text-slate-500"><span>Descuento {coworkDescuento}%</span><span>-{formatCurrency(coworkMontoBase - coworkMontoFinal)}</span></div>
              )}
              <div className="flex justify-between font-semibold text-[#1B4D3E]"><span>Monto final mensual</span><span data-testid="cowork-monto-final">{formatCurrency(coworkMontoFinal)}</span></div>
              {coworkMontoBase === 0 && (
                <p className="text-xs text-amber-600 pt-1">No hay monto configurado para este tipo. Cargalo en la configuración del edificio.</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCoworkDialogOpen(false)} disabled={savingCowork}>Cancelar</Button>
            <Button onClick={handleGuardarCowork} disabled={savingCowork} data-testid="cowork-guardar-btn">
              {savingCowork ? 'Guardando...' : (coworkEditId ? 'Guardar cambios' : 'Dar de alta')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Suscriptores Cowork */}
      {mode === 'contratos' && contratoFilter === 'cowork' && (
        <div className="mt-2 shrink-0" data-testid="cowork-suscriptores-section">
          {coworkSuscriptores.length === 0 ? (
            <div className="bg-slate-50 rounded-lg p-8 text-center">
              <Sparkles className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 font-medium">No hay suscriptores Cowork</p>
              <p className="text-sm text-slate-400 mt-1">Usá "+ Nuevo contrato" y elegí Cowork para dar de alta el primero.</p>
            </div>
          ) : (
          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs">
                <tr>
                  <th className="py-2 px-3 text-left">N° Cowork</th>
                  <th className="py-2 px-3 text-left">Suscriptor</th>
                  <th className="py-2 px-3 text-left">Empresa</th>
                  <th className="py-2 px-3 text-left">Tipo</th>
                  <th className="py-2 px-3 text-left whitespace-nowrap">Inicio</th>
                  <th className="py-2 px-3 text-right">Monto mensual</th>
                  <th className="py-2 px-3 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {[...coworkSuscriptores].sort((a, b) => {
                  const na = parseInt(String(a.numero_cowork || '').match(/\d+/)?.[0] || '999999', 10);
                  const nb = parseInt(String(b.numero_cowork || '').match(/\d+/)?.[0] || '999999', 10);
                  return na - nb;
                }).map((s) => (
                  <tr key={s.suscriptor_id} className="border-t border-slate-100" data-testid={`cowork-suscriptor-row-${s.suscriptor_id}`}>
                    <td className="py-2 px-3">{s.numero_cowork}</td>
                    <td className="py-2 px-3">{s.nombre}</td>
                    <td className="py-2 px-3">{s.razon_social || <span className="text-slate-300">—</span>}</td>
                    <td className="py-2 px-3 capitalize">{s.tipo}</td>
                    <td className="py-2 px-3 whitespace-nowrap">{formatDate(s.fecha_inicio)}</td>
                    <td className="py-2 px-3 text-right font-medium">{formatCurrency(s.monto_final)}{s.descuento_pct ? <span className="text-xs text-slate-400"> ({s.descuento_pct}% desc.)</span> : null}</td>
                    <td className="py-2 px-3 text-right whitespace-nowrap">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEditarCowork(s)}
                        data-testid={`cowork-editar-btn-${s.suscriptor_id}`}
                      >
                        Editar
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                        onClick={() => handleFinalizarCowork(s)}
                        data-testid={`cowork-finalizar-btn-${s.suscriptor_id}`}
                      >
                        Finalizar
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => handleEliminarCowork(s)}
                        data-testid={`cowork-eliminar-btn-${s.suscriptor_id}`}
                      >
                        Eliminar
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}
        </div>
      )}


    </div>
  );
};

export default ContratosDepositos;
